import {
  AcademicCapIcon,
  AnnotationIcon,
  BookOpenIcon,
  BriefcaseIcon,
  CakeIcon,
  CalculatorIcon,
  CalendarIcon,
  CameraIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ClipboardCheckIcon,
  ClipboardListIcon,
  ClockIcon,
  CodeIcon,
  DatabaseIcon,
  DocumentIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
  FlagIcon,
  InformationCircleIcon,
  LightBulbIcon,
  MicrophoneIcon,
  NewspaperIcon,
  PaperAirplaneIcon,
  PhotographIcon,
  PlusIcon,
  PresentationChartBarIcon,
  PuzzleIcon,
  QuestionMarkCircleIcon,
  RefreshIcon,
  // RssIcon,
  SearchIcon,
  ShieldCheckIcon,
  SparklesIcon,
  SpeakerphoneIcon,
  StarIcon,
  ThumbUpIcon,
  UserGroupIcon,
  UserIcon,
  VideoCameraIcon,
  ViewListIcon,
  VolumeUpIcon,
} from '@heroicons/react/outline';
import {
  BeakerIcon,
  ChatAlt2Icon,
  CurrencyDollarIcon,
  FastForwardIcon,
  FilmIcon,
  GlobeAltIcon,
  HeartIcon,
  HomeIcon,
  LightningBoltIcon,
  MailIcon,
  MusicNoteIcon,
  PencilAltIcon,
  PencilIcon,
  PlayIcon,
  ReplyIcon,
  ShoppingCartIcon,
  TranslateIcon,
  UserCircleIcon,
  UsersIcon,
} from '@heroicons/react/solid';
import { IconType } from 'react-icons';
import { FaTools } from 'react-icons/fa';
import {
  FaAmazon,
  FaBullhorn,
  FaBusinessTime,
  FaCompress,
  FaDice,
  FaEnvelope,
  FaEtsy,
  FaExpand,
  FaFacebook,
  FaFeather,
  FaFileExcel,
  FaFish,
  FaGoogle,
  FaHeading,
  FaImages,
  FaJira,
  FaListOl,
  FaMagnet,
  FaPaperclip,
  FaParagraph,
  FaPersonBooth,
  FaPinterest,
  FaPodcast,
  FaQuora,
  FaRobot,
  FaSuitcase,
  FaTextWidth,
  FaTiktok,
  FaToolbox,
  FaTwitter,
  FaYoutube,
} from 'react-icons/fa6';
import {
  GiArcheryTarget,
  GiSofa,
  GiSoundWaves,
  GiTowerBridge,
} from 'react-icons/gi';
import {
  ImBlog,
  ImInfo,
  ImLinkedin,
  ImRocket,
  ImSmile,
  ImTrophy,
  ImWoman,
} from 'react-icons/im';
import {
  RiAdvertisementFill,
  RiArticleFill,
  RiArticleLine,
  RiEnglishInput,
  RiFeedbackFill,
  RiInsertRowBottom,
  RiInstagramLine,
  RiMessage3Line,
  RiMessageFill,
  RiPriceTagLine,
} from 'react-icons/ri';

import ThreadsSvg from '~/svg/Threads.svg';

export const presetCategories = [
  'Writing',
  'Content',
  'Social Media',
  'Marketing',
  'Email & Messages',
  'Business',
  'HR',
  'Career',
  'Education',
  'Images & Video',
  'Audio & Music',
  'Creative Writing',
  'Health & Wellness',
  'Legal',
  'Code & Data',
  'Personal & Fun',
  // Fallback bucket; also where user-created templates land by default
  'Other',
] as const;

export type PresetCategory = (typeof presetCategories)[number];

export interface InputField {
  label: string;
  name: string;
  type:
    | 'text'
    | 'checkbox'
    | 'number'
    | 'textarea'
    | 'select'
    | 'radio'
    | 'hidden'
    | 'file'
    | 'files'
    | 'image'
    | 'brand_voice';
  minRows?: number;
  accept?: string;
  acceptDescription?: string;
  description?: string;
  placeholder: string;
  options?: readonly string[];
  radioOptions?: readonly { name: string; checked: boolean }[];
  limit?: number;
  initialValue?: string | number | boolean;
  enableMicrophone?: boolean;
  maxFiles?: number;
  maxFileSize?: number; // in MB
  // Files uploaded here only go into the generated image (e.g. a logo or a
  // product photo); the text model never sees them.
  imageReferenceOnly?: boolean;
}

// Optional images for the post generators that also make an image
// (generateImage): a logo, product photos, a person. /api/generate puts them
// into the image; they never reach the text model. Hidden on the logged-out
// public form, which makes no post image.
export const POST_IMAGES_FIELD: InputField = {
  label: 'Images for the post (optional)',
  name: 'postImages',
  type: 'files',
  description:
    'Add your logo, product photos or other images to use in the generated image.',
  placeholder: 'example: logo.png',
  accept: 'image/*',
  acceptDescription: 'png, jpg, webp',
  maxFiles: 3,
  maxFileSize: 5,
  imageReferenceOnly: true,
};

// Custom Generator (Dynamic Template) output handlers. Determines which
// generation backend a user-created template runs against:
// - text: LLM completion (default, original behavior)
// - image: text-to-image (logo, poster, tattoo, ...)
// - image-edit: image-to-image transform of a user-uploaded photo
//   (caricature, cartoonify, restyle, ...) — requires an `image` field
// - speech: LLM writes the script, then it is voiced via TTS
export const CUSTOM_GENERATOR_OUTPUT_TYPES = [
  'text',
  'image',
  'image-edit',
  'speech',
] as const;
export type CustomGeneratorOutputType =
  (typeof CUSTOM_GENERATOR_OUTPUT_TYPES)[number];

// Allowed field names/types for user-created dynamic templates. `image1`/
// `image2` hold uploaded image URLs and feed the image-edit handler; the
// remaining names are the classic prompt slots.
export const DYNAMIC_TEMPLATE_FIELD_NAMES = [
  'keywords',
  'extra1',
  'extra2',
  'extra3',
  'extra4',
  'extra5',
  'tone',
  'image1',
  'image2',
] as const;
export const DYNAMIC_TEMPLATE_FIELD_TYPES = [
  'text',
  'textarea',
  'checkbox',
  'select',
  'radio',
  'image',
] as const;
export interface Preset {
  title: string;
  searchKeywords?: string[];
  slug: string;
  landingPageSlug?: string;
  // Path of the tool page when it differs from the slug. The slug doubles as
  // the favorites identifier (shared with toolCategories pins), so it must
  // stay stable — set toolHref instead of encoding the path in the slug.
  toolHref?: string;
  description: string;
  icon: IconType | string;
  iconForeground: string;
  iconBackground: string;
  // component: () => JSX.Element;
  category: PresetCategory | readonly PresetCategory[];
  badge?: string;
  // User-created dynamic template (Magic AI tool) — powers the "My Tools" filter
  isCustom?: boolean;
  fields?: readonly InputField[];
  outputs?: number;
  defaultOutputs?: number;
  hideLandingPage?: boolean;
  hideDashoard?: boolean;
  onlyForPaidPlans?: boolean;
  shouldUseChatGPTAPI?: boolean;
  shouldUseGPT4?: boolean;
  shouldUseGPT4o?: boolean;
  shouldUseAnthropic?: boolean;
  shouldUseO3Mini?: boolean;
  useGemini?: boolean;
  hideLanguageSelector?: boolean;
  saved_at?: string;
  useOpenAI?: boolean;
  isFree?: boolean;
  isAbsoluteURL?: boolean;
  dateAdded?: string;
  showExamples?: boolean;
  maxGenerations?: number;
  useTool?: boolean;
  // Suppress the "Use web search" toggle for presets that purely transform
  // user-supplied input (clinical notes, summarizers, rewriters, …) — web
  // grounding adds nothing there and, for clinical documentation, invites
  // fabricated external facts into the record.
  hideWebSearch?: boolean;
  generateImage?: boolean;
  generateImageDefaultOn?: boolean;
  generateImageAspectRatio?: string;
  // When true, the worksheet generator replaces text-based generation with a
  // full-page image generated by GPT Image 2 Medium. The completion is saved
  // as markdown that embeds the image. Worksheet-specific.
  isWorksheetVisual?: boolean;
  imageModel?:
    | 'Nano Banana 2'
    | 'Nano Banana 2 Lite'
    | 'Nano Banana Pro'
    | 'OpenAI GPT Image 2 Medium'
    | 'OpenAI GPT Image 2.5 Flare Medium'
    | 'OpenAI GPT Image 2.5 Sunburst Medium'
    | 'Seedream 4.5'
    | 'Seedream 5.0 Lite'
    | 'Seedream 5.0 Pro'
    | 'Z-Image Turbo';
  image?: string;
  supportsVideoAnalysis?: boolean;
  // Overrides the signup hand-off destination for this template. When adding
  // one, also add it to TEMPLATE_LOGIN_PRESETS in src/lib/presetFromPath.ts.
  loginPreset?: string;
  // Custom Generator handler: which generation backend this (user-created)
  // template runs against. Undefined means classic text generation.
  outputType?: CustomGeneratorOutputType;
}

// Href for opening a preset's tool page. Absolute-URL presets live at their
// toolHref when set, otherwise at their slug — which may or may not carry a
// leading slash (e.g. '/ai-images/models' vs 'ai-video-editor'); everything
// else goes through /presets/[slug].
export function getPresetToolHref(preset: Preset): string {
  if (!preset.isAbsoluteURL) return `/presets/${preset.slug}`;
  if (preset.toolHref) return preset.toolHref;
  return preset.slug.startsWith('/') ? preset.slug : `/${preset.slug}`;
}

// A saved per-tool "default instructions" value (see PresetSavedInstructions) is
// merged into extra1 at generation time. That is only correct for presets whose
// extra1 field is the free-text "Additional Instructions". For presets where
// extra1 is an image/video upload or another semantic field (age, "Presenting
// Concerns", a product description, etc.) merging would corrupt the prompt — so
// both the UI and the server-side merge are gated on this check.
export function presetUsesAdditionalInstructions(preset?: Preset): boolean {
  return !!preset?.fields?.some(
    (field) =>
      field.name === 'extra1' &&
      /additional instructions/i.test(field.label || ''),
  );
}

// useGemini presets where gpt-4o-mini is NOT a viable fallback (e.g. they rely
// on Gemini web grounding), so the "Standard (Legacy)" model option is hidden
// in the UI and never honored on the server for them.
export const LEGACY_MODEL_EXCLUDED_SLUGS = ['news-article-generator'];

// Multi-slide carousel presets: their output embeds each slide as
// "![Slide N](url)" markdown. Caption.tsx renders these with the swipeable
// CarouselPreview viewer (+ download all), and generate.ts's carousel branch
// generates one designed image per slide.
export const CAROUSEL_PRESET_SLUGS = [
  'linkedin-carousel-generator',
  'instagram-carousel-generator',
];

// Clinical documentation tools — surface HIPAA / BAA compliance for these
// (landing pages + dashboard) since clinicians enter PHI.
export const CLINICAL_PRESET_SLUGS = [
  'progress-note-generator',
  'soap-note-generator',
  'dap-note-generator',
  'birp-note-generator',
  'aba-notes-generator',
  'therapy-intake',
  'treatment-plan-generator',
  'clinical-summary-update-generator',
];

export const presets: Readonly<Preset[]> = [
  {
    title: 'Marky Agent',
    slug: 'chat',
    icon: ChatAlt2Icon,
    iconForeground: 'text-primary-700',
    iconBackground: 'bg-primary-50',
    description:
      'Your AI assistant that helps you create content, answer questions, and complete tasks through natural conversation.',
    landingPageSlug: '/marky',
    fields: [],
    category: ['Writing', 'Business'],
    isAbsoluteURL: true,
  },
  {
    title: 'AI Text-to-Speech',
    slug: 'tts',
    icon: MicrophoneIcon,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    description: 'Generate audio from text using AI. Supports 30+ languages.',
    landingPageSlug: '/text-to-speech',
    fields: [],
    category: 'Audio & Music',
    isAbsoluteURL: true,
  },
  {
    title: 'AI Art & Images',
    slug: 'ai-images',
    icon: FaImages,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    description:
      'Generate stunning images and art with AI. Express ideas. Stand out.',
    landingPageSlug: '/ai-image-generator',
    image:
      'https://media.easy-peasy.ai/56558fe9-8815-45c8-9164-949e68079ec7/f380821f-b00b-40b9-98f8-30b27d0725d1.png',
    fields: [],
    category: ['Images & Video', 'Social Media'],
    isAbsoluteURL: true,
  },
  {
    title: 'AI Video Generator',
    slug: '/ai-videos',
    icon: VideoCameraIcon,
    iconForeground: 'text-red-700',
    iconBackground: 'bg-red-50',
    description:
      'Create videos with AI. Transform text or images into engaging video content.',
    landingPageSlug: '/ai-video-generator',
    fields: [],
    category: ['Images & Video', 'Social Media'],
    isAbsoluteURL: true,
  },
  {
    title: 'AI Talking Videos',
    slug: '/ai-talking-videos',
    icon: VideoCameraIcon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    description:
      'Upload an image, enter text, and create realistic talking videos with AI.',
    fields: [],
    image:
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/b831d235-1a40-419c-b202-e084be3985f7.png',
    category: ['Images & Video', 'Social Media'],
    isAbsoluteURL: true,
    hideLandingPage: true,
  },
  {
    title: 'AI Photo Generator',
    slug: '/ai-images/models',
    icon: CameraIcon,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    image:
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/b3c6b50a-65f3-4ec3-94a4-b9ec87b95895.png',
    description:
      'Generate realistic photos with AI. Create custom images for any purpose.',
    landingPageSlug: '/ai-photo-generator',
    fields: [],
    category: 'Images & Video',
    isAbsoluteURL: true,
    badge: 'New',
    dateAdded: '2024-09-04',
  },
  {
    title: 'AI Headshot Generator',
    slug: 'headshots',
    icon: CameraIcon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    description:
      'Generate professional headshots with AI. For LinkedIn, email signatures, and more.',
    image:
      'https://media.easy-peasy.ai/61706c02-3e34-45ac-a634-88f7ced38f9d/d68ea93c-0e75-4803-ac61-50e3455083fb.png',
    landingPageSlug: '/ai-headshot-generator',
    fields: [],
    category: ['Images & Video', 'Career', 'Business'],
    isAbsoluteURL: true,
    badge: 'New',
    dateAdded: '2024-08-23',
  },
  {
    title: 'AI Interior Design Generator',
    slug: 'ai-interior-designer',
    icon: GiSofa,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    image:
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/1eb1e709-4c53-4fed-97dd-7a2fae5176cc.png',
    description:
      'Brainstorm interior design ideas at a fraction of the cost of hiring an interior designer.',
    landingPageSlug: '/ai-interior-design-generator',
    fields: [
      {
        label: 'Upload a photo of your room',
        name: 'roomPhoto',
        type: 'file',
        description: 'Upload a clear photo of your room for best results.',
        placeholder: 'example: room.jpg',
      },
      {
        label: 'Room Type',
        name: 'roomType',
        type: 'select',
        options: ['Living Room', 'Bedroom', 'Kitchen', 'Bathroom', 'Office'],
        description: 'Select the type of room you want to design.',
        placeholder: 'example: Living Room, Bedroom, Kitchen',
      },
      {
        label: 'Theme',
        name: 'theme',
        type: 'select',
        options: ['Modern', 'Classic', 'Rustic', 'Industrial', 'Scandinavian'],
        description: 'Select the theme you want for your room.',
        placeholder: 'example: Minimalist, Vintage, Industrial',
      },
      {
        label: 'Additional Prompt (Optional)',
        name: 'additionalPrompt',
        type: 'textarea',
        placeholder:
          'example: I want a cozy living room with a fireplace and a large sofa.',
      },
    ],
    category: ['Images & Video', 'Personal & Fun'],
    isAbsoluteURL: true,
  },
  // music
  {
    title: 'AI Music Generator',
    slug: 'music-generator',
    icon: MusicNoteIcon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    description:
      'Create original music with AI, including full songs with lyrics or instrumental tracks.',
    landingPageSlug: '/ai-music-generator',
    fields: [],
    category: 'Audio & Music',
    isAbsoluteURL: true,
    badge: 'New',
  },
  {
    title: 'AI Audio Generator',
    slug: 'audio-generator',
    icon: VolumeUpIcon,
    iconForeground: 'text-primary-700',
    iconBackground: 'bg-primary-50',
    description:
      'Create voices, music, dialogue, and soundscapes with Seed Audio 1.0.',
    landingPageSlug: '/ai-audio-generator',
    loginPreset: 'audio-generator',
    fields: [],
    category: 'Audio & Music',
    isAbsoluteURL: true,
    badge: 'New',
  },
  {
    title: 'AI Sound Effects',
    slug: 'sound-effects',
    icon: VolumeUpIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    description:
      'Generate sound effects with AI. Create unique sounds for any purpose.',
    landingPageSlug: '/ai-sound-effects',
    fields: [],
    category: 'Audio & Music',
    isAbsoluteURL: true,
    badge: 'New',
  },
  {
    title: 'AI Note Taker',
    slug: 'note-taker',
    description:
      'Record any conversation and get transcription, summary, and AI insights',
    icon: GiSoundWaves,
    iconForeground: 'text-emerald-700',
    iconBackground: 'bg-emerald-50',
    landingPageSlug: '/ai-note-taker',
    category: ['Audio & Music', 'Business'],
    isFree: true,
    dateAdded: '2024-12-07',
    fields: [],
    isAbsoluteURL: true,
  },
  {
    title: 'Magic Upscaler',
    slug: 'magic-upscaler',
    image: 'https://easy-peasy.ai/img/tools/CreativeUpscaleGraphic.jpg',
    description: 'Enhance and upscale your images with AI magic.',
    icon: SparklesIcon,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    landingPageSlug: '/magic-upscaler',
    category: 'Images & Video',
    isFree: true,
    dateAdded: '2023-07-15',
    fields: [],
    isAbsoluteURL: true,
    hideLandingPage: true,
  },
  {
    title: 'AI Image Editor',
    slug: 'ai-image-editor',
    icon: PencilAltIcon,
    iconForeground: 'text-rose-700',
    iconBackground: 'bg-rose-50',
    description:
      'Edit and enhance your images with AI. Use prompt or visual annotations to modify any image.',
    landingPageSlug: '/ai-image-editor',
    category: 'Images & Video',
    fields: [],
    isAbsoluteURL: true,
    hideLandingPage: true,
  },
  {
    title: 'Video Agent',
    slug: 'video-agent',
    toolHref: '/create-video',
    icon: SparklesIcon,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    description:
      'Describe your idea and the agent writes the script, picks the voice and music, generates each scene, and hands off to the editor.',
    landingPageSlug: '/create-video',
    category: ['Images & Video', 'Social Media'],
    fields: [],
    isAbsoluteURL: true,
    hideLandingPage: true,
    badge: 'New',
  },
  {
    title: 'AI Video Editor',
    slug: 'ai-video-editor',
    icon: FilmIcon,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    description:
      'Create professional videos with AI-generated content, multi-track timeline, captions, and more.',
    landingPageSlug: '/ai-video-editor',
    category: 'Images & Video',
    fields: [],
    isAbsoluteURL: true,
    hideLandingPage: true,
  },
  {
    title: 'AI Sprite Generator',
    slug: 'sprite-generator',
    icon: PuzzleIcon,
    iconForeground: 'text-emerald-700',
    iconBackground: 'bg-emerald-50',
    image:
      'https://media.easy-peasy.ai/assets/showcase/006-bamboo-panda-warrior-studio-ghibli-inspi.gif',
    description:
      'Generate game sprite sheets with AI. Create character animations for 2D games.',
    landingPageSlug: '/sprite-generator',
    fields: [],
    category: 'Images & Video',
    isAbsoluteURL: true,
    badge: 'New',
  },
  {
    title: 'Custom Generator',
    slug: 'custom-generator',
    shouldUseO3Mini: false,
    onlyForPaidPlans: true,
    description: 'Generate custom text for any purpose.',
    icon: FaRobot,
    // shouldUseO3Mini: true,
    iconForeground: 'text-cyan-700',
    iconBackground: 'bg-cyan-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    category: ['Writing', 'Business'],
    fields: [
      {
        label: 'What do you want to generate?',
        name: 'keywords',
        type: 'textarea',
        minRows: 8,
        limit: 20000,
        enableMicrophone: true,
        description:
          'To get the best result, be as specific as possible with your instructions to the AI',
        placeholder:
          'examples:\n- Write an email to potential investors about my startup\n- Create a list of 8 questions for my interview with Bill Gates\n- Create an outline for an essay about Elon Musk and his contributions to technology\n- List 10 business books\n- Write a creative ad to run on Facebook (add details about the product to the Background Information)',
      },
      {
        label: 'Background Information (optional)',
        name: 'extra1',
        type: 'textarea',
        limit: 20000,
        placeholder:
          'example: Easy-Peasy.AI is an AI copywriting tool that can help you with a variety of writing tasks, from creating better resumes and job descriptions to composing emails and social media content.',
      },
    ],
  },
  {
    title: 'Free AI Image Upscaler',
    slug: 'image-upscaler',
    description:
      'Use our free tool to upscale your images and improve the quality of your photos.',
    icon: SparklesIcon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    shouldUseChatGPTAPI: true,
    category: 'Images & Video',
    isFree: true,
    landingPageSlug: '/free-tools/upscale-image',
    hideDashoard: true,
    fields: [],
    isAbsoluteURL: true,
  },
  {
    title: 'AI Transcription',
    slug: 'audios',
    icon: GiSoundWaves,
    iconForeground: 'text-orange-700',
    iconBackground: 'bg-orange-50',
    description:
      'Transcribe and summarize audios. Generate titles, descriptions and show notes.',
    landingPageSlug: '/ai-transcription',
    fields: [],
    category: ['Audio & Music', 'Business'],
    isAbsoluteURL: true,
  },
  {
    title: 'Bots',
    slug: 'bots',
    icon: FaRobot,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    description: 'Create custom bots for specific purposes.',
    hideLandingPage: true,
    fields: [],
    category: 'Business',
    isAbsoluteURL: true,
  },
  {
    title: 'One Click Article Generation',
    slug: '/presets/blog-post-v3',
    icon: ImBlog,
    iconForeground: 'text-red-700',
    iconBackground: 'bg-red-50',
    description: 'Generate SEO-friendly articles with one click.',
    landingPageSlug: '/templates/blog-post',
    outputs: 1,
    fields: [],
    category: 'Content',
    isAbsoluteURL: true,
    onlyForPaidPlans: true,
  },
  {
    title: 'Brand Voice',
    slug: 'voices',
    icon: SpeakerphoneIcon,
    iconForeground: 'text-teal-700',
    iconBackground: 'bg-teal-50',
    description: 'Train AI to speak like you. Personalize your content.',
    hideLandingPage: true,
    fields: [],
    category: 'Marketing',
    isAbsoluteURL: true,
  },
  {
    title: 'Dynamic Template',
    slug: 'dynamic',
    description: 'Generate any template from a single prompt.',
    icon: SparklesIcon,
    iconForeground: 'text-primary-700',
    iconBackground: 'bg-primary-50',
    // component: GenericComponent,
    // onlyForPaidPlans: true,
    category: 'Writing',
    badge: 'New',
    fields: [],
    // hideLandingPage: true,
    searchKeywords: ['magic ai tool', 'magic ai template'],
  },
  {
    title: 'Blog Post Workflow',
    slug: '/workflows/blog-post',
    description: 'Step-by-step workflow to generate an entire blog post.',
    icon: FaListOl,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    // component: GenericComponent,
    // onlyForPaidPlans: true,
    category: 'Content',
    badge: 'Updated',
    fields: [],
    hideLandingPage: true,
    isAbsoluteURL: true,
  },

  {
    title: 'Long-form editor',
    slug: 'documents',
    onlyForPaidPlans: true,
    description: 'Write long-form content with AI',
    icon: DocumentTextIcon,
    iconForeground: 'text-cyan-700',
    iconBackground: 'bg-cyan-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    hideDashoard: false,
    hideLandingPage: true,
    category: 'Content',
    isAbsoluteURL: true,
  },
  {
    title: 'YouTube to Article',
    slug: 'youtube-to-article',
    description: 'Turn a YouTube video into an article',
    icon: FaRobot,
    iconForeground: 'text-cyan-700',
    iconBackground: 'bg-cyan-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    hideDashoard: true,
    hideLandingPage: true,
    category: 'Content',
    fields: [
      {
        label: 'YouTube Transcript',
        name: 'keywords',
        type: 'textarea',
        minRows: 8,
        limit: 10000,
        description:
          'To get the best result, be as specific as possible with your instructions to the AI',
        placeholder: '',
      },
    ],
  },

  {
    title: 'Language Detector',
    slug: 'language-detector',
    hideWebSearch: true,
    description: 'Detect the language of a text',
    icon: FaRobot,
    iconForeground: 'text-cyan-700',
    iconBackground: 'bg-cyan-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    hideDashoard: true,
    hideLandingPage: true,
    category: 'Writing',
    fields: [
      {
        label: 'YouTube Transcript',
        name: 'keywords',
        type: 'textarea',
        minRows: 8,
        limit: 1000,
        placeholder: '',
      },
    ],
  },
  {
    title: 'Instagram Post Caption',
    slug: 'instagram-post-caption',
    description: 'Generate a caption for an Instagram post',
    icon: RiInstagramLine,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    // component: GenericComponent,
    category: 'Social Media',
    shouldUseChatGPTAPI: true,
    onlyForPaidPlans: true,
    isFree: true,
    generateImage: true,
    generateImageAspectRatio: '9:16',
    supportsVideoAnalysis: true,
    fields: [
      // image file upload
      {
        label: 'Upload an image (optional)',
        name: 'extra1',
        type: 'file',
        placeholder: 'example: image.png',
        accept: 'image/*',
        acceptDescription: 'jpg, png, webp',
      },
      {
        label: 'Upload a video (optional)',
        name: 'extra2',
        type: 'file',
        placeholder: 'Upload a video to generate caption from',
        accept: 'video/*',
        acceptDescription: 'mp4, mov, webm',
        description:
          'Upload a video and AI will analyze it to create a relevant caption',
      },
      {
        label: 'Post Topic',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: weekend with family in the park',
        limit: 1000,
      },

      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: friendly, funny, cheerful',
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'example: friendly, funny, cheerful',
        initialValue: 'Short',
        radioOptions: [
          { name: 'Short', checked: true },
          { name: 'Medium', checked: false },
          { name: 'Long', checked: false },
        ],
      },
      POST_IMAGES_FIELD,
    ],
  },
  {
    title: 'Paragraph Writer',
    slug: 'paragraph-writer',
    onlyForPaidPlans: true,
    description: 'Generate paragraphs with the click of a button!',
    icon: FaParagraph,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    isFree: true,
    category: ['Writing', 'Content'],
    showExamples: true,
    generateImage: false,
    fields: [
      {
        label: 'What the paragraph is about',
        name: 'keywords',
        enableMicrophone: true,
        type: 'textarea',
        placeholder: 'example: how to optimize your website for search engines',
        limit: 2500,
      },
      {
        label: 'Keywords to include (optional)',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: meta tags, link building, sitemap.xml',
        limit: 1000,
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: friendly, funny, cheerful',
        limit: 250,
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'example: friendly, funny, cheerful',
        radioOptions: [
          { name: 'Short', checked: false },
          { name: 'Medium', checked: true },
          { name: 'Long', checked: false },
        ],
      },
    ],
  },
  {
    title: 'Headline Generator',
    slug: 'headline-generator',
    description: 'Make your headlines pop with our headline generator!',
    icon: FaHeading,
    iconForeground: 'text-brown-700',
    iconBackground: 'bg-brown-50',
    isFree: true,
    outputs: 1,
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    category: ['Writing', 'Content', 'Marketing'],
    maxGenerations: 20,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'textarea',
        limit: 1000,
        placeholder: 'example: Website Design',
      },
      {
        label: 'Tone (optional)',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: catchy, energetic, clever',
      },
    ],
  },
  {
    title: 'AI Video & Audio Dubbing',
    slug: 'dubbing',
    icon: TranslateIcon,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    description:
      'Translate videos and audio to 32 languages while preserving emotion, timing, and tone.',
    landingPageSlug: '/dubbing',
    fields: [],
    category: ['Audio & Music', 'Images & Video'],
    isAbsoluteURL: true,
    badge: 'New',
    hideLandingPage: true,
  },
  {
    title: 'AI Voice Changer',
    slug: 'voice-changer',
    icon: VolumeUpIcon,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    description:
      'Transform any recording into a different voice while keeping the original delivery and timing.',
    landingPageSlug: '/voice-changer',
    fields: [],
    category: ['Content', 'Social Media'],
    isAbsoluteURL: true,
    badge: 'New',
    hideLandingPage: true,
  },
  {
    title: 'Write like a native speaker',
    slug: 'native-speaker',
    hideWebSearch: true,
    description:
      'Clear, concise and authentic writing that gets your message across effectively.',
    icon: RiEnglishInput,
    iconForeground: 'text-orange-700',
    iconBackground: 'bg-orange-50',
    // component: GenericComponent,
    category: 'Writing',
    outputs: 1,
    onlyForPaidPlans: true,
    isFree: true,
    shouldUseChatGPTAPI: true,
    fields: [
      {
        label: 'Text',
        name: 'keywords',
        limit: 5000,
        enableMicrophone: true,
        type: 'textarea',
        placeholder:
          'example: I has breakfast with the my friends these morning.',
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: friendly, funny, cheerful',
      },
    ],
  },
  {
    title: 'Content Rewriter',
    slug: 'content-rewriter',
    description:
      'Make your content shine with our easy-to-use content rewriter',
    icon: FaTextWidth,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    // component: GenericComponent,
    category: ['Writing', 'Content'],
    outputs: 1,
    onlyForPaidPlans: true,
    isFree: true,
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    fields: [
      {
        label: 'Content',
        name: 'keywords',
        type: 'textarea',
        limit: 50000,
        placeholder:
          'example: Most companies try to stay ahead of the curve when it comes to visual design, but for Planetaria we needed to create a brand that would still inspire us 100 years from now when humanity has spread across our entire solar system.',
      },
      {
        label: 'Expand the content?',
        name: 'extra1',
        type: 'checkbox',
        placeholder: 'example: true',
        initialValue: true,
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: friendly, funny, cheerful',
      },
      // {
      //   label: 'Use GPT-4?',
      //   name: 'useGPT4',
      //   type: 'checkbox',
      //   placeholder: 'example: true',
      //   initialValue: false,
      // },
    ],
  },
  {
    title: 'AI Text Generator',
    slug: 'ai-text-generator',
    description:
      'Generate creative and engaging text for any purpose with our AI-powered text generator',
    icon: DocumentTextIcon,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    category: ['Writing', 'Content'],
    isFree: true,
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    onlyForPaidPlans: true,
    maxGenerations: 2,
    fields: [
      {
        label: 'What type of text do you need?',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: blog post, social media update, marketing copy',
        limit: 200,
      },
      {
        label: 'Main topic or subject',
        name: 'extra1',
        type: 'textarea',
        placeholder:
          'example: Describe the main topic or subject matter you want to write about',
        limit: 1000,
      },
      {
        label: 'Key points to include',
        name: 'extra2',
        type: 'textarea',
        placeholder:
          'example: List any specific points, facts, or ideas you want to include',
        limit: 1000,
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, casual, informative',
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'example: short, medium, long',
        radioOptions: [
          { name: 'Short', checked: true },
          { name: 'Medium', checked: false },
          { name: 'Long', checked: false },
        ],
      },
    ],
  },
  {
    title: 'Writing Enhancer',
    slug: 'writing-enhancer',
    hideWebSearch: true,
    description: 'Improve your writing with our AI-powered tool',
    icon: PencilAltIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    category: 'Writing',
    isFree: true,
    shouldUseChatGPTAPI: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Content',
        name: 'keywords',
        type: 'textarea',
        limit: 50000,
        placeholder: 'example: Enter the content you want to enhance',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: friendly, funny, cheerful',
      },
    ],
  },
  {
    title: 'Humanize AI Text To Bypass AI Detection',
    slug: 'humanize-ai-text',
    description:
      'Transform AI-generated text to make it more human-like and bypass AI detection.',
    icon: ShieldCheckIcon,
    iconForeground: 'text-red-700',
    iconBackground: 'bg-red-50',
    category: ['Writing', 'Content'],
    isFree: true,
    shouldUseGPT4o: true,
    shouldUseChatGPTAPI: true,
    onlyForPaidPlans: true,
    dateAdded: '2024-07-31',
    fields: [
      {
        label: 'AI-generated text',
        name: 'keywords',
        type: 'textarea',
        limit: 10000,
        placeholder:
          'example: Enter the AI-generated text you want to humanize',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: friendly, professional, casual',
      },
    ],
  },
  {
    title: 'SEO Title and Meta Descriptions',
    slug: 'seo-title-meta-descriptions',
    description: 'Get your website to the top of the search engines!',
    icon: FaGoogle,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    // component: GenericComponent,
    category: ['Marketing', 'Content'],
    onlyForPaidPlans: true,
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    isFree: true,
    showExamples: true,
    fields: [
      {
        label: 'Product description',
        name: 'keywords',
        limit: 1000,
        type: 'textarea',
        placeholder:
          'example: Meta Tags is a tool to debug and generate meta tag code for any website. With Meta Tags you can edit and experiment with your content then preview how your webpage will look on Google, Facebook, Twitter and more!',
      },
      {
        label: 'Target Keywords',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: meta tags',
      },
    ],
  },
  {
    title: 'Meta Description Generator',
    slug: 'meta-description-generator',
    description:
      'Generate compelling meta descriptions to improve click-through rates from search results.',
    icon: SearchIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    category: ['Marketing', 'Content'],
    isFree: true,
    fields: [
      {
        label: 'Page Content or Purpose',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'example: Our product helps businesses automate customer support with AI chatbots that integrate with existing systems.',
        limit: 5000,
      },
      {
        label: 'Target Keywords (optional)',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: AI chatbot, customer support automation',
        limit: 500,
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'text',
        placeholder: 'example: professional, friendly, authoritative',
        limit: 500,
      },
    ],
  },
  {
    title: 'Keywords Generator',
    slug: 'keywords-generator',
    description:
      'Generate relevant keywords from your input text to boost SEO and content relevance.',
    icon: AnnotationIcon,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    category: ['Marketing', 'Content'],
    shouldUseChatGPTAPI: true,
    isFree: true,
    fields: [
      {
        label: 'Content',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: Enter the text to analyze for keywords',
        limit: 20000,
      },
    ],
  },
  {
    title: 'Facebook Post Generator',
    slug: 'facebook-post-generator',
    description:
      'Easily create unique, high-quality posts for your Facebook page - with just one click!',
    icon: FaFacebook,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    shouldUseO3Mini: true,
    // component: GenericComponent,
    onlyForPaidPlans: true,
    isFree: true,
    showExamples: true,
    category: ['Social Media', 'Business'],
    maxGenerations: 2,
    generateImage: true,
    fields: [
      {
        label: 'What is you post about?',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'example: Easy-Peasy.AI is a powerful AI copywriting tool that can help you write better resumes, job descriptions, user stories, acceptance criteria, emails, and social media content.',
        limit: 1000,
        enableMicrophone: true,
      },
      {
        label: 'Target Audience (optional)',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: sales managers',
        limit: 1000,
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, funny',
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'example: friendly, funny, cheerful',
        radioOptions: [
          { name: 'Short', checked: false },
          { name: 'Long', checked: true },
        ],
      },
      POST_IMAGES_FIELD,
    ],
  },
  {
    title: 'Response Generator',
    slug: 'reply-to-messsage',
    description: 'Reply to WhatsApp, Slack or any other message',
    icon: RiMessageFill,
    iconForeground: 'text-lime-700',
    iconBackground: 'bg-lime-50',
    onlyForPaidPlans: true,
    // component: GenericComponent,
    category: ['Email & Messages', 'Personal & Fun'],
    isFree: true,
    shouldUseChatGPTAPI: true,
    // shouldUseO3Mini: true,
    maxGenerations: 2,
    fields: [
      {
        label: 'Message',
        name: 'keywords',
        type: 'textarea',
        limit: 10000,
        placeholder: 'example: Hi Janice! How did you spend your weekend?',
      },
      {
        // 'Flirty' serves the biggest search cluster feeding this page —
        // Spanish "ia para responder mensajes para ligar" (reply to flirt).
        label: 'Your Reply',
        name: 'extra2',
        type: 'select',
        placeholder: 'example: Positive',
        options: ['Positive', 'Flirty', 'Negative'],
        initialValue: 'Positive',
      },
      {
        label: 'What do you want to include in your reply? (optional)',
        name: 'extra1',
        type: 'textarea',
        limit: 5000,
        placeholder: 'example: went to East Coast park with family',
        enableMicrophone: true,
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, clever',
      },
    ],
  },
  {
    title: 'Reply To Email',
    description: 'Make your email replies more efficient',
    slug: 'reply-to-email',
    icon: RiMessage3Line,
    iconForeground: 'text-teal-700',
    iconBackground: 'bg-teal-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    shouldUseO3Mini: true,
    onlyForPaidPlans: true,
    shouldUseGPT4o: true,
    isFree: true,
    category: ['Email & Messages', 'Business'],
    maxGenerations: 2,
    fields: [
      {
        label: 'Received Email',
        name: 'keywords',
        type: 'textarea',
        limit: 10000,
        placeholder: 'example: Hi Janice! How did you spend your weekend?',
      },
      {
        label: 'Your Reply',
        name: 'extra2',
        type: 'select',
        placeholder: 'example: Positive',
        options: ['Positive', 'Negative'],
      },
      {
        label: 'What do you want to include in your reply? (optional)',
        name: 'extra1',
        type: 'textarea',
        limit: 10000,
        placeholder: 'example: went to East Coast park with family',
        enableMicrophone: true,
      },
      {
        label: 'Tone (optional)',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, clever',
      },
    ],
  },
  {
    title: 'Email Generation',
    slug: 'email-generation',
    description: 'Turn a sentence into an email',
    icon: MailIcon,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    // component: GenericComponent,
    category: ['Email & Messages', 'Business'],
    isFree: true,
    onlyForPaidPlans: true,
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    showExamples: true,
    maxGenerations: 5,
    fields: [
      {
        label: 'Email Topic',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: ask about pay raise',
        limit: 5000,
        enableMicrophone: true,
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, funny',
      },
    ],
  },
  {
    title: 'LinkedIn Post Generator',
    slug: 'linkedin-post-generator',
    description: 'Get more out of LinkedIn with the LinkedIn Post Generator!',
    icon: ImLinkedin,
    iconForeground: 'text-sky-700',
    iconBackground: 'bg-sky-50',
    isFree: true,
    // component: GenericComponent,
    category: ['Social Media', 'Business', 'HR'],
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    showExamples: true,
    onlyForPaidPlans: true,
    generateImage: true,
    fields: [
      {
        label: 'What is you post about?',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'example:\n- got promotion to regional sales manager\n- grateful to my colleagues\n- looking for the new challenges',
        limit: 1000,
        enableMicrophone: true,
      },
      // images optional using files
      {
        label: 'Upload an image (optional)',
        name: 'extra1',
        type: 'files',
        placeholder: 'example: image.png',
        accept: 'image/*',
        acceptDescription: 'jpg, png, webp',
        maxFiles: 5,
        maxFileSize: 5,
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, funny',
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'example: friendly, funny, cheerful',
        radioOptions: [
          { name: 'Short', checked: false },
          { name: 'Medium', checked: true },
          { name: 'Long', checked: false },
        ],
      },
      POST_IMAGES_FIELD,
    ],
  },
  {
    title: 'LinkedIn Post Generator V2',
    slug: 'linkedin-post-generator-v2',
    description: 'Create a narrative Linkedin post',
    icon: ImLinkedin,
    iconForeground: 'text-sky-700',
    iconBackground: 'bg-sky-50',
    // component: GenericComponent,
    category: ['Social Media', 'Business', 'HR'],
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    onlyForPaidPlans: true,
    hideLandingPage: true,
    showExamples: true,
    generateImage: true,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Promotion',
      },
      {
        label: 'Details as bullet points',
        name: 'extra1',
        type: 'textarea',
        placeholder:
          'example:\n- got promotion to regional sales manager\n- grateful to my colleagues\n- looking for the new challenges',
        limit: 10000,
        enableMicrophone: true,
      },
      // images optional using files
      {
        label: 'Upload an image (optional)',
        name: 'extra1',
        type: 'files',
        placeholder: 'example: image.png',
        accept: 'image/*',
        acceptDescription: 'jpg, png, webp',
        maxFiles: 5,
        maxFileSize: 5,
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, funny',
      },
    ],
  },
  {
    title: 'LinkedIn Carousel Generator',
    slug: 'linkedin-carousel-generator',
    description:
      'Turn any idea into a scroll-stopping LinkedIn carousel — designed slides plus a ready-to-post caption.',
    icon: ImLinkedin,
    iconForeground: 'text-sky-700',
    iconBackground: 'bg-sky-50',
    category: ['Social Media', 'Business', 'Marketing'],
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    onlyForPaidPlans: true,
    showExamples: true,
    dateAdded: '2026-07-02',
    // One carousel per run (each slide is its own generated image, so multiple
    // outputs would multiply image credits without adding value).
    outputs: 1,
    // Each slide is rendered as a designed image (text on-slide) via Nano Banana
    // 2 Lite. The multi-slide logic lives in generateImageMarkdown, gated on this
    // slug; images generate in the async background closure so N slides never hit
    // the edge timeout.
    generateImage: true,
    generateImageDefaultOn: true,
    // GPT Image 2 Medium won the 3-way slide bake-off (vs Nano Banana 2 /
    // 2 Lite): crispest typography, and with reference images it goes through
    // openai.images.edit (editImageWithGptImage2) which reproduced the brand
    // logo faithfully on every slide. 3 cr/slide; 'Nano Banana 2' (2 cr) and
    // 'Nano Banana 2 Lite' (1 cr) remain supported alternatives.
    imageModel: 'OpenAI GPT Image 2.5 Sunburst Medium',
    generateImageAspectRatio: '4:5',
    fields: [
      {
        label: 'What is your carousel about?',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'example: 5 mistakes founders make when hiring their first salesperson',
        limit: 1000,
        enableMicrophone: true,
      },
      {
        label: 'Key points to cover (optional)',
        name: 'extra1',
        type: 'textarea',
        placeholder:
          'example:\n- hiring too senior too early\n- no clear ideal customer profile\n- comp plan misaligned with the sales motion',
        limit: 2000,
        enableMicrophone: true,
      },
      {
        label: 'Number of slides',
        name: 'extra2',
        type: 'radio',
        placeholder: '',
        initialValue: '7 slides',
        radioOptions: [
          { name: '5 slides', checked: false },
          { name: '7 slides', checked: true },
          { name: '10 slides', checked: false },
        ],
      },
      {
        label: 'Brand color or visual vibe (optional)',
        name: 'extra3',
        type: 'text',
        placeholder: 'example: deep navy with an electric-blue accent, minimal',
        limit: 120,
      },
      {
        label: 'Reference images (optional)',
        name: 'extra4',
        type: 'files',
        description:
          'Upload brand slides, product shots, or screenshots — the slides will match their style and can feature them.',
        placeholder: 'example: brand-slide.png',
        accept: 'image/*',
        acceptDescription: 'jpg, png, webp',
        maxFiles: 3,
        maxFileSize: 5,
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, bold',
      },
    ],
  },
  {
    title: 'Instagram Carousel Generator',
    slug: 'instagram-carousel-generator',
    description:
      'Turn any idea into a save-worthy Instagram carousel — designed slides plus a ready-to-post caption.',
    icon: RiInstagramLine,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    category: ['Social Media', 'Marketing', 'Business'],
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    onlyForPaidPlans: true,
    showExamples: true,
    dateAdded: '2026-07-03',
    // One carousel per run (each slide is its own generated image, so multiple
    // outputs would multiply image credits without adding value).
    outputs: 1,
    // Shares the multi-slide carousel pipeline with the LinkedIn preset
    // (generateImageMarkdown branch gated on CAROUSEL_PRESET_SLUGS).
    generateImage: true,
    generateImageDefaultOn: true,
    imageModel: 'OpenAI GPT Image 2.5 Sunburst Medium',
    generateImageAspectRatio: '4:5',
    fields: [
      {
        label: 'What is your carousel about?',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'example: 5 morning habits that actually changed my productivity',
        limit: 1000,
        enableMicrophone: true,
      },
      {
        label: 'Key points to cover (optional)',
        name: 'extra1',
        type: 'textarea',
        placeholder:
          'example:\n- no phone for the first hour\n- 10-minute walk before coffee\n- planning the day the night before',
        limit: 2000,
        enableMicrophone: true,
      },
      {
        label: 'Number of slides',
        name: 'extra2',
        type: 'radio',
        placeholder: '',
        initialValue: '7 slides',
        radioOptions: [
          { name: '5 slides', checked: false },
          { name: '7 slides', checked: true },
          { name: '10 slides', checked: false },
        ],
      },
      {
        label: 'Brand color or visual vibe (optional)',
        name: 'extra3',
        type: 'text',
        placeholder: 'example: warm beige with terracotta accents, minimal',
        limit: 120,
      },
      {
        label: 'Reference images (optional)',
        name: 'extra4',
        type: 'files',
        description:
          'Upload brand slides, product shots, or screenshots — the slides will match their style and can feature them.',
        placeholder: 'example: brand-slide.png',
        accept: 'image/*',
        acceptDescription: 'jpg, png, webp',
        maxFiles: 3,
        maxFileSize: 5,
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: friendly, inspiring, bold',
      },
    ],
  },
  {
    title: 'Content Idea Generator',
    slug: 'content-idea-generator',
    description:
      'Get inspiration for your next piece of content by generating a huge variety of content ideas',
    icon: LightningBoltIcon,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    // component: GenericComponent,
    isFree: true,
    category: ['Content', 'Marketing', 'Business'],
    fields: [
      {
        label: 'What is your topic?',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: how to optimize your website for search engines',
        limit: 1000,
      },
      {
        label: 'What is your target audience?',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: small business owners',
        limit: 250,
      },
    ],
  },
  {
    title: 'Sentence Generator',
    slug: 'sentence-generator',
    hideWebSearch: true,
    description: 'Generate sentences with the click of a button!',
    icon: FaRobot,
    iconForeground: 'text-cyan-700',
    iconBackground: 'bg-cyan-50',
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    // isFree: true,
    category: 'Writing',
    fields: [
      {
        label: 'What is the sentence about?',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: the importance of AI in modern technology',
        limit: 500,
      },
      {
        label: 'Keywords to include (optional)',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: machine learning, neural networks, automation',
        limit: 1000,
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: friendly, funny, cheerful',
        limit: 250,
      },
    ],
  },
  {
    title: 'Bullet Points to Paragraph Converter',
    slug: 'bullet-points-to-paragraph-converter',
    hideWebSearch: true,
    description: 'Convert your bullet points into a well-structured paragraph!',
    icon: FaParagraph,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    onlyForPaidPlans: true,
    isFree: true,
    category: 'Writing',
    fields: [
      {
        label: 'Details as bullet points',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'example: \n- The educational path to becoming a software engineer\n- The skills required to become a software engineer',
        limit: 5000,
        enableMicrophone: true,
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, friendly, informative',
        limit: 250,
      },
    ],
  },
  {
    title: 'Clickbait Title Generator',
    slug: 'clickbait-title-generator',
    description: 'Generate attention-grabbing titles that get clicks!',
    icon: FaFish,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    onlyForPaidPlans: true,
    isFree: true,
    category: ['Marketing', 'Content', 'Social Media', 'Business'],
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'textarea',
        limit: 1000,
        placeholder: 'example: Website Design',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: catchy, energetic, clever',
      },
    ],
  },
  {
    title: 'Blog Post Generator',
    slug: 'full-blog-post',
    description: 'Generate a full blog post in seconds',
    icon: RiArticleFill,
    iconForeground: 'text-indigo-700',
    // onlyForPaidPlans: true,
    iconBackground: 'bg-indigo-50',
    // component: GenericComponent,
    hideDashoard: true,
    hideLandingPage: true,
    category: 'Content',
    outputs: 1,
    fields: [
      {
        label: 'Blog post title/topic',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: How To Become A Software Engineer',
      },
      // {
      //   label: 'Blog post intro',
      //   name: 'extra1',
      //   type: 'textarea',
      //   placeholder:
      //     'example: The skills required to become a software engineer. The education required to become a software engineer. The career opportunities for software engineers. The salary of a software engineer.',
      //   limit: 2000,
      // },
      {
        label: 'Blog post outline',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'example:\n- Introduction\n- The educational path to becoming a software engineer\n- The skills required to become a software engineer\n- The career opportunities\n- The salary of a software engineer\n- Network with other software engineers\n- Conclusion',
        limit: 2500,
      },

      {
        label: 'Point of View',
        name: 'extra6',
        type: 'select',
        placeholder: 'example: Positive',
        options: [
          'First Person Singular (I, me, my, mine)',
          'First Person Plural (we, us, our, ours)',
          'Second Person (you, your, yours)',
          'Third Person (he, she, it, they)',
        ],
      },
      // backround knowledge
      {
        label: 'Background knowledge (optional)',
        name: 'extra4',
        type: 'textarea',
        limit: 10000,

        // description:
        //   'If left blank, the AI will get the background knowledge from the internet.',
        placeholder:
          'include any additional information here for the AI to use when writing the article',
      },

      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'text',
        placeholder: 'example: professional, energetic, clever',
      },

      // toggle if get info from internet
      {
        label: 'Use Real-Time Data',
        name: 'extra5',
        type: 'checkbox',
        placeholder: 'example: true',
        initialValue: true,
      },
      // include FAQ
      {
        label: 'Include FAQ',
        name: 'extra3',
        type: 'checkbox',
        placeholder: 'example: true',
        initialValue: false,
      },
      {
        label: 'AI Model',
        name: 'model',
        type: 'select',
        options: [
          'GPT-4o mini',
          'GPT-4o',
          'GPT-5 mini',
          'GPT-5',
          // 'o3-mini',
          'Claude 4.5 Haiku',
          'Claude 4.5 Sonnet',
          // 'Claude 3.7 Sonnet',
          'Claude 4.8 Opus',
          'Google Gemini 2.5 Pro',
        ],
        placeholder: 'example: gpt-4',
        initialValue: 'gpt-4',
      },
    ],
  },
  {
    title: 'Instant Blog Post Generator',
    slug: 'blog-post',
    onlyForPaidPlans: true,
    description: 'Generate complete blog posts on any topic in seconds!',
    icon: ImBlog,
    iconForeground: 'text-orange-700',
    iconBackground: 'bg-orange-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    category: 'Content',
    // badge: 'Updated',
    outputs: 1,
    fields: [
      {
        label: 'Describe your blog topic',
        name: 'keywords',
        type: 'textarea',
        limit: 500,
        placeholder:
          'example: I visited the crypto event Token2049 in Singapore and met many VCs and entrepreneurs.',
      },

      {
        label: 'Keywords to include (optional)',
        name: 'extra3',
        type: 'text',
        placeholder: 'example: NFT, DeFi, Ethereum, collaboration',
        limit: 1000,
      },
      {
        label: 'Background knowledge (optional)',
        name: 'extra4',
        type: 'textarea',
        limit: 10000,
        placeholder:
          'example: The Premier Crypto Event in Asia TOKEN2049 brings together the global Web3 industry, uniting entrepreneurs, investors, developers, industry insiders and global media - and creates unparalleled networking opportunities. This is the community that will define what’s next in the space. Join us in Singapore, 13-14 September 2022 at the Marina Bay Sands',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, clever',
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'example: friendly, funny, cheerful',
        radioOptions: [
          { name: 'Short', checked: false },
          { name: 'Medium', checked: true },
          { name: 'Long', checked: false },
        ],
      },
    ],
  },
  {
    title: 'Instant Blog Post Generator V2',
    slug: 'blog-post-v2',
    onlyForPaidPlans: true,
    description:
      'Generate human-like blog posts on any topic in seconds! Undetectable by 🤖 AI Detectors.',
    icon: ImBlog,
    iconForeground: 'text-orange-700',
    iconBackground: 'bg-orange-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    category: 'Content',
    // badge: 'Updated',
    outputs: 1,
    fields: [
      {
        label: 'Describe your blog topic',
        name: 'keywords',
        type: 'textarea',
        limit: 500,
        placeholder:
          'example: I visited the crypto event Token2049 in Singapore and met many VCs and entrepreneurs.',
      },
      {
        label: 'Keywords to include (optional)',
        name: 'extra3',
        type: 'text',
        placeholder: 'example: NFT, DeFi, Ethereum, collaboration',
        limit: 1000,
      },
      // backround knowledge
      {
        label: 'Background knowledge (optional)',
        name: 'extra4',
        type: 'textarea',
        limit: 10000,
        placeholder:
          'example: The Premier Crypto Event in Asia TOKEN2049 brings together the global Web3 industry, uniting entrepreneurs, investors, developers, industry insiders and global media - and creates unparalleled networking opportunities. This is the community that will define what’s next in the space. Join us in Singapore, 13-14 September 2022 at the Marina Bay Sands',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, clever',
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'example: friendly, funny, cheerful',
        radioOptions: [
          { name: 'Short', checked: false },
          { name: 'Medium', checked: true },
          { name: 'Long', checked: false },
        ],
      },
    ],
  },
  {
    title: 'Instant Blog Post Generator V3',
    slug: 'blog-post-v3',
    badge: 'New',
    description:
      'Write long, real-time, factual articles with the click of a button. Featured image included.',
    icon: ImBlog,
    iconForeground: 'text-orange-700',
    iconBackground: 'bg-orange-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    category: 'Content',
    // onlyForPaidPlans: true,
    outputs: 1,
    fields: [
      {
        label: 'Describe your blog topic',
        name: 'keywords',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: how to optimize your website for search engines',
      },
      // point of view
      {
        label: 'Point of View',
        name: 'extra1',
        type: 'select',
        placeholder: 'example: Positive',
        options: [
          'First Person Singular (I, me, my, mine)',
          'First Person Plural (we, us, our, ours)',
          'Second Person (you, your, yours)',
          'Third Person (he, she, it, they)',
        ],
      },
      // backround knowledge
      {
        label: 'Background knowledge (optional)',
        name: 'extra4',
        type: 'textarea',
        limit: 10000,
        // description:
        //   'If left blank, the AI will get the background knowledge from the internet.',
        placeholder:
          'include any additional information here for the AI to use when writing the article',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'text',
        placeholder: 'example: professional, energetic, clever',
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'example: friendly, funny, cheerful',
        radioOptions: [
          { name: 'Short', checked: false },
          { name: 'Medium', checked: false },
          { name: 'Long', checked: true },
        ],
      },
      // toggle if get info from internet
      {
        label: 'Use Real-Time Data',
        name: 'extra2',
        type: 'checkbox',
        placeholder: 'example: true',
        initialValue: true,
      },
      // include FAQ
      {
        label: 'Include FAQ',
        name: 'extra3',
        type: 'checkbox',
        placeholder: 'example: true',
        initialValue: false,
      },
      {
        label: 'AI Model',
        name: 'model',
        type: 'select',
        options: [
          'GPT-4o mini',
          'GPT-4o',
          'GPT-5 mini',
          'GPT-5',
          // 'o3-mini',
          'Claude 4.5 Haiku',
          'Claude 4.5 Sonnet',
          // 'Claude 3.7 Sonnet',
          'Claude 4.8 Opus',
          'Google Gemini 2.5 Pro',
        ],
        placeholder: 'example: GPT-3.5',
        initialValue: 'GPT-4o mini',
      },
    ],
  },
  {
    title: 'YouTube Video to Article Generator',
    slug: 'youtube-video-to-article',
    badge: 'New',
    description:
      'Convert any YouTube video into a well-structured article in seconds!',
    icon: FaYoutube,
    iconForeground: 'text-orange-700',
    iconBackground: 'bg-orange-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    dateAdded: '2024-06-06',
    category: 'Content',
    outputs: 1,
    fields: [
      {
        label: 'YouTube Video URL',
        name: 'keywords',
        type: 'text',
        limit: 500,
        placeholder: 'example: https://www.youtube.com/watch?v=ngxyalzOS1g',
      },
      {
        label: 'Article Topic (optional)',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: How to add AI Chatbot to your website',
      },
      // backround knowledge
      {
        label: 'Background knowledge (optional)',
        name: 'extra4',
        type: 'textarea',
        limit: 10000,
        // description:
        //   'If left blank, the AI will get the background knowledge from the internet.',
        placeholder:
          'include any additional information here for the AI to use when writing the article',
      },
      // point of view
      {
        label: 'Point of View',
        name: 'extra1',
        type: 'select',
        placeholder: 'example: Positive',
        options: [
          'First Person Singular (I, me, my, mine)',
          'First Person Plural (we, us, our, ours)',
          'Second Person (you, your, yours)',
          'Third Person (he, she, it, they)',
        ],
      },

      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'text',
        placeholder: 'example: professional, energetic, clever',
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'example: friendly, funny, cheerful',
        radioOptions: [
          { name: 'Short', checked: false },
          { name: 'Medium', checked: false },
          { name: 'Long', checked: true },
        ],
      },
      // include FAQ
      {
        label: 'Include FAQ',
        name: 'extra3',
        type: 'checkbox',
        placeholder: 'example: true',
        initialValue: false,
      },
      {
        label: 'AI Model',
        name: 'model',
        type: 'select',
        options: [
          'GPT-4o mini',
          'GPT-4o',
          'GPT-5 mini',
          'GPT-5',
          // 'o3-mini',
          'Claude 4.5 Haiku',
          'Claude 4.5 Sonnet',
          // 'Claude 3.7 Sonnet',
          'Claude 4.8 Opus',
          'Google Gemini 2.5 Pro',
        ],
        placeholder: 'example: GPT-3.5',
        initialValue: 'GPT-4o mini',
      },
    ],
  },
  // {
  //   title: 'Instant Blog Post Generator V2',
  //   slug: 'blog-post-v2',
  // onlyForPaidPlans: true,
  //   description: 'Generate complete blog posts on any topic in seconds!',
  //   icon: ImBlog,
  //   shouldUseChatGPTAPI: true,
  //   shouldUseGPT4: true,
  //   iconForeground: 'text-orange-700',
  //   iconBackground: 'bg-orange-50',
  // component: GenericComponent,
  //   category: 'Content',
  //   outputs: 1,
  //   fields: [
  //     {
  //       label: 'Describe your blog topic',
  //       name: 'keywords',
  //       type: 'textarea',
  //       limit: 500,
  //       placeholder:
  //         'example: I visited the crypto event Token2049 in Singapore and met many VCs and entrepreneurs.',
  //     },
  //     {
  //       label: 'Keywords to include (optional)',
  //       name: 'extra3',
  //       type: 'text',
  //       placeholder: 'example: NFT, DeFi, Ethereum, collaboration',
  //       limit: 100,
  //     },
  //     {
  //       label: 'Tone (optional)',
  //       name: 'tone',
  //       type: 'text',
  //       placeholder: 'example: professional, energetic, clever',
  //     },
  //     {
  //       label: 'Output length',
  //       name: 'length',
  //       type: 'radio',
  //       placeholder: 'example: friendly, funny, cheerful',
  //       radioOptions: [
  //         { name: 'Short', checked: false },
  //         { name: 'Medium', checked: true },
  //         { name: 'Long', checked: false },
  //       ],
  //     },
  //   ],
  // },
  {
    title: 'Ask AI Writer',
    slug: 'ask-ai-writer',
    description: 'Generate a text based on your writing request',
    icon: GlobeAltIcon,
    iconForeground: 'text-sky-700',
    iconBackground: 'bg-sky-50',
    shouldUseChatGPTAPI: true,
    isFree: true,
    onlyForPaidPlans: true,
    category: ['Writing', 'Content'],
    fields: [
      {
        label: 'What do you want to generate?',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'example: Write a text about the importance of AI in modern technology',
        enableMicrophone: true,
      },
    ],
  },
  {
    title: 'Ghostwriter',
    slug: 'ghostwriter',
    description: 'Generate a text based on your writing request',
    icon: GlobeAltIcon,
    iconForeground: 'text-sky-700',
    iconBackground: 'bg-sky-50',
    shouldUseChatGPTAPI: true,
    category: ['Writing', 'Content'],
    isFree: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'What do you want to generate?',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'example: Write a text about the importance of AI in modern technology',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, clever',
      },
    ],
  },
  {
    title: 'Landing Page Copy',
    slug: 'landing-page-copy-generator',
    description: 'Generate a landing page copy in seconds',
    icon: GlobeAltIcon,
    iconForeground: 'text-sky-700',
    iconBackground: 'bg-sky-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    isFree: true,
    category: 'Marketing',
    fields: [
      {
        label: 'What is your landing page about?',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'example: Innovative AI-powered software to help you write better and faster copy',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, clever',
      },
    ],
  },
  {
    title: 'About Us Page Generator',
    slug: 'about-us-page-generator',
    description: 'Generate an About Us page for your website',
    icon: GlobeAltIcon,
    iconForeground: 'text-sky-700',
    iconBackground: 'bg-sky-50',
    shouldUseChatGPTAPI: true,
    isFree: true,
    onlyForPaidPlans: true,
    category: 'Content',
    fields: [
      {
        label: 'Company Name',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: Easy-Peasy.AI',
      },
      {
        label: 'What is your company about?',
        name: 'extra1',
        type: 'textarea',
        placeholder: 'example: We are a tech startup focusing on AI solutions',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, clever',
      },
    ],
  },
  {
    title: 'Company Profile Generator',
    slug: 'company-profile-generator',
    description: 'Generate a professional company profile',
    icon: GlobeAltIcon,
    iconForeground: 'text-sky-700',
    iconBackground: 'bg-sky-50',
    shouldUseChatGPTAPI: true,
    shouldUseO3Mini: true,
    onlyForPaidPlans: true,
    useTool: true,
    outputs: 1,
    maxGenerations: 1,
    loginPreset: 'chat?mode=create-slides',
    category: 'Business',
    fields: [
      {
        label: 'Company Name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Easy-Peasy.AI',
      },
      {
        label: 'What does your company do?',
        name: 'extra1',
        type: 'textarea',
        placeholder: 'example: We are a tech startup focusing on AI solutions',
        limit: 10000,
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, clever',
      },
    ],
  },
  {
    title: 'Company Bio Generator',
    slug: 'company-bio-generator',
    description: 'Generate a compelling company bio for your business',
    icon: GlobeAltIcon,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    shouldUseChatGPTAPI: true,
    // shouldUseO3Mini: true,
    onlyForPaidPlans: true,
    dateAdded: '2024-08-06',
    isFree: true,
    category: 'Business',
    fields: [
      {
        label: 'Company Name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: TechInnovate Solutions',
      },
      {
        label: 'Company Description',
        name: 'extra1',
        type: 'textarea',
        placeholder:
          'example: We are a cutting-edge software development company specializing in AI and machine learning solutions.',
        limit: 10000,
      },
      {
        label: 'Key Achievements or Unique Selling Points',
        name: 'extra2',
        type: 'textarea',
        placeholder:
          'example: Developed award-winning AI algorithms, Served Fortune 500 clients',
        limit: 10000,
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, innovative, trustworthy',
      },
    ],
  },
  {
    title: 'Core Value Generator',
    slug: 'core-value-generator',
    description: 'Generate meaningful core values for your organization',
    icon: HeartIcon,
    iconForeground: 'text-red-700',
    // shouldUseO3Mini: true,
    iconBackground: 'bg-red-50',
    shouldUseChatGPTAPI: true,
    isFree: true,
    category: 'Business',
    fields: [
      {
        label: 'Company Name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: TechInnovate Solutions',
      },
      {
        label: 'Company Description',
        name: 'extra1',
        type: 'textarea',
        placeholder:
          'example: We are a software development company focusing on AI solutions',
      },
      {
        label: 'Desired Number of Core Values',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: 5',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: inspiring, authentic, concise',
      },
    ],
  },
  {
    title: 'Sentence Expander',
    slug: 'sentence-expander',
    hideWebSearch: true,
    description:
      'This will make your writing more engaging and interesting to read',
    icon: FaExpand,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    // component: GenericComponent,
    category: 'Writing',
    shouldUseChatGPTAPI: true,
    isFree: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Sentence to expand',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Today is good weather',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, clever',
      },
    ],
  },
  {
    title: 'Paragraph Expander',
    slug: 'paragraph-expander',
    hideWebSearch: true,
    description:
      'Expand your paragraphs to make them more engaging and interesting to read.',
    icon: FaExpand,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    // component: GenericComponent,
    category: 'Writing',
    shouldUseChatGPTAPI: true,
    isFree: true,
    onlyForPaidPlans: true,
    maxGenerations: 2,
    fields: [
      {
        label: 'Paragraph to expand',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'example: Easy-Peasy.AI is an AI Content tool that can help you with a variety of writing tasks, from writing blog post, creating better resumes and job descriptions to composing emails and social media content, and many more. With 90+ templates, Easy-Peasy.AI can save you time and improve your writing skills.',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, clever',
      },
    ],
  },
  {
    title: 'Sentence Rewriter',
    slug: 'sentence-rewriter',
    hideWebSearch: true,
    description:
      'Rewrite your sentences to make them more engaging and interesting to read',
    icon: FaTextWidth,
    iconForeground: 'text-lime-700',
    iconBackground: 'bg-lime-50',
    // component: GenericComponent,
    category: 'Writing',
    shouldUseChatGPTAPI: true,
    onlyForPaidPlans: true,
    isFree: true,
    maxGenerations: 2,
    useOpenAI: true,
    fields: [
      {
        label: 'What sentence do you want to rewrite?',
        name: 'keywords',
        type: 'textarea',
        limit: 500,
        placeholder:
          'example: Now that we have a better understanding of the problem, we can start to think about solutions.',
      },
      {
        label: 'Expand the content?',
        name: 'extra1',
        type: 'checkbox',
        placeholder: 'example: true',
        initialValue: false,
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: friendly, funny, cheerful',
      },
      // {
      //   label: 'Use GPT-4?',
      //   name: 'useGPT4',
      //   type: 'checkbox',
      //   placeholder: 'example: true',
      //   initialValue: false,
      // },
    ],
  },
  {
    title: 'Sentence Finisher',
    slug: 'sentence-finisher',
    hideWebSearch: true,
    description: 'Complete your sentences with the help of AI',
    icon: FaTextWidth,
    iconForeground: 'text-lime-700',
    iconBackground: 'bg-lime-50',
    category: 'Writing',
    shouldUseChatGPTAPI: true,
    onlyForPaidPlans: true,
    isFree: true,
    maxGenerations: 3,
    fields: [
      {
        label: 'What sentence do you want to finish?',
        name: 'keywords',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: Despite the challenges we faced...',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, clever',
      },
    ],
  },
  {
    title: 'Paraphrasing Tool',
    slug: 'paraphrasing-tool',
    hideWebSearch: true,
    description: 'Rephrase any text in a variety of ways',
    icon: RefreshIcon,
    iconForeground: 'text-lime-700',
    iconBackground: 'bg-lime-50',
    // component: GenericComponent,
    category: 'Writing',
    outputs: 1,
    isFree: true,
    shouldUseChatGPTAPI: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Content',
        name: 'keywords',
        type: 'textarea',
        limit: 50000,
        placeholder:
          'example: Most companies try to stay ahead of the curve when it comes to visual design, but for Planetaria we needed to create a brand that would still inspire us 100 years from now when humanity has spread across our entire solar system.',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: friendly, funny, cheerful',
      },
      // {
      //   label: 'Use GPT-4?',
      //   name: 'useGPT4',
      //   type: 'checkbox',
      //   placeholder: 'example: true',
      //   initialValue: false,
      // },
    ],
  },
  {
    title: 'Content Continuer',
    slug: 'content-continuer',
    description: 'Takes a piece of text and keeps writing it for you',
    icon: FastForwardIcon,
    onlyForPaidPlans: true,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    // component: GenericComponent,
    isFree: true,
    category: 'Writing',
    shouldUseChatGPTAPI: true,
    maxGenerations: 3,
    fields: [
      {
        label: 'Text to continue',
        name: 'keywords',
        type: 'textarea',
        limit: 8000,
        placeholder:
          'example: In our situation nowadays, it is very important to',
      },
      {
        label: 'Keywords to include (optional)',
        name: 'extra1',
        type: 'text',
        placeholder:
          'example: carbon footprint, sustainability, climate change',
        limit: 500,
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: friendly, funny, cheerful',
        limit: 100,
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'example: friendly, funny, cheerful',
        radioOptions: [
          { name: 'Short', checked: false },
          { name: 'Medium', checked: true },
          { name: 'Long', checked: false },
        ],
      },
    ],
  },
  {
    title: 'Bullet Points Generator',
    slug: 'bullet-points-generator',
    hideWebSearch: true,
    description: 'Generates bullet points based on the provided text',
    icon: ViewListIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    category: 'Writing',
    shouldUseChatGPTAPI: true,
    // shouldUseO3Mini: true,
    isFree: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Text to generate bullet points from',
        name: 'keywords',
        type: 'textarea',
        limit: 50000,
        placeholder: 'example: Enter the text here',
      },
    ],
  },
  {
    title: 'Content Summarizer',
    slug: 'content-summarizer',
    hideWebSearch: true,
    description:
      'Get the main points of any text in a fraction of the time it would take to read it',
    icon: SearchIcon,
    iconForeground: 'text-red-700',
    iconBackground: 'bg-red-50',
    // component: GenericComponent,
    category: 'Writing',
    onlyForPaidPlans: true,
    //     shouldUseChatGPTAPI: true,
    outputs: 1,
    isFree: true,
    shouldUseAnthropic: true,
    fields: [
      {
        label: 'Content',
        name: 'keywords',
        type: 'textarea',
        limit: 200000,
        placeholder: `example:\nJavaScript was created at Netscape Communications by Brendan Eich in 1995. Netscape and Eich designed JavaScript as a scripting language for use with the company's flagship web browser, Netscape Navigator. Initially known as LiveScript, Netscape changed the name to JavaScript so they could position it as a companion for the Java language, a product of their partner, Sun Microsystems. Apart from some superficial syntactic similarities, though, JavaScript is in no way related to the Java programming language.\n\nAfter its release, more and more browsers started adding JavaScript support. Still, for much of its history JavaScript was not regarded as a serious programming language. Its earliest releases suffered from notable performance and security issues, but developers had no alternatives. If they wanted to run programs in the browser, they had to use JavaScript.\n\nIn 2008, the creation of Google's open-source Chrome V8, a high-performance JavaScript engine, provided a crucial turning point for JavaScript. The subsequent proliferation of fast JavaScript engines made it possible for developers to build sophisticated browser-based applications with performance that competed with desktop and mobile applications.\n\nSoon after, Ryan Dahl released an open-source, cross-platform environment called Node.js. It provided a way to run JavaScript code from outside a browser. It freed JavaScript from the browser's confines and led directly to JavaScript's current popularity. Today, you can use JavaScript to write all kinds of applications, including browser, server, mobile, and desktop applications. Most major online companies today, including Facebook, Twitter, Netflix, and Google, all use JavaScript in their products.`,
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'example: friendly, funny, cheerful',
        radioOptions: [
          { name: 'Short', checked: true },
          { name: 'Long', checked: false },
        ],
      },
    ],
  },
  {
    title: 'Action Items Generator',
    slug: 'action-items-generator',
    description: 'Generate actionable items from meeting notes or discussions',
    icon: CheckCircleIcon,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    category: 'Business',
    shouldUseChatGPTAPI: true,
    shouldUseAnthropic: true,
    isFree: true,
    fields: [
      {
        label: 'Content',
        name: 'keywords',
        type: 'textarea',
        limit: 100000,
        placeholder: 'example: Enter meeting notes or discussion points here',
      },
    ],
  },
  {
    title: 'Meeting Minutes Generator',
    slug: 'meeting-minutes-generator',
    description:
      'Generate concise and organized meeting minutes from your notes',
    icon: ViewListIcon,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    category: 'Business',
    shouldUseChatGPTAPI: true,
    shouldUseAnthropic: true,
    isFree: true,
    fields: [
      {
        label: 'Content',
        name: 'keywords',
        type: 'textarea',
        limit: 100000,
        placeholder: 'Enter your raw meeting notes or discussion points here',
      },
      {
        label: 'Meeting Date',
        name: 'extra1',
        type: 'text',
        placeholder: 'e.g., 2023-07-15',
      },
      {
        label: 'Attendees',
        name: 'extra2',
        type: 'text',
        placeholder: 'e.g., John Doe, Jane Smith, Alex Johnson',
      },
      {
        label: 'Meeting Objective',
        name: 'extra3',
        type: 'text',
        placeholder: 'e.g., Quarterly review, Project kickoff',
      },
    ],
  },
  {
    title: 'Topics and Bullets Generator',
    slug: 'topics-and-bullets',
    description: 'Extract key topics and bullet points from your text',
    icon: ViewListIcon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    category: 'Business',
    shouldUseAnthropic: true,
    shouldUseChatGPTAPI: true,
    isFree: true,
    fields: [
      {
        label: 'Content',
        name: 'keywords',
        type: 'textarea',
        limit: 200000,
        placeholder: 'Enter your text here to extract topics and bullet points',
      },
    ],
  },
  {
    title: 'Grammar Corrector',
    slug: 'grammar-corrector',
    hideWebSearch: true,
    description: 'Make sure your writing is error-free!',
    icon: FaTools,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    // component: GenericComponent,
    category: 'Writing',
    outputs: 1,
    // hideLanguageSelector: true,
    shouldUseChatGPTAPI: true,
    isFree: true,
    onlyForPaidPlans: true,
    maxGenerations: 2,
    fields: [
      {
        label: 'Text',
        name: 'keywords',
        type: 'textarea',
        limit: 5000,
        placeholder:
          'example: I has breakfast with the my friends these morning.',
        enableMicrophone: true,
      },
    ],
  },
  {
    title: 'Text Shortener',
    slug: 'content-shortener',
    hideWebSearch: true,
    description: 'Shorten your text to the most important parts',
    outputs: 1,
    icon: FaCompress,
    iconForeground: 'text-cyan-700',
    iconBackground: 'bg-cyan-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    category: 'Writing',
    maxGenerations: 5,
    fields: [
      {
        label: 'Text to shorten',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'example: If you notice weak airflow, contact us to schedule a Heating, ventilation, and air conditioning service technician to check it out as soon as possible.',
      },
    ],
  },
  {
    title: 'Brainstorming Tool',
    slug: 'brainstorming-tool',
    description: 'Generate creative solutions and ideas for any problem',
    icon: LightBulbIcon,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    // component: GenericComponent,
    category: ['Business', 'Education'],
    outputs: 1,
    // hideLanguageSelector: true,
    shouldUseChatGPTAPI: true,
    onlyForPaidPlans: true,
    isFree: true,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'textarea',
        limit: 5000,
        placeholder: 'example: Sustainable energy solutions',
      },
    ],
  },
  {
    title: 'Cold Email Generator',
    slug: 'cold-email-generation',
    description: 'Write cold emails that actually work and get responses.',
    icon: MailIcon,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    // component: GenericComponent,
    category: ['Email & Messages', 'Marketing'],
    isFree: true,
    shouldUseChatGPTAPI: true,
    onlyForPaidPlans: true,
    maxGenerations: 2,
    fields: [
      {
        label: 'Product/Service Description',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'example: Easy-Peasy.AI is an AI-powered content assistant that helps you and your team create original and polished content 10 times faster.',
      },
      {
        label: 'Your Company/Product Name',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Easy-Peasy.AI',
      },
      {
        label: 'Context to include in the email',
        name: 'extra2',
        type: 'text',
        placeholder:
          'example: I saw your blog and I think you might be interested in our product',
      },
      {
        label: 'Tone (optional)',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, witty, energetic',
      },
    ],
  },
  {
    title: 'Event Invitation Email',
    slug: 'event-invitation-email',
    description:
      'Write a persuasive email to increase attendance at your event',
    icon: MailIcon,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    // component: GenericComponent,
    category: ['Email & Messages', 'Business'],
    isFree: true,
    shouldUseChatGPTAPI: true,
    maxGenerations: 5,
    fields: [
      {
        label: 'Event Name/Topic',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: AI Conference',
      },
      {
        label: 'Event Details',
        name: 'extra1',
        type: 'textarea',
        placeholder:
          'example: The AI Conference is a 2-day event that will bring together the brightest minds in the AI industry to discuss the latest developments in the field.',
      },
      {
        label: 'Tone (optional)',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, witty, energetic',
      },
    ],
  },
  {
    title: 'Event Reminder Email Generator',
    slug: 'event-reminder-email',
    description: 'Generate a reminder email for your upcoming event',
    icon: MailIcon,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    category: ['Email & Messages', 'Business'],
    isFree: true,
    shouldUseChatGPTAPI: true,
    maxGenerations: 5,
    fields: [
      {
        label: 'Event name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: AI Conference',
      },
      {
        label: 'Event description',
        name: 'extra1',
        type: 'textarea',
        placeholder:
          'example: A 2-day event that brings together the brightest minds in the AI industry.',
      },
      {
        label: 'Location',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: Marina Bay Sands, Singapore',
      },
      {
        label: 'Date and time',
        name: 'extra3',
        type: 'text',
        placeholder: 'example: 13-14 September 2022, 9:00 AM - 5:00 PM',
      },
      {
        label: 'Contact information for questions or concerns',
        name: 'extra4',
        type: 'text',
        placeholder: 'example: John Doe, johndoe@example.com, +1 234 567 890',
      },
    ],
  },
  {
    title: 'Event Name Generator',
    slug: 'event-name-generator',
    description: 'Generate creative and catchy names for your event',
    icon: CalendarIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    category: ['Business', 'Content'],
    isFree: true,
    shouldUseChatGPTAPI: true,
    maxGenerations: 5,
    fields: [
      {
        label: 'Event description',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'example: A 2-day event that brings together the brightest minds in the AI industry.',
      },
    ],
  },
  {
    title: 'Backlink Outreach Email',
    slug: 'backlink-outreach-email',
    description: 'Write an email to get backlinks from other websites',
    icon: MailIcon,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    // component: GenericComponent,
    category: ['Email & Messages', 'Marketing'],
    maxGenerations: 5,
    isFree: true,
    shouldUseChatGPTAPI: true,
    fields: [
      {
        label: 'Your Name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Peter Tan',
      },
      {
        label: 'Your Company Name',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Easy-Peasy.AI',
      },
      {
        label: 'Your Website URL',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: https://easy-peasy.ai',
      },
      {
        label: 'The Website URL You Want to Get a Backlink From',
        name: 'extra3',
        type: 'text',
        placeholder: 'example: https://www.entrepreneur.com',
      },
      {
        label: 'Tone (optional)',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, witty, energetic',
      },
    ],
  },
  {
    title: 'Follow Up Email Generator',
    slug: 'follow-up-email',
    description:
      'Craft a compelling follow-up email to keep your conversation going',
    icon: MailIcon,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    shouldUseChatGPTAPI: true,
    category: ['Email & Messages', 'Business'],
    isFree: true,
    maxGenerations: 5,
    fields: [
      {
        label: "Recipient's name",
        name: 'keywords',
        type: 'text',
        placeholder: 'example: John Doe',
      },
      {
        label: 'Subject of the previous conversation',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Project Collaboration',
      },
      {
        label: 'Key points to mention in the follow-up',
        name: 'extra2',
        type: 'text',
        placeholder:
          'example: Discussing next steps, Asking for feedback, etc.',
      },
      {
        label: 'Tone (optional)',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, friendly, energetic',
      },
    ],
  },
  {
    title: 'Waitlist Email Generator',
    slug: 'waitlist-email',
    description: 'Craft a compelling waitlist email to keep your users engaged',
    icon: MailIcon,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    shouldUseChatGPTAPI: true,
    category: ['Email & Messages', 'Marketing'],
    isFree: true,
    maxGenerations: 5,
    fields: [
      {
        label: "User's name",
        name: 'keywords',
        type: 'text',
        placeholder: 'example: John Doe',
      },
      {
        label: 'Product/Service name',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Easy-Peasy.AI',
      },
      {
        label: 'Reason for waitlisting',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: too many users, not enough resources, etc.',
      },
      {
        label: 'Ways to stay engaged with the product',
        name: 'extra3',
        type: 'text',
        placeholder:
          'example: join an online community, sign up for a newsletter, etc.',
      },
    ],
  },
  {
    title: 'Email Subject Line Generator',
    slug: 'email-subject-line-generation',
    description: 'Generate attention-grabbing email subject lines in seconds!',
    icon: MailIcon,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    shouldUseChatGPTAPI: true,
    isFree: true,
    // component: GenericComponent,
    category: ['Email & Messages', 'Marketing'],
    fields: [
      {
        label: 'What is the email about?',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: ask about pay raise',
      },
      {
        label: 'Tone (optional)',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic',
      },
    ],
  },
  {
    title: 'Email Rewriter',
    slug: 'email-rewriter',
    description:
      'Rewrite and improve your emails for better clarity and impact.',
    icon: MailIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    category: ['Email & Messages', 'Business'],
    isFree: true,
    shouldUseGPT4o: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Email to rewrite',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'Paste the email you want to rewrite here',
        limit: 10000,
      },
      {
        label: 'Improvement goals',
        name: 'extra1',
        type: 'select',
        options: [
          'More concise',
          'More professional',
          'More persuasive',
          'More friendly',
          'Fix grammar and clarity',
          'All of the above',
        ],
        placeholder: 'Select improvement goal',
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'e.g., professional, friendly, assertive',
      },
      {
        label: 'Additional instructions (optional)',
        name: 'extra2',
        type: 'textarea',
        placeholder: 'Any specific changes or elements to include',
        limit: 500,
      },
    ],
  },
  {
    title: 'Personal Statement Generator',
    slug: 'personal-statement-generator',
    description: 'Creating Strong, Unique Personal Statements Effortlessly!',
    icon: AcademicCapIcon,
    iconForeground: 'text-cyan-700',
    iconBackground: 'bg-cyan-50',
    // component: GenericComponent,
    onlyForPaidPlans: true,
    isFree: true,
    shouldUseGPT4o: true,
    category: ['Career', 'Education'],
    shouldUseChatGPTAPI: true,
    fields: [
      {
        label: 'Course applied',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Computer Science',
      },
      {
        label: 'Type of school',
        name: 'extra3',
        type: 'select',
        placeholder: 'example: Positive',
        options: [
          'College',
          'University',
          'Graduate School',
          'MBA',
          'PhD',
          'Medical School',
          'Law School',
          'Nursing School',
          'Dental School',
          'PA School',
          'Other',
        ],
      },
      {
        label: 'Why did you choose this subject?',
        name: 'extra4',
        type: 'textarea',
        limit: 1500,
        placeholder: '',
      },
      {
        label:
          'Skills and achievements, experience of studying, reading or research in the field',
        name: 'extra1',
        type: 'textarea',
        limit: 1500,
        placeholder: '',
      },
      {
        label: 'Work experience and future plans (optional)',
        name: 'extra2',
        type: 'textarea',
        limit: 1500,
        placeholder: '',
      },
    ],
  },
  {
    title: 'Passion Project Generator',
    slug: 'passion-project-generator',
    description:
      'Generate a unique and inspiring passion project idea in seconds!',
    icon: LightBulbIcon,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    shouldUseChatGPTAPI: true,
    isFree: true,
    maxGenerations: 5,
    category: ['Personal & Fun', 'Education'],
    fields: [
      {
        label: 'Your Interests',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Photography, Coding, Cooking',
      },
      {
        label: 'Time Commitment',
        name: 'extra1',
        type: 'select',
        placeholder: 'example: 5 hours per week',
        options: [
          '1-2 hours per week',
          '3-5 hours per week',
          '6-10 hours per week',
          'More than 10 hours per week',
        ],
      },
      {
        label: 'Project Goals',
        name: 'extra2',
        type: 'textarea',
        limit: 1500,
        placeholder:
          'example: Learn a new skill, Build a portfolio, Start a business, Improve academic performance',
      },
    ],
  },
  {
    title: 'Reference Letter Generator',
    slug: 'reference-letter-generator',
    description: 'Generate a personalized reference letter in seconds!',
    icon: BriefcaseIcon,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    onlyForPaidPlans: true,
    category: ['HR', 'Career'],
    isFree: true,
    fields: [
      {
        label: "Recipient's name",
        name: 'keywords',
        type: 'text',
        placeholder: 'example: John Doe',
      },
      {
        label: "Writer's name",
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Jane Smith',
      },
      {
        label: "Writer's relationship to subject",
        name: 'extra2',
        type: 'text',
        placeholder: 'example: Manager',
      },
      {
        label: "Subject's character, skills, and accomplishments",
        name: 'extra3',
        type: 'textarea',
        placeholder:
          'example: John is a hardworking individual with excellent communication skills...',
      },
      {
        label: 'Position/scholarship/etc. that subject is applying for',
        name: 'extra4',
        type: 'text',
        placeholder: 'example: Software Engineer position at Google',
      },
    ],
  },
  {
    title: 'Character Reference Letter Generator',
    slug: 'character-reference-letter-generator',
    description:
      'Generate a personalized character reference letter in seconds!',
    icon: BriefcaseIcon,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    shouldUseChatGPTAPI: true,
    isFree: true,
    onlyForPaidPlans: true,
    category: ['HR', 'Career'],
    fields: [
      {
        label: "Recipient's name",
        name: 'keywords',
        type: 'text',
        placeholder: 'example: John Doe',
      },
      {
        label: "Writer's relationship to subject",
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Manager',
      },
      {
        label: "Subject's strengths",
        name: 'extra2',
        type: 'textarea',
        placeholder:
          'example: John is a hardworking individual with excellent communication skills...',
      },
      {
        label: "Subject's weaknesses",
        name: 'extra3',
        type: 'textarea',
        placeholder:
          'example: John sometimes struggles with time management...',
      },
      {
        label: 'Job/school/etc. description',
        name: 'extra4',
        type: 'text',
        placeholder: 'example: Software Engineer position at Google',
      },
    ],
  },
  {
    title: 'Resume Headline Generator',
    slug: 'resume-headline-generator',
    description: 'Get the Job You Want with Our Resume Headline Generator',
    icon: ImWoman,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    category: 'Career',
    isFree: true,
    fields: [
      {
        label: 'Job Role and skills',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: web developer, php, javascript',
      },
      {
        label: 'Tone (optional)',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic',
      },
    ],
  },
  {
    title: 'LinkedIn Bio Generator',
    slug: 'linkedin-bio-generator',
    description:
      'With just a few clicks, you can create a custom bio that will help you stand out from the crowd',
    icon: ImLinkedin,
    iconForeground: 'text-sky-700',
    iconBackground: 'bg-sky-50',
    shouldUseChatGPTAPI: true,
    isFree: true,
    maxGenerations: 5,
    // component: GenericComponent,
    category: ['Career', 'Social Media'],
    fields: [
      {
        label: 'Personal Information',
        name: 'keywords',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example:\n- 10 years of experience\n- Worked at Google\n- Expert in Javascript and Java',
      },
    ],
  },
  {
    title: 'LinkedIn Recommendation Generator',
    slug: 'linkedin-recommendation-generator',
    description:
      "Stuck writing a recommendation for someone's profile? Use this tool to get started!",
    icon: ImLinkedin,
    iconForeground: 'text-sky-700',
    iconBackground: 'bg-sky-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    maxGenerations: 5,
    isFree: true,
    category: ['Career', 'HR', 'Social Media'],
    fields: [
      {
        label: 'Name of the person you want to recommend',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Peter Parker',
      },
      {
        label: 'Their Job Title',
        name: 'extra3',
        type: 'text',
        placeholder: 'example: Web Developer',
      },
      {
        label: 'What is the company name?',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Google',
      },
      {
        label: 'What is the role of the person you want to recommend?',
        name: 'extra2',
        type: 'select',
        placeholder: 'example: Colleague',
        options: ['Colleague', 'Manager', 'Client', 'Vendor', 'Other'],
      },
      {
        label: 'What to include in the recommendation? (optional)',
        name: 'extra4',
        type: 'textarea',
        placeholder:
          'example: Successful in managing projects, managing teams anf implementing new technologies',
        limit: 500,
      },
    ],
  },
  {
    title: 'LinkedIn Connection Request Message Generator',
    slug: 'linkedin-connection-invite-message',
    description:
      'Craft a personalized connection request on LinkedIn to expand your professional network',
    icon: ImLinkedin,
    iconForeground: 'text-sky-700',
    iconBackground: 'bg-sky-50',
    shouldUseChatGPTAPI: true,
    maxGenerations: 5,
    isFree: true,
    category: ['Career', 'Social Media'],
    fields: [
      {
        label: 'Your Name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: John Doe',
      },
      {
        label: 'Your Goals',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Find a new job in software development',
      },
      {
        label: "Person's Name",
        name: 'extra2',
        type: 'text',
        placeholder: 'example: Jane Smith',
      },
      {
        label: 'How You Know Them',
        name: 'extra3',
        type: 'text',
        placeholder: 'example: Met at a tech conference',
      },
      {
        label: 'Specific Action Requested',
        name: 'extra4',
        type: 'text',
        placeholder: 'example: Meet for a virtual coffee chat',
      },
    ],
  },
  {
    title: 'LinkedIn Headline Generator',
    slug: 'linkedin-headline-generator',
    description: 'Create the Perfect Professional Headline for Your Profile',
    icon: ImLinkedin,
    iconForeground: 'text-sky-700',
    iconBackground: 'bg-sky-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    category: ['Career', 'Social Media'],
    isFree: true,
    maxGenerations: 5,
    fields: [
      {
        label: 'Job Role and skills',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: web developer, php, javascript',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic',
      },
    ],
  },
  {
    title: 'Performance Review Generator',
    slug: 'performance-review-generator',
    description:
      'Make your performance review process easy with our generator!',
    icon: PencilAltIcon,
    iconForeground: 'text-sky-700',
    iconBackground: 'bg-sky-50',
    // component: GenericComponent,
    onlyForPaidPlans: true,
    // shouldUseO3Mini: true,
    isFree: true,
    shouldUseChatGPTAPI: true,
    category: ['Business', 'HR'],
    showExamples: true,
    fields: [
      {
        label: 'Employee Name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Peter Parker',
      },
      {
        label: 'Employee Job Title',
        name: 'extra3',
        type: 'text',
        placeholder: 'example: Web Developer',
      },
      {
        label: 'Point of View',
        name: 'extra2',
        type: 'select',
        options: [
          '"First Person Singular (I, me, my, mine)"',
          'Second Person (you, your, yours)',
          'Third Person (he, she, it, they)',
        ],
        placeholder: 'example: First Person Singular (I, me, my, mine)',
      },
      {
        label: 'Key Achievements (optional)',
        name: 'extra1',
        type: 'textarea',
        limit: 10000,
        placeholder:
          'example:\n- improved website speed by 20%,\n- introduced Tailwind CSS to the team\n- spearheaded the company’s move to remote work',
        enableMicrophone: true,
      },
      {
        label: 'Areas for Improvement',
        name: 'extra4',
        type: 'textarea',
        limit: 5000,
        placeholder:
          'example:\n- time management skills\n- documentation practices\n- communication with stakeholders',
        enableMicrophone: true,
      },
      {
        label: 'Additional Instructions (optional)',
        name: 'extra5',
        type: 'textarea',
        placeholder: 'example: Make the review concise and to the point',
      },
    ],
  },
  // {
  //   title: 'Comprehensive Performance Review',
  //   slug: 'comprehensive-performance-review',
  //   description:
  //     'Generate detailed performance reviews covering key professional competencies including technical skills, collaboration, communication, leadership, and more.',
  //   icon: ClipboardCheckIcon,
  //   iconForeground: 'text-purple-700',
  //   iconBackground: 'bg-purple-50',
  //   onlyForPaidPlans: true,
  //   isFree: true,
  //   shouldUseChatGPTAPI: true,
  //   shouldUseGPT4o: true,
  //   category: ['Business', 'HR'],
  //   dateAdded: '2025-01-29',
  //   fields: [
  //     {
  //       label: 'Employee Name',
  //       name: 'keywords',
  //       type: 'text',
  //       placeholder: 'example: John Smith',
  //     },
  //     {
  //       label: 'Job Title/Role',
  //       name: 'extra1',
  //       type: 'text',
  //       placeholder: 'example: Senior Software Engineer',
  //     },
  //     {
  //       label: 'Review Period',
  //       name: 'extra2',
  //       type: 'text',
  //       placeholder: 'example: Q1 2025 or January - March 2025',
  //     },
  //     {
  //       label: 'Technical Knowledge & Capability - Strengths',
  //       name: 'extra3',
  //       type: 'textarea',
  //       placeholder:
  //         'Examples:\n- Demonstrated deep expertise in cloud architecture and AWS services\n- Successfully implemented new CI/CD pipeline reducing deployment time by 50%\n- Mentored junior developers on best coding practices and design patterns\n- Mastered React and TypeScript, becoming the go-to expert on the team',
  //       limit: 2500,
  //     },
  //     {
  //       label: 'Technical Knowledge & Capability - Development Areas',
  //       name: 'extra4',
  //       type: 'textarea',
  //       placeholder:
  //         'Examples:\n- Could benefit from learning more about database optimization\n- Need to stay updated with latest security best practices\n- Should explore machine learning fundamentals for upcoming projects\n- Documentation skills could be improved for better knowledge sharing',
  //       limit: 2500,
  //     },
  //     {
  //       label: 'Collaboration & Relationship Building - Strengths',
  //       name: 'extra5',
  //       type: 'textarea',
  //       placeholder:
  //         'Examples:\n- Excellent at fostering team cohesion and inclusive environment\n- Built strong cross-functional relationships with product and design teams\n- Successfully mediated conflicts and found win-win solutions\n- Actively participates in knowledge sharing and pair programming sessions',
  //       limit: 2500,
  //     },
  //     {
  //       label: 'Collaboration & Relationship Building - Development Areas',
  //       name: 'extra6',
  //       type: 'textarea',
  //       placeholder:
  //         'Examples:\n- Could engage more proactively with stakeholders\n- Sometimes works in isolation - should seek more collaboration opportunities\n- Need to improve active listening during team discussions\n- Should work on building relationships outside immediate team',
  //       limit: 2500,
  //     },
  //     {
  //       label: 'Communication & Impact - Strengths',
  //       name: 'extra7',
  //       type: 'textarea',
  //       placeholder:
  //         'Examples:\n- Clear and concise in written documentation and emails\n- Delivered compelling presentations to executive leadership\n- Effectively communicates technical concepts to non-technical audiences\n- Provides constructive feedback that drives team improvement',
  //       limit: 2500,
  //     },
  //     {
  //       label: 'Communication & Impact - Development Areas',
  //       name: 'extra8',
  //       type: 'textarea',
  //       placeholder:
  //         'Examples:\n- Could be more assertive in expressing opinions during meetings\n- Written communication sometimes lacks necessary detail\n- Should work on presentation skills for larger audiences\n- Need to improve follow-up communication on project status',
  //       limit: 2500,
  //     },
  //     {
  //       label: 'Firm Building - Strengths',
  //       name: 'extra9',
  //       type: 'textarea',
  //       placeholder:
  //         'Examples:\n- Championed diversity and inclusion initiatives\n- Led recruitment efforts resulting in 3 successful hires\n- Contributed to company blog and thought leadership\n- Organized team building events that improved morale',
  //       limit: 2500,
  //     },
  //     {
  //       label: 'Firm Building - Development Areas',
  //       name: 'extra10',
  //       type: 'textarea',
  //       placeholder:
  //         'Examples:\n- Could take more initiative in company-wide projects\n- Should participate more in recruitment and interviewing\n- Need to contribute to defining team/company best practices\n- Could be more involved in company culture initiatives',
  //       limit: 2500,
  //     },
  //     {
  //       label: 'People Leadership & Management - Strengths',
  //       name: 'extra11',
  //       type: 'textarea',
  //       placeholder:
  //         'Examples:\n- Successfully coached 2 team members to promotions\n- Delegates effectively while maintaining accountability\n- Creates psychological safety for team to experiment and fail\n- Excellent at setting clear expectations and goals',
  //       limit: 2500,
  //     },
  //     {
  //       label: 'People Leadership & Management - Development Areas',
  //       name: 'extra12',
  //       type: 'textarea',
  //       placeholder:
  //         'Examples:\n- Need to improve at having difficult conversations\n- Should work on providing more regular feedback\n- Could better balance being supportive vs. challenging team\n- Need to develop skills in performance management',
  //       limit: 2500,
  //     },
  //     {
  //       label: 'Professionalism - Strengths',
  //       name: 'extra13',
  //       type: 'textarea',
  //       placeholder:
  //         'Examples:\n- Consistently meets deadlines and commitments\n- Maintains high ethical standards in all interactions\n- Handles confidential information with appropriate discretion\n- Demonstrates resilience and composure under pressure',
  //       limit: 2500,
  //     },
  //     {
  //       label: 'Professionalism - Development Areas',
  //       name: 'extra14',
  //       type: 'textarea',
  //       placeholder:
  //         'Examples:\n- Could improve time management and prioritization\n- Sometimes needs reminders about following processes\n- Should work on maintaining professional boundaries\n- Need to be more punctual for meetings and deadlines',
  //       limit: 2500,
  //     },
  //     {
  //       label: 'Length',
  //       name: 'length',
  //       type: 'select',
  //       options: ['Concise', 'Detailed', 'Comprehensive'],
  //       placeholder: 'Select review length',
  //       initialValue: 'Detailed',
  //     },
  //   ],
  //   maxGenerations: 1,
  // },
  {
    title: 'Peer Review Generator',
    slug: 'peer-review-generator',
    description:
      'Generate constructive peer reviews for colleagues or team members.',
    icon: UsersIcon,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    shouldUseChatGPTAPI: true,
    category: ['Business', 'HR'],
    dateAdded: '2024-06-30',
    isFree: true,
    onlyForPaidPlans: true,
    shouldUseGPT4o: true,
    fields: [
      {
        label: 'Name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: John Doe',
      },
      {
        label: 'Overall feedback',
        name: 'extra1',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: John has been a valuable team member this quarter...',
      },
      {
        label: 'Key strengths',
        name: 'extra2',
        type: 'textarea',
        limit: 200,
        placeholder: 'example: Teamwork, Problem-solving',
      },
      {
        label: 'Areas for improvement',
        name: 'extra3',
        type: 'textarea',
        limit: 200,
        placeholder: 'example: Time management, Communication',
      },
    ],
  },
  {
    title: 'Letter Of Recommendation Generator',
    slug: 'letter-of-recommendation-generator',
    description: 'Generate a Letter of Recommendation in Seconds!',
    icon: BriefcaseIcon,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    onlyForPaidPlans: true,
    isFree: true,
    category: ['HR', 'Career'],
    fields: [
      {
        label: 'Name of the person you want to recommend',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Peter Parker',
      },
      {
        label: 'Their Job Title',
        name: 'extra3',
        type: 'text',
        placeholder: 'example: Web Developer',
      },
      {
        label: 'Relationship with the person you want to recommend',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: Colleague',
      },
      {
        label: 'What is their strengths, skills, and achievements?',
        name: 'extra1',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example:\n- improved website speed by 20%,\n- introduced Tailwind CSS to the team\n- spearheaded the company’s move to remote work',
      },
      {
        label: 'Why they are a good fit for the job?',
        name: 'extra4',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example:\n- team player\n- hard worker\n- good communicator',
      },
    ],
  },
  // {
  //   title: 'Testimonial & Review Generator',
  //   slug: 'testimonial-and-review-generator',
  //   description: 'Generate Testimonials & Reviews in Seconds!',
  //   icon: RssIcon,
  //   iconForeground: 'text-red-700',
  //   iconBackground: 'bg-red-50',
  //   // component: GenericComponent,
  //   isFree: false,
  //   onlyForPaidPlans: true,
  //   shouldUseChatGPTAPI: true,
  //   shouldUseGPT4o: true,
  //   category: 'Business',
  //   fields: [
  //     {
  //       label: 'Product/Service Name',
  //       name: 'keywords',
  //       type: 'text',
  //       placeholder: 'example: Easy-Peasy.AI',
  //     },
  //     {
  //       label: 'What include in the review/testimonial?',
  //       name: 'extra1',
  //       type: 'textarea',
  //       limit: 1000,
  //       placeholder:
  //         'example: Amazing app. can quickly create content for my blog, social media, job descriptions. can manipulate its tone according to your need.',
  //     },
  //     {
  //       label: 'Tone (optional)',
  //       name: 'tone',
  //       type: 'brand_voice',
  //       placeholder: 'example: catchy, energetic, clever',
  //     },
  //     {
  //       label: 'Output length',
  //       name: 'length',
  //       type: 'radio',
  //       placeholder: 'example: friendly, funny, cheerful',
  //       radioOptions: [
  //         { name: 'Short', checked: true },
  //         { name: 'Long', checked: false },
  //       ],
  //     },
  //   ],
  // },
  {
    title: 'Review Response Generator',
    slug: 'review-response-generator',
    description: 'Write responses to public customer reviews easily!',
    icon: ReplyIcon,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    onlyForPaidPlans: true,
    isFree: true,
    showExamples: true,
    category: 'Business',
    fields: [
      {
        label: 'Customer review',
        name: 'keywords',
        type: 'textarea',
        limit: 10000,
        placeholder: 'Insert the full review here',
      },
      {
        label: 'Product/Service Name',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Easy-Peasy.AI',
      },
      {
        label: 'Reviewer name',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: Peter',
      },
      {
        label: 'Star rating from reviewer (from 1-5) (optional)',
        name: 'extra3',
        type: 'text',
        placeholder: 'example: 5',
      },
      {
        label: 'What include in response? (optional)',
        name: 'extra4',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Thank you for your review. We are glad you liked our product. We are working on improving our product and will keep you updated.',
      },
      {
        label: 'Tone (optional)',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: catchy, energetic, clever',
      },
    ],
  },
  {
    title: 'Press Release Generator',
    slug: 'press-release-generator',
    description:
      'Create Professional Press Releases in Seconds with our Easy-Peasy-to-Use Generator!',
    icon: NewspaperIcon,
    iconForeground: 'text-zinc-700',
    iconBackground: 'bg-zinc-50',
    // component: GenericComponent,
    category: 'Business',
    shouldUseChatGPTAPI: true,
    isFree: true,
    // shouldUseO3Mini: true,
    onlyForPaidPlans: true,
    outputs: 2,
    fields: [
      {
        label: 'Product/Service Name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Easy-Peasy.AI',
      },
      {
        label: 'What is your Press Release about?',
        name: 'extra1',
        type: 'textarea',
        limit: 75000,
        placeholder:
          'example: Easy-Peasy.AI launches a new product called AI Audio Transcription. Transcribe your audio files with AI-powered transcription.',
      },
      {
        label: 'Keywords (optional)',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: artificial intelligence, podcast, transcription',
      },
      {
        label: 'Additional Instructions (optional)',
        name: 'extra3',
        type: 'textarea',
        placeholder:
          'example: The length of the press release should be 300 words. Or the press release should be in the format of a news article.',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional',
      },
    ],
  },
  {
    title: 'Catchy Tagline',
    slug: 'catchy-tagline',
    description: 'Create catchy slogans and taglines for free!',
    icon: RiPriceTagLine,
    iconForeground: 'text-orange-700',
    iconBackground: 'bg-orange-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    category: 'Marketing',
    isFree: true,
    showExamples: true,
    maxGenerations: 5,
    fields: [
      {
        label: 'Tell us about your business or product',
        name: 'keywords',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Easy-Peasy.AI is an AI copywriting tool that can help you with a variety of writing tasks, from creating better resumes and job descriptions to composing emails and social media content.',
      },
    ],
  },
  {
    title: 'Slogan Generator',
    slug: 'slogan-generator',
    description: 'Create slogan for your business or product!',
    icon: RiPriceTagLine,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    category: 'Marketing',
    isFree: true,
    maxGenerations: 5,
    fields: [
      {
        label: 'Tell us about your business or product',
        name: 'keywords',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Easy-Peasy.AI is an AI copywriting tool that can help you with a variety of writing tasks, from creating better resumes and job descriptions to composing emails and social media content.',
      },
      {
        label: 'Tone (optional)',
        name: 'tone',
        type: 'text',
        placeholder: 'example: catchy, energetic, clever',
      },
    ],
  },
  {
    title: 'Motto Generator',
    slug: 'motto-generator',
    description:
      'Generate a catchy and meaningful motto for your business or product!',
    icon: RiPriceTagLine,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    category: 'Marketing',
    isFree: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Tell us about your business or product',
        name: 'keywords',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Easy-Peasy.AI is an AI copywriting tool that can help you with a variety of writing tasks, from creating better resumes and job descriptions to composing emails and social media content.',
      },
      {
        label: 'Tone (optional)',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: inspirational, motivational, catchy',
      },
    ],
  },
  {
    title: 'Amazon Product Description (paragraph)',
    slug: 'amazon-product-description-paragraph',
    description: 'Create Amazon product descriptions that sell!',
    icon: FaAmazon,
    onlyForPaidPlans: true,
    iconForeground: 'text-orange-700',
    iconBackground: 'bg-orange-50',
    // component: GenericComponent,
    category: 'Business',
    isFree: true,
    shouldUseChatGPTAPI: true,
    fields: [
      {
        label: 'Product Name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Aveeno',
      },
      {
        label: 'Key benefits/features',
        name: 'extra1',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: A baby soap that is gentle and effective. It’s formulated with natural oat ingredients. It hydrates baby’s skin. It’s hypoallergenic and formulated with clean ingredients. It isn’t tear-free, so it can sting the eyes.',
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: witty, professional',
      },
    ],
  },
  {
    title: 'Amazon Product Features (bullets)',
    slug: 'amazon-product-bullets',
    description:
      'Create key feature and benefit bullet points for Amazon listings under the "about this item" section.',
    icon: FaAmazon,
    iconForeground: 'text-orange-700',
    iconBackground: 'bg-orange-50',
    // component: GenericComponent,
    onlyForPaidPlans: true,
    isFree: true,
    category: 'Business',
    shouldUseChatGPTAPI: true,
    fields: [
      {
        label: 'Product Name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Aveeno',
      },
      {
        label: 'Key benefits/features',
        name: 'extra1',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: A baby soap that is gentle and effective. It’s formulated with natural oat ingredients. It hydrates baby’s skin. It’s hypoallergenic and formulated with clean ingredients. It isn’t tear-free, so it can sting the eyes.',
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: witty, professional',
      },
    ],
  },
  {
    title: 'Etsy Product Description Generator',
    slug: 'etsy-product-description',
    description:
      'Generate compelling product descriptions for your Etsy listings.',
    icon: FaEtsy,
    iconForeground: 'text-orange-700',
    iconBackground: 'bg-orange-50',
    category: 'Business',
    shouldUseChatGPTAPI: true,
    onlyForPaidPlans: true,
    isFree: true,
    fields: [
      {
        label: 'Product Name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Handmade Leather Wallet',
      },
      {
        label: 'Key benefits/features',
        name: 'extra1',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Made from high-quality leather. Compact design with multiple compartments. Perfect for everyday use.',
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: friendly, professional',
      },
    ],
  },
  {
    title: 'Resume Objective Generator',
    slug: 'resume-objective-generator',
    description: 'Create your professional resume objective in seconds',
    icon: ImInfo,
    iconForeground: 'text-pink-700',
    iconBackground: 'bg-pink-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    isFree: true,
    onlyForPaidPlans: true,
    category: 'Career',
    fields: [
      {
        label: 'Job Title',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Customer Service Agent',
      },
      {
        label: 'Key skills (optional)',
        name: 'extra1',
        type: 'text',
        placeholder:
          'example: trained in conflict resolution and communication',
      },
      {
        label: 'Professional goals (optional)',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: increase sales and customer loyalty',
      },
    ],
  },
  {
    title: 'Resume Summary Generator',
    slug: 'resume-summary-generator',
    description: 'Create your professional resume summary in seconds',
    icon: ImInfo,
    iconForeground: 'text-pink-700',
    iconBackground: 'bg-pink-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    category: 'Career',
    isFree: true,
    maxGenerations: 5,
    fields: [
      {
        label: 'Job Title',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Senior Software Engineer',
      },
      {
        label: 'Years of Experience',
        name: 'extra3',
        type: 'text',
        placeholder: 'example: 5',
      },
      {
        label: 'Key skills',
        name: 'extra1',
        type: 'text',
        placeholder:
          'example: Javascript, React, Node.js, HTML, CSS, Tailwind CSS, Agile Methodology, SEO, Google Analytics',
      },
      {
        label: 'Career Highlights',
        name: 'extra2',
        type: 'textarea',
        placeholder:
          'example: - Led a team of developers to successfully deliver a major project\n- Implemented a new feature that increased user engagement by 20%',
      },
    ],
  },
  {
    title: 'Resume Bullet Point Generator',
    slug: 'resume-bullet-point-generator',
    description:
      'Generate professional bullet points for your resume in seconds',
    icon: PencilIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    category: 'Career',
    isFree: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Job Title',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Software Engineer',
      },
      {
        label: 'Key Responsibilities',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Developed and maintained software applications',
      },
      {
        label: 'Key Achievements',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: Increased application performance by 20%',
      },
    ],
  },
  {
    title: 'Resume Skills Generator',
    slug: 'resume-skills-generator',
    description: 'Generate a list of skills for your resume',
    icon: PencilIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    category: 'Career',
    isFree: true,
    maxGenerations: 5,
    fields: [
      {
        label: 'Job Title',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Software Engineer',
      },
      {
        label: 'Key skills (optional)',
        name: 'extra1',
        type: 'textarea',
        placeholder: 'example: Javascript, React, Node.js',
      },
    ],
  },
  {
    title: 'Job Title Generator',
    slug: 'job-title-generator',
    description: 'Generate professional job titles in seconds',
    icon: PencilIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    category: ['Business', 'HR'],
    isFree: true,
    maxGenerations: 5,
    fields: [
      {
        label: 'Industry',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Software Development',
      },
      {
        label: 'Responsibilities',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Manage a team of developers',
      },
      {
        label: 'Tone (optional)',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: witty, professional',
      },
    ],
  },
  {
    title: 'Job Description Generator',
    slug: 'job-description-generator',
    description: 'Quickly generate job descriptions for any position',
    icon: PencilIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    // component: GenericComponent,
    category: ['Business', 'HR'],
    shouldUseChatGPTAPI: true,
    isFree: true,
    shouldUseGPT4o: true,
    maxGenerations: 5,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Job Title',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Customer Service Agent',
      },
      {
        label: 'Key skills (optional)',
        name: 'extra1',
        type: 'text',
        placeholder:
          'example: trained in conflict resolution and communication',
      },
      {
        label: 'Company Overview (optional)',
        name: 'extra2',
        type: 'textarea',
        placeholder: 'Brief description of your company and its culture',
      },
      {
        label: 'Additional Details (Optional)',
        name: 'extra3',
        type: 'textarea',
        placeholder:
          'Any other important information like benefits, work environment, or special requirements',
      },
    ],
  },
  {
    title: 'Job Summary Generator',
    slug: 'job-summary-generator',
    description: 'Get a strong, attention-grabbing summary',
    icon: FaPaperclip,
    iconForeground: 'text-amber-700',
    iconBackground: 'bg-amber-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    isFree: true,
    onlyForPaidPlans: true,
    category: ['Business', 'HR'],
    fields: [
      {
        label: 'Job Title',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Customer Service Agent',
      },
      {
        label: 'Company overview and expectations for the position (optional)',
        name: 'extra1',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'What makes your company unique. Include details about your company culture',
      },
    ],
  },
  {
    title: 'Job Qualifications Generator',
    slug: 'job-qualifications-generator',
    description: 'Quickly generate job qualifications for any job posting',
    icon: FaListOl,
    iconForeground: 'text-emerald-700',
    iconBackground: 'bg-emerald-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    isFree: true,
    maxGenerations: 5,
    category: ['Business', 'HR'],
    fields: [
      {
        label: 'Job Title',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Customer Service Agent',
      },
      {
        label: 'Requirements (optional)',
        name: 'extra1',
        type: 'text',
        placeholder:
          'educational, work Experience, soft skills, languages spoken, particular knowledge',
      },
    ],
  },
  {
    title: 'Job Responsibilities Generator',
    slug: 'job-responsibilities-generator',
    description:
      'Quickly generate job responsibilities for any job description',
    icon: FaToolbox,
    iconForeground: 'text-orange-700',
    iconBackground: 'bg-orange-50',
    // component: GenericComponent,
    category: ['Business', 'HR'],
    shouldUseChatGPTAPI: true,
    isFree: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Job Title',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Customer Service Agent',
      },
      {
        label: 'Responsibilities and Duties (optional)',
        name: 'extra1',
        type: 'text',
        placeholder:
          'Specify core responsibilities, day-to-day activities, how the position fits into the organization',
      },
    ],
  },
  {
    title: 'Policy Generator',
    slug: 'policy-generator',
    description:
      'Generate professional policies and guidelines for your organization',
    icon: InformationCircleIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    category: ['Business', 'HR'],
    shouldUseChatGPTAPI: true,
    isFree: true,
    onlyForPaidPlans: true,
    shouldUseO3Mini: true,
    fields: [
      {
        label: 'Type of Policy',
        name: 'keywords',
        type: 'text',
        placeholder:
          'example: Remote Work Policy, Social Media Policy, Code of Conduct',
      },
      {
        label: 'Key Points to Include (optional)',
        name: 'extra1',
        type: 'textarea',
        limit: 20000,
        placeholder:
          'example: working hours, communication tools, security requirements',
      },
      {
        label: 'Company Details (optional)',
        name: 'extra2',
        type: 'textarea',
        limit: 20000,
        placeholder: 'example: company name, industry, size, values',
      },
    ],
  },
  {
    title: 'Interview Questions Generator',
    slug: 'interview-questions-generator',
    description: 'Make your next interview preparation a breeze',
    icon: FaSuitcase,
    iconForeground: 'text-orange-700',
    iconBackground: 'bg-orange-50',
    // component: GenericComponent,
    category: ['Business', 'HR'],
    shouldUseChatGPTAPI: true,
    maxGenerations: 2,
    onlyForPaidPlans: true,
    isFree: true,
    fields: [
      {
        label: 'Job Title',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Customer Service Agent',
      },
    ],
  },
  {
    title: 'Interview Answers Generator',
    slug: 'interview-answers-generator',
    description: 'Generate answers for common interview questions',
    icon: FaSuitcase,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    // component: GenericComponent,
    category: ['Career', 'HR'],
    shouldUseChatGPTAPI: true,
    isFree: true,
    onlyForPaidPlans: true,
    // shouldUseO3Mini: true,
    fields: [
      {
        label: 'Interview Question',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: How do you handle customer complaints?',
      },
      {
        label: 'Job Title',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Customer Service Agent',
      },
      {
        label: 'What include in the answer (optional)',
        name: 'extra2',
        type: 'textarea',
        limit: 10000,
        placeholder:
          'example: I listen carefully to the customer and try to understand their problem. Then I try to find a solution that satisfies both the customer and the company.',
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'example: friendly, funny, cheerful',
        initialValue: 'Short',
        radioOptions: [
          { name: 'Short', checked: true },
          { name: 'Medium', checked: false },
          { name: 'Long', checked: false },
        ],
      },
    ],
  },
  {
    title: 'Interview Feedback Generator',
    slug: 'interview-feedback-generator',
    description: 'Write Interview Feedback With Ease and Confidence',
    icon: RiFeedbackFill,
    iconForeground: 'text-blue-700',
    isFree: true,
    iconBackground: 'bg-blue-50',
    // component: GenericComponent,
    category: ['Business', 'HR'],
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Job Title',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Lead iOS Developer',
      },
      {
        label: 'Feedback Notes',
        name: 'extra1',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: 8 years of experience. Knows design patterns, SOLID principles. Experience with memory management and multithreading, SwiftUI, CI/CD pipelines.',
      },
    ],
  },
  {
    title: 'Job Offer Letter Generator',
    slug: 'job-offer-letter-generator',
    description: 'Generate a professional job offer letter in seconds',
    icon: MailIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    // component: GenericComponent,
    category: ['Business', 'HR'],
    shouldUseChatGPTAPI: true,
    isFree: true,
    onlyForPaidPlans: true,
    maxGenerations: 5,
    fields: [
      {
        label: "Candidate's Name",
        name: 'keywords',
        type: 'text',
        placeholder: 'example: John Doe',
      },
      {
        label: 'Position Title',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Senior Software Engineer',
      },
      {
        label: 'Responsibilities',
        name: 'extra2',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Managing a team, developing a product, scale the infrastructure, etc.',
      },
      {
        label: 'Compensation Package',
        name: 'extra3',
        type: 'text',
        placeholder: 'example: $160,000 salary, 1% equity, etc.',
      },
      {
        label: 'Benefits',
        name: 'extra4',
        type: 'text',
        placeholder:
          'example: Health insurance, 401k matching, gym membership, etc.',
      },
    ],
  },
  {
    title: 'Employee Growth Plan Generator',
    slug: 'employee-growth-plan-generator',
    description:
      'Create a structured development plan to help employees reach their full potential',
    icon: ChartBarIcon,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    category: ['Business', 'HR'],
    shouldUseChatGPTAPI: true,
    isFree: true,
    maxGenerations: 5,
    fields: [
      {
        label: "Employee's Name and Current Role",
        name: 'keywords',
        type: 'text',
        placeholder: 'example: John Doe - Junior Software Developer',
      },
      {
        label: 'Current Skills and Strengths',
        name: 'extra1',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Strong in JavaScript, Good team player, Quick learner',
      },
      {
        label: 'Areas for Improvement',
        name: 'extra2',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Leadership skills, Public speaking, Advanced React concepts',
      },
      {
        label: 'Career Goals',
        name: 'extra3',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Become a Senior Developer in 2 years, Lead a team of developers',
      },
      {
        label: 'Timeline',
        name: 'extra4',
        type: 'text',
        placeholder: 'example: 12 months',
      },
    ],
  },
  {
    title: 'Team Building Activity Generator',
    slug: 'team-building-activity-generator',
    description:
      'Generate engaging team building activities to boost morale and collaboration',
    icon: UserGroupIcon,
    iconForeground: 'text-emerald-700',
    iconBackground: 'bg-emerald-50',
    category: ['Business', 'HR'],
    shouldUseChatGPTAPI: true,
    isFree: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Team Size',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: 15 people',
      },
      {
        label: 'Activity Setting',
        name: 'extra1',
        placeholder: 'example: In-person',
        type: 'radio',
        radioOptions: [
          { name: 'In-person', checked: true },
          { name: 'Virtual', checked: false },
          { name: 'Hybrid', checked: false },
        ],
      },
      {
        label: 'Duration',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: 2 hours',
      },
      {
        label: 'Team Characteristics (optional)',
        name: 'extra3',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Mix of introverts and extroverts, tech-savvy, different time zones',
      },
      {
        label: 'Activity Goals',
        name: 'extra4',
        type: 'text',
        placeholder:
          'example: improve communication, build trust, increase collaboration',
      },
    ],
  },
  {
    title: 'Employee Recognition Letter',
    slug: 'employee-recognition-letter',
    description:
      'Create meaningful recognition letters to celebrate employee achievements',
    icon: StarIcon,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    category: ['Business', 'HR'],
    shouldUseChatGPTAPI: true,
    isFree: true,
    maxGenerations: 5,
    fields: [
      {
        label: 'Employee Name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: John Smith',
      },
      {
        label: 'Achievement or Contribution',
        name: 'extra1',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Led the successful launch of our new product line, resulting in a 25% increase in quarterly revenue',
      },
      {
        label: 'Duration at Company',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: 3 years',
      },
      {
        label: 'Impact on Team/Company',
        name: 'extra3',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Improved team productivity, mentored junior employees, enhanced customer satisfaction',
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: sincere, professional, appreciative',
      },
    ],
  },
  {
    title: 'Performance Improvement Plan',
    slug: 'performance-improvement-plan',
    description:
      'Create a structured plan to help employees improve their performance',
    icon: ChartBarIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    category: ['Business', 'HR'],
    isFree: true,
    onlyForPaidPlans: true,
    maxGenerations: 2,
    fields: [
      {
        label: 'Employee Name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: John Smith',
      },
      {
        label: 'Performance Issues',
        name: 'extra1',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Missing deadlines, quality of work below expectations, communication issues with team members',
      },
      {
        label: 'Expected Performance Standards',
        name: 'extra2',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Complete projects on time, maintain quality standards, improve team collaboration',
      },
      {
        label: 'Timeline for Improvement',
        name: 'extra3',
        type: 'text',
        placeholder: 'example: 90 days',
      },
      {
        label: 'Support/Resources Provided',
        name: 'extra4',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Weekly check-ins with manager, training sessions, mentoring support',
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, constructive, supportive',
      },
    ],
  },
  {
    title: 'Blog Post Title',
    slug: 'blog-post-title',
    description: 'Generate catchy titles for your blog posts in seconds!',
    icon: RiArticleLine,
    iconForeground: 'text-orange-700',
    iconBackground: 'bg-orange-50',
    // component: GenericComponent,
    category: 'Content',
    shouldUseChatGPTAPI: true,
    isFree: true,
    showExamples: true,
    fields: [
      {
        label: 'Describe your blog topic',
        name: 'keywords',
        type: 'text',
        placeholder:
          'example: I visited the crypto event Token2049 in Singapore and met many VCs and entrepreneurs.',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, clever',
      },
    ],
  },
  {
    title: 'Blog Post Outline',
    slug: 'blog-post-outline',
    description: 'Because writing a blog post is easy when you have a plan!',
    icon: AcademicCapIcon,
    iconForeground: 'text-teal-700',
    iconBackground: 'bg-teal-50',
    // component: GenericComponent,
    category: 'Content',
    // shouldUseChatGPTAPI: true,
    isFree: true,
    fields: [
      {
        label: 'Blog post title/topic',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: How To Become A Software Engineer',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, clever',
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'example: friendly, funny, cheerful',
        initialValue: 'Medium',
        radioOptions: [
          { name: 'Short', checked: false },
          { name: 'Medium', checked: true },
          { name: 'Long', checked: false },
        ],
      },
      {
        label: 'Extra4',
        name: 'extra4',
        type: 'hidden',
        placeholder: 'example: professional, energetic, clever',
      },
    ],
  },
  {
    title: 'Blog Post Intro',
    slug: 'blog-post-intro',
    description: 'Get your readers hooked from the very beginning',
    icon: PlayIcon,
    iconForeground: 'text-rose-700',
    iconBackground: 'bg-rose-50',
    // component: GenericComponent,
    isFree: true,
    category: 'Content',
    shouldUseChatGPTAPI: true,
    fields: [
      {
        label: 'Blog post title/topic',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: How To Become A Software Engineer',
      },
      {
        label: 'What is the blog about?',
        name: 'extra1',
        type: 'textarea',
        limit: 2500,
        placeholder:
          'example: The skills required to become a software engineer. The education required to become a software engineer. The career opportunities for software engineers. The salary of a software engineer.',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, clever',
      },
    ],
  },
  {
    title: 'Marketing Campaign Generator',
    slug: 'marketing-campaign-generator',
    description:
      'Create customized marketing campaigns tailored to your target audience and goals.',
    icon: FaBullhorn,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    category: 'Marketing',
    dateAdded: '2024-06-30',
    isFree: true,
    shouldUseO3Mini: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Campaign Name',
        name: 'extra4',
        type: 'text',
        placeholder: 'e.g., Summer Sale',
        limit: 100,
      },
      {
        label: 'What is your target audience?',
        name: 'keywords',
        type: 'text',
        placeholder: 'e.g., Young Adults, Small Business Owners',
        limit: 100,
      },
      {
        label: 'Key Message',
        name: 'extra1',
        type: 'textarea',
        placeholder: 'e.g., Best deals of the season, Limited time offer',
        limit: 2000,
      },
      {
        label: 'Goals',
        name: 'extra2',
        type: 'textarea',
        placeholder: 'e.g., Increase sales, Boost brand awareness',
        limit: 2000,
      },
      {
        label: 'Preferred Channels',
        name: 'extra3',
        type: 'select',
        options: ['Email', 'Social Media', 'Search Engines', 'Print Media'],
        placeholder: 'Select preferred channels',
      },
    ],
  },
  {
    title: 'Marketing Email Generator',
    slug: 'marketing-email-generator',
    description:
      'Create compelling marketing emails tailored to your product and audience',
    icon: MailIcon,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    category: ['Email & Messages', 'Marketing'],
    dateAdded: '2024-07-01',
    isFree: true,
    maxGenerations: 5,
    fields: [
      {
        label: 'Product/Service Name',
        name: 'keywords',
        type: 'text',
        placeholder: 'e.g., Premium Fitness Tracker',
        limit: 100,
      },
      {
        label: 'Description',
        name: 'extra1',
        type: 'textarea',
        placeholder:
          'e.g., Advanced fitness tracker with heart rate monitoring and sleep analysis',
        limit: 300,
      },
      {
        label: 'Goal',
        name: 'extra2',
        type: 'text',
        placeholder: 'e.g., Increase product sales, Promote new features',
        limit: 100,
      },
      {
        label: 'Audience Demographic Info',
        name: 'extra3',
        type: 'textarea',
        placeholder:
          'e.g., Health-conscious adults, 25-45 years old, urban professionals',
        limit: 200,
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'text',
        placeholder: 'e.g., Enthusiastic, Informative, Persuasive',
        limit: 50,
      },
    ],
  },
  {
    title: 'Advertisement Copy Generator',
    slug: 'advertisement-copy-generator',
    description:
      'Create compelling advertisement copy for your products or services.',
    icon: FaBullhorn,
    iconForeground: 'text-teal-700',
    iconBackground: 'bg-teal-50',
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    category: 'Marketing',
    dateAdded: '2024-06-28',
    isFree: true,
    maxGenerations: 5,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Product or Service Name',
        name: 'keywords',
        type: 'text',
        placeholder: 'e.g., Eco-friendly Water Bottle',
        limit: 100,
      },
      {
        label: 'Key Features or Benefits',
        name: 'extra1',
        type: 'textarea',
        placeholder: 'e.g., Reusable, BPA-free, Keeps drinks cold for 24 hours',
        limit: 300,
      },
      {
        label: 'What is your target audience?',
        name: 'extra2',
        type: 'text',
        placeholder: 'e.g., Fitness Enthusiasts, Eco-conscious Consumers',
        limit: 100,
      },
      {
        label: 'Call to action',
        name: 'extra3',
        type: 'text',
        placeholder: 'e.g., Buy Now, Sign Up Today',
        limit: 50,
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'text',
        placeholder: 'e.g., Exciting, Professional, Friendly',
        limit: 50,
      },
    ],
  },
  {
    title: 'Blog Post Conclusion',
    slug: 'blog-post-conclusion',
    description: 'End your blog post with a bang!',
    icon: ImTrophy,
    iconForeground: 'text-fuchsia-700',
    iconBackground: 'bg-fuchsia-50',
    // component: GenericComponent,
    category: 'Content',
    isFree: true,
    maxGenerations: 5,
    shouldUseChatGPTAPI: true,
    fields: [
      {
        label: 'Blog post title/topic',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: How To Become A Software Engineer',
      },
      {
        label: 'What is the blog about?',
        name: 'extra1',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: The skills required to become a software engineer. The education required to become a software engineer. The career opportunities for software engineers. The salary of a software engineer.',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, clever',
      },
    ],
  },
  {
    title: 'Startup Ideas',
    slug: 'startup-ideas',
    description: 'Generate your next startup idea',
    icon: ImRocket,
    iconForeground: 'text-lime-700',
    iconBackground: 'bg-lime-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    isFree: true,
    maxGenerations: 5,
    category: 'Business',
    fields: [
      {
        label: 'Keywords',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: blockchain, ecommerce',
      },
    ],
  },
  {
    title: 'Elevator Pitch Generator',
    slug: 'elevator-pitch-generator',
    description:
      'Generate a compelling elevator pitch for your startup or business',
    icon: ImRocket,
    iconForeground: 'text-lime-700',
    iconBackground: 'bg-lime-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    category: 'Business',
    maxGenerations: 5,
    isFree: true,
    fields: [
      {
        label: 'What is your business about?',
        name: 'keywords',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: We are a fintech startup focusing on blockchain technology.',
      },
      {
        label: 'What makes your business unique?',
        name: 'extra1',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: We are the first to implement a decentralized payment system for ecommerce.',
      },
      {
        label: 'Who are your target customers?',
        name: 'extra2',
        type: 'text',
        limit: 1000,
        placeholder:
          'example: Online retailers seeking a secure and efficient payment system.',
      },
    ],
  },
  {
    title: 'User Story',
    slug: 'user-story',
    description:
      'Get your product features into the hands of users faster with agile user stories',
    icon: UserCircleIcon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    category: 'Business',
    onlyForPaidPlans: true,
    isFree: true,
    maxGenerations: 2,
    fields: [
      {
        label: 'Feature',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: data syncing with an Apple Watch',
        limit: 15000,
      },
      // images optional using files
      {
        label: 'Upload an image (optional)',
        name: 'extra1',
        type: 'files',
        placeholder: 'example: image.png',
        accept: 'image/*',
        acceptDescription: 'jpg, png, webp',
        maxFiles: 5,
        maxFileSize: 5,
      },
    ],
  },
  {
    title: 'Acceptance Criteria',
    slug: 'acceptance-criteria',
    description:
      'Quickly and easily generate acceptance criteria for your next project',
    icon: AcademicCapIcon,
    iconForeground: 'text-slate-700',
    iconBackground: 'bg-slate-50',
    // component: GenericComponent,
    category: 'Business',
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    onlyForPaidPlans: true,
    isFree: true,
    maxGenerations: 2,
    fields: [
      {
        label: 'Feature',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: data syncing with an Apple Watch',
        limit: 15000,
      },
      // images optional using files
      {
        label: 'Upload an image (optional)',
        name: 'extra1',
        type: 'files',
        placeholder: 'example: image.png',
        accept: 'image/*',
        acceptDescription: 'jpg, png, webp',
        maxFiles: 5,
        maxFileSize: 5,
      },
    ],
  },
  {
    title: 'JIRA Issue Creator',
    slug: 'jira-issue-creator',
    //agile project management software
    description: 'Create JIRA or any other issue tracker tickets in seconds',
    icon: FaJira,
    iconForeground: 'text-slate-700',
    iconBackground: 'bg-slate-50',
    // component: GenericComponent,
    category: ['Business', 'Code & Data'],
    shouldUseChatGPTAPI: true,
    isFree: true,
    onlyForPaidPlans: true,
    maxGenerations: 5,
    fields: [
      {
        label: 'Feature',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: data syncing with an Apple Watch',
        limit: 10000,
      },
      // images optional using files
      {
        label: 'Upload an image (optional)',
        name: 'extra1',
        type: 'files',
        placeholder: 'example: image.png',
        accept: 'image/*',
        acceptDescription: 'jpg, png, webp',
        maxFiles: 5,
        maxFileSize: 5,
      },
    ],
  },
  {
    title: 'Project Timeline Generator',
    slug: 'project-timeline-generator',
    description:
      'Effortlessly create accurate and efficient timelines that keep your team on the same page.',
    icon: FaListOl,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    onlyForPaidPlans: true,
    shouldUseGPT4o: true,
    category: 'Business',
    isFree: true,
    maxGenerations: 2,
    fields: [
      {
        label: 'Project Name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Website Redesign',
      },
      {
        label: 'Project Description',
        name: 'extra1',
        type: 'textarea',
        placeholder:
          'example: Redesign the company website for better user experience',
        limit: 10000,
      },
      {
        label: 'Key Milestones',
        name: 'extra2',
        type: 'textarea',
        placeholder: 'example: Design, Development, Testing, Launch',
        limit: 10000,
      },
    ],
  },
  {
    title: 'Project Report Generator',
    slug: 'project-report-generator',
    description: 'Generate comprehensive project reports with ease.',
    icon: DocumentTextIcon,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    shouldUseChatGPTAPI: true,
    onlyForPaidPlans: true,
    shouldUseGPT4o: true,
    category: 'Business',
    isFree: true,
    fields: [
      {
        label: 'Project Name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Project Alpha',
      },
      {
        label: 'Project Description',
        name: 'extra1',
        type: 'textarea',
        placeholder: 'example: A project to develop a new software product.',
        limit: 10000,
        enableMicrophone: true,
      },
      {
        label: 'Key Milestones',
        name: 'extra2',
        type: 'textarea',
        placeholder: 'example: Planning, Design, Development, Testing, Launch',
        limit: 10000,
        enableMicrophone: true,
      },
      {
        label: 'Project Outcome',
        name: 'extra3',
        type: 'textarea',
        placeholder:
          'example: The project was successful and the product is now live.',
        limit: 10000,
        enableMicrophone: true,
      },
    ],
  },
  {
    title: 'User Persona Generator',
    slug: 'user-persona-generator',
    description: 'Generate detailed user personas for your product or service.',
    icon: UserCircleIcon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    category: ['Business', 'Marketing'],
    isFree: true,
    dateAdded: '2024-06-30',
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Your business',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: digital marketing solutions',
        limit: 100,
      },
      {
        label: 'What is your target audience?',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: small business owners',
        limit: 200,
      },
      {
        label: "User's challenges (optional)",
        name: 'extra2',
        type: 'textarea',
        placeholder: 'example: Limited budget, Lack of time',
        limit: 2000,
      },
    ],
  },
  {
    title: 'Executive Summary Generator',
    slug: 'executive-summary-generator',
    description: 'Generate concise and effective executive summaries.',
    icon: DocumentTextIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    category: 'Business',
    shouldUseGPT4o: true,
    onlyForPaidPlans: true,
    shouldUseO3Mini: true,
    isFree: true,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: Project Alpha',
        limit: 2000,
      },
      {
        label: 'Problem that needs to be solved',
        name: 'extra1',
        type: 'textarea',
        placeholder: 'example: Improve user experience on the website.',
        limit: 2000,
      },
      {
        label: 'How problem will be solved',
        name: 'extra2',
        type: 'textarea',
        placeholder:
          'example: Redesign the website with a focus on user-friendly interfaces.',
        limit: 2000,
      },
      {
        label: 'Benefits of solving problem',
        name: 'extra3',
        type: 'textarea',
        placeholder:
          'example: Increased user engagement and customer satisfaction.',
        limit: 2000,
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, clever',
      },
    ],
  },
  {
    title: 'Call to Action Generator',
    slug: 'call-to-action-generator',
    description: 'Generate compelling CTAs in seconds',
    icon: FaBullhorn,
    iconForeground: 'text-red-700',
    iconBackground: 'bg-red-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    isFree: true,
    category: 'Marketing',
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'What is your product/service?',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: AI-powered writing tool',
      },
      {
        label: 'What is the main benefit of your product/service?',
        name: 'extra1',
        type: 'textarea',
        placeholder: 'example: Write better and faster',
      },
      {
        label: 'What action do you want users to take?',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: Sign up for a free trial',
      },
    ],
  },
  {
    title: 'AIDA Framework',
    slug: 'aida-framework',
    description: 'Attention, Interest, Desire, Action. Generate a sales copy!',
    icon: RiAdvertisementFill,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    category: 'Marketing',
    isFree: true,
    maxGenerations: 5,
    fields: [
      {
        label: 'Company/Product name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Easy-Peasy.AI',
      },
      {
        label: 'Product description',
        name: 'extra1',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Easy-Peasy.AI is an AI copywriting tool that can help you with a variety of writing tasks, from creating better resumes and job descriptions to composing emails and social media content.',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, clever',
      },
    ],
  },
  {
    title: 'SCQA Framework',
    slug: 'scqa-framework',
    description:
      'Situation, Complication, Question, Answer. Create structured, persuasive content.',
    icon: QuestionMarkCircleIcon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    // component: GenericComponent,
    category: 'Marketing',
    dateAdded: '2024-06-30',
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    isFree: true,
    fields: [
      {
        label: 'Company/Product name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Easy-Peasy.AI',
      },
      {
        label: 'Product/Service Description',
        name: 'extra1',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Easy-Peasy.AI is an AI copywriting tool that helps with various writing tasks, from resumes to social media content.',
      },
      {
        label: 'Target audience',
        name: 'extra2',
        type: 'text',
        placeholder:
          'example: Marketing professionals, content creators, small business owners',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, informative, engaging',
      },
    ],
  },
  {
    title: 'Before-After-Bridge Framework',
    slug: 'before-after-bridge-framework',
    description: 'Before, After, Bridge. Generate marketing copy in seconds!',
    icon: GiTowerBridge,
    iconForeground: 'text-stone-700',
    iconBackground: 'bg-stone-50',
    // component: GenericComponent,
    category: 'Marketing',
    shouldUseChatGPTAPI: true,
    isFree: true,
    fields: [
      {
        label: 'Company/Product name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Easy-Peasy.AI',
      },
      {
        label: 'Product description',
        name: 'extra1',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Easy-Peasy.AI is an AI copywriting tool that can help you with a variety of writing tasks, from creating better resumes and job descriptions to composing emails and social media content.',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, clever',
      },
    ],
  },
  {
    title: 'PAS Framework',
    slug: 'problem-agitate-solution-framework',
    description: 'Problem-Agitate-Solution. Write Persuasive Copy.',
    icon: GiArcheryTarget,
    iconForeground: 'text-sky-700',
    iconBackground: 'bg-sky-50',
    // component: GenericComponent,
    category: 'Marketing',
    isFree: true,
    shouldUseChatGPTAPI: true,
    fields: [
      {
        label: 'Company/Product name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Easy-Peasy.AI',
      },
      {
        label: 'Product description',
        name: 'extra1',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Easy-Peasy.AI is an AI copywriting tool that can help you with a variety of writing tasks, from creating better resumes and job descriptions to composing emails and social media content.',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, clever',
      },
    ],
  },
  {
    title: 'Universal Translator',
    slug: 'universal-translator',
    hideWebSearch: true,
    description: 'Accurately translate text between languages with AI',
    icon: TranslateIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    // component: GenericComponent,
    category: 'Writing',
    isFree: true,
    hideLanguageSelector: true,
    outputs: 1,
    shouldUseChatGPTAPI: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Text',
        name: 'keywords',
        type: 'textarea',
        limit: 8000,
        placeholder: 'example: Hello, how are you?',
      },
      {
        label: 'To Language',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: Spanish',
      },
    ],
  },
  {
    title: '🇸🇬Translate to Singlish',
    slug: 'translate-to-singlish',
    description: 'Get your message across in Singlish!',
    icon: TranslateIcon,
    iconForeground: 'text-red-700',
    iconBackground: 'bg-red-50',
    // component: GenericComponent,
    category: ['Writing', 'Personal & Fun'],
    isFree: true,
    maxGenerations: 5,
    shouldUseChatGPTAPI: true,
    fields: [
      {
        label: 'Text',
        name: 'keywords',
        type: 'textarea',
        limit: 1000,
        placeholder: 'example: I would like coffee with milk',
      },
    ],
  },
  {
    title: "Explain Like I'm Five",
    slug: 'eli5',
    description: 'Explain a complex or obscure topic in the simplest of terms',
    icon: QuestionMarkCircleIcon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    category: ['Education', 'Personal & Fun'],
    isFree: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'textarea',
        limit: 1000,
        placeholder: 'example: What is an API',
      },
    ],
  },
  {
    title: 'Business Name Generator',
    slug: 'business-name',
    description:
      'Generate business names that are both creative and professional!',
    icon: FaBusinessTime,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    isFree: true,
    // component: GenericComponent,
    category: 'Business',
    shouldUseChatGPTAPI: true,
    maxGenerations: 5,
    fields: [
      {
        label: 'What is your business about?',
        name: 'keywords',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: A startup that uses AI to help copywriters create human-like text.',
      },
    ],
  },
  {
    title: 'Unique Value Propositions',
    slug: 'unique-value-propositions',
    description:
      'Create a statement that clearly tells the benefit of your offer',
    icon: LightningBoltIcon,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    // component: GenericComponent,
    category: 'Business',
    shouldUseChatGPTAPI: true,
    isFree: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Product description',
        name: 'keywords',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Easy-Peasy.AI, AI Content and Copywriting assistant, transcribes audios, summarizes long content, generate images, has Chatbot, and more.',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: witty, professional, energetic',
      },
    ],
  },
  {
    title: 'Unique Selling Propositions',
    slug: 'unique-selling-propositions',
    description:
      'Create a statement that differentiates your product from your competitors',
    icon: CurrencyDollarIcon,
    iconForeground: 'text-lime-700',
    iconBackground: 'bg-lime-50',
    // component: GenericComponent,
    isFree: true,
    category: 'Business',
    shouldUseChatGPTAPI: true,
    maxGenerations: 5,
    fields: [
      {
        label: 'Product description',
        name: 'keywords',
        type: 'textarea',
        limit: 20000,
        placeholder:
          'example: Easy-Peasy.AI, AI Content and Copywriting assistant, transcribes audios, summarizes long content, generate images, has Chatbot, and more.',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: witty, professional, energetic',
      },
    ],
  },
  {
    title: 'Marketing Strategy Generator',
    slug: 'marketing-strategy-generator',
    description:
      'Generate a comprehensive marketing strategy for your business',
    icon: FaBullhorn,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    onlyForPaidPlans: true,
    shouldUseO3Mini: true,
    // component: GenericComponent,
    category: ['Business', 'Marketing'],
    shouldUseChatGPTAPI: true,
    isFree: true,
    fields: [
      {
        label: 'Business description',
        name: 'keywords',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Easy-Peasy.AI, AI Content and Copywriting assistant, transcribes audios, summarizes long content, generate images, has Chatbot, and more.',
      },
      {
        label: 'Target audience',
        name: 'extra1',
        type: 'text',
        limit: 1000,
        placeholder:
          'example: Small to medium-sized businesses looking for AI solutions',
      },
      {
        label: 'Marketing goals',
        name: 'extra2',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Increase brand awareness, generate leads, improve customer engagement',
      },
    ],
  },
  {
    title: 'Marketing Plan Generator',
    slug: 'marketing-plan-generator',
    description: 'Generate a comprehensive marketing plan for your business',
    icon: FaBullhorn,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    // component: GenericComponent,
    category: ['Business', 'Marketing'],
    shouldUseChatGPTAPI: true,
    onlyForPaidPlans: true,
    shouldUseO3Mini: true,
    isFree: true,
    fields: [
      {
        label: 'Business description',
        name: 'keywords',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Easy-Peasy.AI, AI Content and Copywriting assistant, transcribes audios, summarizes long content, generate images, has Chatbot, and more.',
      },
      {
        label: 'Target audience',
        name: 'extra1',
        type: 'text',
        limit: 1000,
        placeholder:
          'example: Small to medium-sized businesses looking for AI solutions',
      },
      {
        label: 'Marketing goals',
        name: 'extra2',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Increase brand awareness, generate leads, improve customer engagement',
      },
    ],
  },
  {
    title: 'Fill The Gaps',
    slug: 'fill-the-gaps',
    hideWebSearch: true,
    description:
      'Add contextually relevant text in the middle of existing content',
    icon: RiInsertRowBottom,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    // component: GenericComponent,
    category: 'Writing',
    fields: [
      {
        label: 'Start section',
        name: 'keywords',
        limit: 1000,
        type: 'textarea',
        placeholder: 'example: Hi Peter! Thank you for your offer',
      },
      {
        label: 'End section',
        name: 'suffix',
        limit: 1000,
        type: 'textarea',
        placeholder: 'example: Looking forward working together with you!',
      },
    ],
  },
  {
    title: 'Cornell Notes Generator',
    slug: 'cornell-notes-generator',
    description: 'Generate structured Cornell notes in seconds!',
    icon: FaFeather,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    category: 'Education',
    outputs: 1,
    shouldUseChatGPTAPI: true,
    shouldUseO3Mini: true,
    onlyForPaidPlans: true,
    isFree: true,
    fields: [
      {
        label: 'Title',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: Introduction to AI',
      },
    ],
  },
  {
    title: 'Chemistry Problem Solver',
    slug: 'chemistry-assistant',
    description: 'Assist with chemistry problems and provide solutions',
    icon: BeakerIcon,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    category: 'Education',
    shouldUseChatGPTAPI: true,
    shouldUseO3Mini: true,
    onlyForPaidPlans: true,
    outputs: 1,
    isFree: true,
    fields: [
      {
        label: 'Upload an image (optional)',
        name: 'extra1',
        type: 'file',
        placeholder: 'example: image.png',
        accept: 'image/*',
        acceptDescription: 'jpg, png, webp',
      },
      {
        label: 'Problem or question',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: Calculate the molar mass of H2O',
      },
    ],
  },
  {
    title: 'Math Solver',
    slug: 'math-solver',
    description:
      'Easily solve math problems step-by-step with detailed explanations. Upload a photo of your math question or type it in, and get instant, clear solutions. Perfect for students and anyone needing fast, accurate math help.',
    icon: CalculatorIcon,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    category: 'Education',
    shouldUseChatGPTAPI: true,
    shouldUseO3Mini: true,
    isFree: true,
    onlyForPaidPlans: true,
    outputs: 1,
    fields: [
      {
        label: 'Math Problem',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: Solve for x: 2x + 5 = 13',
        limit: 1000,
      },
      {
        label: 'Upload an image (optional)',
        name: 'extra1',
        type: 'file',
        placeholder: 'example: math_problem.png',
        accept: 'image/*',
        acceptDescription: 'jpg, png, webp',
      },
    ],
  },
  {
    title: 'Accounting Solver',
    slug: 'accounting-solver',
    description:
      'Solve accounting problems step-by-step with clear explanations. Upload a photo of your accounting question or type it in, and get instant, accurate solutions. Ideal for students and professionals needing help with accounting concepts, calculations, and journal entries.',
    icon: CalculatorIcon,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    category: 'Education',
    shouldUseChatGPTAPI: true,
    shouldUseO3Mini: true,
    isFree: false,
    outputs: 1,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Accounting Problem',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'example: Prepare a journal entry for the purchase of equipment costing $5,000 paid in cash.',
        limit: 5000,
      },
      {
        label: 'Upload an image (optional)',
        name: 'extra1',
        type: 'file',
        placeholder: 'example: accounting_problem.png',
        accept: 'image/*',
        acceptDescription: 'jpg, png, webp',
      },
    ],
  },
  {
    title: 'Economics Solver',
    slug: 'economics-solver',
    description:
      'Solve economics problems step-by-step with clear explanations. Upload a photo of your economics question or type it in, and get instant, accurate solutions. Ideal for students and professionals needing help with microeconomics, macroeconomics, graphs, and calculations.',
    icon: CalculatorIcon,
    iconForeground: 'text-emerald-700',
    iconBackground: 'bg-emerald-50',
    category: 'Education',
    shouldUseChatGPTAPI: true,
    shouldUseO3Mini: true,
    isFree: true,
    onlyForPaidPlans: true,
    outputs: 1,
    fields: [
      {
        label: 'Economics Problem',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'example: Explain the difference between demand-pull and cost-push inflation. Or: Calculate the equilibrium price given the following supply and demand equations.',
        limit: 5000,
      },
      {
        label: 'Upload an image (optional)',
        name: 'extra1',
        type: 'file',
        placeholder: 'example: economics_problem.png',
        accept: 'image/*',
        acceptDescription: 'jpg, png, webp',
      },
    ],
  },
  {
    title: 'Statistics Solver',
    slug: 'statistics-solver',
    description:
      'Solve statistics problems step-by-step with clear explanations. Upload a photo of your statistics question or type it in, and get instant, accurate solutions. Ideal for students and professionals needing help with statistics concepts, calculations, and data analysis.',
    icon: CalculatorIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    category: 'Education',
    shouldUseChatGPTAPI: true,
    shouldUseO3Mini: true,
    isFree: true,
    onlyForPaidPlans: true,
    outputs: 1,
    fields: [
      {
        label: 'Statistics Problem',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'example: Calculate the mean and standard deviation for the following data set: 5, 7, 8, 10, 12.',
        limit: 5000,
      },
      {
        label: 'Upload an image (optional)',
        name: 'extra1',
        type: 'file',
        placeholder: 'example: statistics_problem.png',
        accept: 'image/*',
        acceptDescription: 'jpg, png, webp',
      },
    ],
  },
  {
    title: 'Physics Solver',
    slug: 'physics-solver',
    description:
      'Solve physics problems step-by-step with clear explanations. Upload a photo of your physics question or type it in, and get instant, accurate solutions. Ideal for students and professionals working with mechanics, electricity, waves, and more.',
    icon: LightningBoltIcon,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    category: 'Education',
    shouldUseChatGPTAPI: true,
    shouldUseO3Mini: true,
    isFree: true,
    onlyForPaidPlans: true,
    outputs: 1,
    fields: [
      {
        label: 'Physics Problem',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'example: A 2 kg block slides down a 30° incline with friction coefficient 0.1. Find its acceleration.',
        limit: 5000,
      },
      {
        label: 'Upload an image (optional)',
        name: 'extra1',
        type: 'file',
        placeholder: 'example: physics_problem.png',
        accept: 'image/*',
        acceptDescription: 'jpg, png, webp',
      },
    ],
  },
  {
    title: 'Geometry Solver',
    slug: 'geometry-solver',
    description:
      'Solve geometry problems step-by-step with clear explanations. Upload a photo of your geometry question or type it in, and get instant, accurate solutions. Ideal for angles, triangles, circles, coordinate geometry, and proofs.',
    icon: CalculatorIcon,
    iconForeground: 'text-sky-700',
    iconBackground: 'bg-sky-50',
    category: 'Education',
    shouldUseChatGPTAPI: true,
    shouldUseO3Mini: true,
    isFree: true,
    onlyForPaidPlans: true,
    outputs: 1,
    fields: [
      {
        label: 'Geometry Problem',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'example: In a circle with radius 10, a chord is 12 units long. Find the distance from the center to the chord.',
        limit: 5000,
      },
      {
        label: 'Upload an image (optional)',
        name: 'extra1',
        type: 'file',
        placeholder: 'example: geometry_problem.png',
        accept: 'image/*',
        acceptDescription: 'jpg, png, webp',
      },
    ],
  },
  {
    title: 'History Solver',
    slug: 'history-solver',
    description:
      'Answer history questions with clear, structured explanations. Upload a photo of your history question or type it in, and get accurate answers with key dates, events, and context.',
    icon: BookOpenIcon,
    iconForeground: 'text-orange-700',
    iconBackground: 'bg-orange-50',
    category: 'Education',
    shouldUseChatGPTAPI: true,
    shouldUseO3Mini: true,
    isFree: true,
    onlyForPaidPlans: true,
    outputs: 1,
    fields: [
      {
        label: 'History Question',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'example: What were the main causes of the French Revolution?',
        limit: 5000,
      },
      {
        label: 'Upload an image (optional)',
        name: 'extra1',
        type: 'file',
        placeholder: 'example: history_question.png',
        accept: 'image/*',
        acceptDescription: 'jpg, png, webp',
      },
    ],
  },
  {
    title: 'Biology Solver',
    slug: 'biology-solver',
    description:
      'Solve biology questions with clear, step-by-step explanations. Upload a photo of your biology question or type it in, and get accurate answers across cell biology, genetics, physiology, and more.',
    icon: HeartIcon,
    iconForeground: 'text-rose-700',
    iconBackground: 'bg-rose-50',
    category: 'Education',
    shouldUseChatGPTAPI: true,
    shouldUseO3Mini: true,
    isFree: true,
    onlyForPaidPlans: true,
    outputs: 1,
    fields: [
      {
        label: 'Biology Question',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: Explain how meiosis leads to genetic variation.',
        limit: 5000,
      },
      {
        label: 'Upload an image (optional)',
        name: 'extra1',
        type: 'file',
        placeholder: 'example: biology_question.png',
        accept: 'image/*',
        acceptDescription: 'jpg, png, webp',
      },
    ],
  },
  {
    title: 'Hypothesis Maker',
    slug: 'hypothesis-maker',
    description:
      'Generate a hypothesis for your research or project in seconds!',
    icon: LightBulbIcon,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    category: 'Education',
    shouldUseChatGPTAPI: true,
    shouldUseO3Mini: true,
    isFree: true,
    onlyForPaidPlans: true,
    maxGenerations: 2,
    fields: [
      {
        label: 'Research Topic',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: The effect of light on plant growth',
      },
    ],
  },
  {
    title: 'Thesis Statement Generator',
    slug: 'thesis-statement-generator',
    description: 'Generate a strong thesis statement for your paper',
    icon: LightBulbIcon,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    category: 'Education',
    shouldUseChatGPTAPI: true,
    isFree: true,
    onlyForPaidPlans: true,
    maxGenerations: 2,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: The impact of technology on education',
      },
    ],
  },
  {
    title: 'Research Problem Statement Generator',
    slug: 'research-problem-statement-generator',
    description: 'Generate a concise problem statement for your research',
    icon: LightBulbIcon,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    category: 'Education',
    isFree: true,
    shouldUseO3Mini: true,
    onlyForPaidPlans: true,
    shouldUseChatGPTAPI: true,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: The impact of climate change on agriculture',
      },
    ],
  },
  {
    title: 'Literature Review Generator',
    slug: 'literature-review-generator',
    description:
      'Generate a comprehensive literature review based on your research topic',
    icon: BookOpenIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    category: 'Education',
    isFree: false,
    shouldUseChatGPTAPI: true,
    outputs: 1,
    onlyForPaidPlans: true,
    useTool: true,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: The impact of social media on mental health',
      },
      {
        label: 'Relevant Sources (optional)',
        name: 'extra1',
        type: 'textarea',
        placeholder:
          'List any specific sources you want to include, separated by commas',
      },
    ],
  },
  {
    title: 'Abstract Generator',
    slug: 'abstract-generator',
    description:
      'Generate a concise, professional academic abstract from your research context and highlights.',
    icon: AcademicCapIcon,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    category: 'Education',
    isFree: true,
    onlyForPaidPlans: true,
    showExamples: true,
    dateAdded: '2025-08-12',
    fields: [
      {
        label: 'Research summary or context',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'Brief overview of the work (field, topic, purpose). Example: We investigate transformer-based models for low-resource NER across 6 languages...',
        limit: 12000,
        enableMicrophone: true,
      },
      {
        label: 'Objective / Research question',
        name: 'extra1',
        type: 'textarea',
        placeholder:
          'What problem are you addressing or hypothesis being tested?',
        limit: 2000,
        enableMicrophone: true,
      },
      {
        label: 'Methods / Approach',
        name: 'extra2',
        type: 'textarea',
        placeholder:
          'Study design, datasets/samples, techniques, experiments, analyses.',
        limit: 3000,
        enableMicrophone: true,
      },
      {
        label: 'Key Results / Findings',
        name: 'extra3',
        type: 'textarea',
        placeholder:
          'Summarize the main quantitative/qualitative findings. Include metrics where possible.',
        limit: 3000,
        enableMicrophone: true,
      },
      {
        label: 'Conclusions / Implications',
        name: 'extra4',
        type: 'textarea',
        placeholder:
          'Main takeaways, contributions, significance, or practical implications.',
        limit: 3000,
        enableMicrophone: true,
      },
      {
        label: 'Style',
        name: 'tone',
        type: 'select',
        options: [
          'Formal Academic',
          'Concise',
          'Lay Summary',
          'Technical',
          'Persuasive (Grant)',
        ],
        placeholder: 'Select preferred abstract style',
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        initialValue: 'Medium',
        radioOptions: [
          { name: 'Short', checked: false },
          { name: 'Medium', checked: true },
          { name: 'Long', checked: false },
        ],
        placeholder: 'Short (~120–150w), Medium (~150–200w), Long (~200–250w)',
      },
    ],
  },
  {
    title: 'YouTube Video Title',
    slug: 'youtube-video-title',
    description: 'Generate catchy titles for your YouTube Videos in seconds!',
    icon: FaYoutube,
    iconForeground: 'text-red-700',
    iconBackground: 'bg-red-50',
    // component: GenericComponent,
    category: 'Social Media',
    isFree: true,
    shouldUseChatGPTAPI: true,
    supportsVideoAnalysis: true,
    fields: [
      {
        label: 'Upload a video (optional)',
        name: 'extra1',
        type: 'file',
        placeholder: 'Upload a video to generate a title from',
        accept: 'video/*',
        acceptDescription: 'mp4, mov, webm',
        description:
          'Upload a video and AI will analyze it to create a relevant title',
      },
      {
        label: 'What is your video about?',
        name: 'keywords',
        type: 'textarea',
        limit: 10000,
        placeholder:
          'example: I visited the crypto event Token2049 in Singapore and met many VCs and entrepreneurs.',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, clever',
      },
    ],
  },
  {
    title: 'YouTube Video Description',
    slug: 'youtube-video-description',
    description:
      'Make your videos stand out and rank well with great descriptions!',
    icon: FaYoutube,
    iconForeground: 'text-red-700',
    iconBackground: 'bg-red-50',
    shouldUseChatGPTAPI: true,
    isFree: true,
    // component: GenericComponent,
    category: 'Social Media',
    onlyForPaidPlans: true,
    supportsVideoAnalysis: true,
    fields: [
      {
        label: 'Upload a video (optional)',
        name: 'extra1',
        type: 'file',
        placeholder: 'Upload a video to generate a description from',
        accept: 'video/*',
        acceptDescription: 'mp4, mov, webm',
        description:
          'Upload a video and AI will analyze it to write a relevant description',
      },
      {
        label: 'What is your video about?',
        name: 'keywords',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: I visited the crypto event Token2049 in Singapore and met many VCs and entrepreneurs.',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, clever',
      },
    ],
  },
  {
    title: 'YouTube Video Ideas',
    slug: 'youtube-video-ideas',
    description: 'Get inspired to make your next YouTube video!',
    icon: FaYoutube,
    iconForeground: 'text-red-700',
    iconBackground: 'bg-red-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    category: 'Social Media',
    isFree: true,
    fields: [
      {
        label: 'What is your video about?',
        name: 'keywords',
        limit: 1000,
        type: 'textarea',
        placeholder:
          'example: I visited the crypto event Token2049 in Singapore and met many VCs and entrepreneurs.',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, clever',
      },
    ],
  },
  {
    title: 'Video Script Outline',
    slug: 'youtube-video-script-outline',
    description:
      'Make your next video a hit with a professional script outline!',
    icon: FaYoutube,
    iconForeground: 'text-red-700',
    iconBackground: 'bg-red-50',
    // component: GenericComponent,
    category: 'Social Media',
    isFree: true,
    shouldUseChatGPTAPI: true,
    supportsVideoAnalysis: true,
    fields: [
      {
        label: 'Upload a video (optional)',
        name: 'extra1',
        type: 'file',
        placeholder: 'Upload a video to refine the script',
        accept: 'video/*',
        acceptDescription: 'mp4, mov, webm',
        description:
          'Upload a video and AI will analyze it to create a relevant script outline',
      },
      {
        label: 'What is your video about?',
        name: 'keywords',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: I visited the crypto event Token2049 in Singapore and met many VCs and entrepreneurs.',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, clever',
      },
    ],
  },
  {
    title: 'TikTok Video Captions',
    slug: 'tiktok-video-caption',
    description: 'Get more likes and views on your TikTok videos!',
    icon: FaTiktok,
    iconForeground: 'text-gray-700',
    iconBackground: 'bg-gray-50',
    // component: GenericComponent,
    category: 'Social Media',
    isFree: true,
    shouldUseChatGPTAPI: true,
    supportsVideoAnalysis: true,
    fields: [
      {
        label: 'Upload a video (optional)',
        name: 'extra1',
        type: 'file',
        placeholder: 'Upload a video to generate a caption from',
        accept: 'video/*',
        acceptDescription: 'mp4, mov, webm',
        description:
          'Upload a video and AI will analyze it to create a relevant caption',
      },
      {
        label: 'What is your video about?',
        name: 'keywords',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: I visited the crypto event Token2049 in Singapore and met many VCs and entrepreneurs.',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, clever',
      },
    ],
  },
  {
    title: 'TikTok Hashtags Generator',
    slug: 'tiktok-hashtags-generator',
    description: 'Get more TikTok followers and likes',
    icon: FaTiktok,
    iconForeground: 'text-gray-700',
    iconBackground: 'bg-gray-50',
    // component: GenericComponent,
    category: 'Social Media',
    isFree: true,
    shouldUseChatGPTAPI: true,
    fields: [
      {
        label: 'What is your video about?',
        name: 'keywords',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: I visited the crypto event Token2049 in Singapore and met many VCs and entrepreneurs.',
      },
    ],
  },
  {
    title: 'Pinterest Hashtag Generator',
    slug: 'pinterest-hashtag-generator',
    description: 'Generate popular hashtags for your Pinterest posts!',
    icon: FaPinterest,
    iconForeground: 'text-red-700',
    iconBackground: 'bg-red-50',
    // component: GenericComponent,
    category: 'Social Media',
    isFree: true,
    shouldUseChatGPTAPI: true,
    fields: [
      {
        label: 'What is your post about?',
        name: 'keywords',
        type: 'textarea',
        limit: 1000,
        placeholder: 'example: I am posting about DIY home decor ideas.',
      },
    ],
  },
  {
    title: 'Essay Title Generator',
    slug: 'essay-title-generator',
    description: 'Generate catchy and relevant titles for your essays',
    icon: PencilAltIcon,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    category: ['Content', 'Education'],
    shouldUseChatGPTAPI: true,
    isFree: true,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: The impact of social media on modern communication',
      },
      {
        label: 'Academic level',
        name: 'extra1',
        type: 'select',
        options: ['High School', 'Undergraduate', 'Graduate', 'Doctoral'],
        placeholder: 'Select academic level',
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: academic, thought-provoking, engaging',
      },
    ],
  },
  {
    title: 'Essay Outline',
    slug: 'essay-outline',
    description: 'Get an essay outline in seconds!',
    icon: AcademicCapIcon,
    iconForeground: 'text-teal-700',
    iconBackground: 'bg-teal-50',
    // component: GenericComponent,
    category: ['Content', 'Education'],
    shouldUseChatGPTAPI: true,
    isFree: true,
    fields: [
      {
        label: 'Essay topic',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: How to make a million dollars in 5 years',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: academic, professional, casual',
      },
      // {
      //   label: 'Output length',
      //   name: 'length',
      //   type: 'radio',
      //   placeholder: 'example: friendly, funny, cheerful',
      //   radioOptions: [
      //     { name: 'Short', checked: false },
      //     { name: 'Medium', checked: true },
      //     { name: 'Long', checked: false },
      //   ],
      // },
    ],
  },
  {
    title: 'Essay Intro',
    slug: 'essay-intro',
    description: 'Get an essay introduction in no time!',
    icon: AcademicCapIcon,
    isFree: true,
    iconForeground: 'text-teal-700',
    iconBackground: 'bg-teal-50',
    // component: GenericComponent,
    category: ['Content', 'Education'],
    shouldUseChatGPTAPI: true,
    fields: [
      {
        label: 'Essay topic',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: The importance of education',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: academic, professional, casual',
      },
    ],
  },
  {
    title: 'Essay Extender',
    slug: 'essay-extender',
    description: 'Extend your essay with relevant content!',
    icon: AcademicCapIcon,
    iconForeground: 'text-teal-700',
    iconBackground: 'bg-teal-50',
    // component: GenericComponent,
    category: ['Content', 'Education'],
    shouldUseChatGPTAPI: true,
    isFree: true,
    onlyForPaidPlans: true,
    maxGenerations: 2,
    fields: [
      {
        label: 'Essay content',
        name: 'keywords',
        type: 'textarea',
        limit: 1500,
        placeholder: 'example: Insert your essay content here',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: academic, professional, casual',
      },
    ],
  },
  {
    title: 'Essay Generator with References',
    slug: 'essay-generator-with-references',
    description:
      'Generate well-structured academic essays with proper citations and references!',
    icon: DocumentTextIcon,
    iconForeground: 'text-teal-700',
    iconBackground: 'bg-teal-50',
    category: ['Content', 'Education'],
    shouldUseChatGPTAPI: true,
    isFree: true,
    outputs: 1,
    useTool: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Essay topic',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'example: The impact of artificial intelligence on modern society',
      },
      {
        label: 'Citation Format',
        name: 'extra2',
        type: 'radio',
        radioOptions: [
          { name: 'APA', checked: true },
          { name: 'Chicago', checked: false },
          { name: 'MLA', checked: false },
          { name: 'Harvard', checked: false },
          { name: 'Vancouver', checked: false },
        ],
        placeholder: 'Select your citation format',
      },
      {
        label: 'Additional requirements (optional)',
        name: 'extra3',
        type: 'textarea',
        placeholder: 'example: Include counter-arguments, cite recent research',
      },
      {
        label: 'Length',
        name: 'length',
        type: 'radio',
        radioOptions: [
          { name: 'Short', checked: false },
          { name: 'Medium', checked: true },
          { name: 'Long', checked: false },
        ],
        placeholder: 'Select essay length',
      },
      {
        label: 'Writing style',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: standard, argumentative, academic',
      },
    ],
  },
  {
    title: 'Research Paper Generator',
    slug: 'research-paper-generator',
    description:
      'Generate comprehensive research papers with proper structure and citations',
    icon: DocumentTextIcon,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    category: ['Education', 'Content'],
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    onlyForPaidPlans: true,
    isFree: true,
    outputs: 1,
    useTool: true,
    fields: [
      {
        label: 'Research Topic',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'example: The effects of climate change on marine ecosystems',
        limit: 1000,
      },
      {
        label: 'Academic Field',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Environmental Science, Psychology, Economics',
      },
      {
        label: 'Citation Format',
        name: 'extra2',
        type: 'radio',
        radioOptions: [
          { name: 'APA', checked: true },
          { name: 'MLA', checked: false },
          { name: 'Chicago', checked: false },
          { name: 'Harvard', checked: false },
          { name: 'IEEE', checked: false },
        ],
        placeholder: 'Select your citation format',
      },
      {
        label: 'Specific Requirements (optional)',
        name: 'extra3',
        type: 'textarea',
        placeholder:
          'example: Include methodology section, focus on recent studies from 2018-2023',
        limit: 1000,
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        radioOptions: [
          { name: 'Short', checked: false },
          { name: 'Medium', checked: true },
          { name: 'Long', checked: false },
        ],
        placeholder: 'Select paper length',
      },
      {
        label: 'Academic Style',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: formal, analytical, objective',
      },
    ],
  },
  {
    title: 'Question Answerer',
    slug: 'quora-answers',
    description: 'Get quick and reliable answers to any questions',
    icon: FaQuora,
    iconForeground: 'text-red-700',
    iconBackground: 'bg-red-50',
    // component: GenericComponent,
    category: 'Social Media',
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    isFree: true,
    onlyForPaidPlans: true,
    showExamples: true,
    maxGenerations: 3,
    fields: [
      {
        label: 'What is your question?',
        name: 'keywords',
        type: 'textarea',
        limit: 1000,
        placeholder: 'example: How to become a UX designer?',
      },
      {
        label: 'What include in the answer (optional)',
        name: 'extra1',
        type: 'text',
        placeholder:
          'example: become professional with Figma, read Steve Krug books',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, clever',
      },
    ],
  },
  {
    title: 'About Me Generator',
    slug: 'about-me-generator',
    description:
      'As a creative professional, your bio is one of the first things potential clients will see.',
    icon: FaPersonBooth,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    // component: GenericComponent,
    category: ['Social Media', 'Career'],
    shouldUseGPT4o: true,
    shouldUseChatGPTAPI: true,
    isFree: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Personal Information',
        name: 'keywords',
        limit: 1000,
        type: 'textarea',
        placeholder:
          'example: Born and raised in small town in upstate New York. Writer and editor with over 10 years of experience.',
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'example: friendly, funny, cheerful',
        radioOptions: [
          { name: 'Short', checked: false },
          { name: 'Long', checked: true },
        ],
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, clever',
      },
    ],
  },
  {
    title: 'Professional Bio Generator',
    slug: 'professional-bio-generator',
    description:
      'Generate a professional bio based on your personal and professional information.',
    icon: UserIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    // component: GenericComponent,
    shouldUseGPT4o: true,
    onlyForPaidPlans: true,
    category: ['Career', 'Social Media'],
    shouldUseChatGPTAPI: true,
    shouldUseO3Mini: true,
    isFree: true,
    fields: [
      {
        label: 'Personal Information',
        name: 'keywords',
        limit: 10000,
        type: 'textarea',
        placeholder:
          'example: John Doe, software engineer, 5 years of experience, Python, Java, Agile',
        description:
          'Just list the facts — name, role, years, skills, achievements. Pasting your resume or LinkedIn summary works great. We’ll do the writing.',
        enableMicrophone: true,
      },
      {
        label: 'Point of View',
        name: 'extra1',
        type: 'select',
        placeholder: 'example: Positive',
        options: [
          'First Person Singular (I, me, my, mine)',
          'First Person Plural (we, us, our, ours)',
          'Second Person (you, your, yours)',
          'Third Person (he, she, it, they)',
        ],
        initialValue: 'First Person Singular (I, me, my, mine)',
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'example: short, medium, long',
        radioOptions: [
          { name: 'Short', checked: false },
          { name: 'Medium', checked: true },
          { name: 'Long', checked: false },
        ],
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, clever',
      },
      {
        label: 'Additional Instructions (optional)',
        name: 'extra2',
        type: 'textarea',
        placeholder:
          'example: Emphasize leadership experience, include a call-to-action for consulting inquiries',
      },
    ],
  },
  {
    title: 'Google Ads Headlines',
    slug: 'google-ads-headlines',
    description: 'Get more clicks with our Google Ads headlines generator!',
    icon: FaGoogle,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    // component: GenericComponent,
    category: ['Marketing', 'Business'],
    shouldUseChatGPTAPI: true,
    isFree: true,
    maxGenerations: 5,
    fields: [
      {
        label: 'Product description',
        name: 'keywords',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Easy-Peasy.AI is a powerful AI copywriting tool that can help you write better resumes, job descriptions, user stories, acceptance criteria, emails, and social media content.',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, clever',
      },
    ],
  },
  {
    title: 'Google Ads Descriptions',
    slug: 'google-ads-descriptions',
    description:
      'Get more conversions with our Google Ads Descriptions generator!',
    icon: FaGoogle,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    // component: GenericComponent,
    category: ['Marketing', 'Business'],
    shouldUseChatGPTAPI: true,
    isFree: true,
    maxGenerations: 5,
    fields: [
      {
        label: 'Product description',
        name: 'keywords',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Easy-Peasy.AI is a powerful AI copywriting tool that can help you write better resumes, job descriptions, user stories, acceptance criteria, emails, and social media content.',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, clever',
      },
    ],
  },
  {
    title: 'Google My Business - Product Description',
    slug: 'google-my-business-product-description',
    description:
      'Generate product or service descriptions for your Google My Business',
    icon: FaGoogle,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    isFree: true,
    maxGenerations: 5,
    // component: GenericComponent,
    category: ['Marketing', 'Business'],
    shouldUseChatGPTAPI: true,
    fields: [
      {
        label: 'Company/Product name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Easy-Peasy.AI',
      },
      {
        label: 'Product description',
        name: 'extra1',
        type: 'textarea',
        limit: 5000,
        placeholder:
          'example: Easy-Peasy.AI is an AI copywriting tool that can help you with a variety of writing tasks, from creating better resumes and job descriptions to composing emails and social media content.',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, clever',
      },
    ],
  },
  {
    title: 'Google My Business Post',
    slug: 'google-my-business-post',
    description: "Generate What's New post updates for Google My Business",
    icon: FaGoogle,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    // component: GenericComponent,
    category: ['Marketing', 'Business'],
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    onlyForPaidPlans: true,
    isFree: true,
    fields: [
      {
        label: 'Tell us about your business update',
        name: 'keywords',
        type: 'textarea',
        limit: 500,
        placeholder:
          'example: We now offer 35 homemade, hand-churned ice creams. We opened second location in downtown.',
      },
      {
        label: 'Company/Product name',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Ice Cream Crazy',
      },

      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, clever',
      },
    ],
  },
  {
    title: 'Facebook Ad Headlines',
    slug: 'facebook-ads-headlines',
    description:
      'Create attention-grabbing Facebook Headlines that get results!',
    icon: FaFacebook,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    isFree: true,
    // component: GenericComponent,
    category: ['Marketing', 'Business'],
    shouldUseChatGPTAPI: true,
    maxGenerations: 5,
    fields: [
      {
        label: 'Product description',
        name: 'keywords',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Easy-Peasy.AI is a powerful AI copywriting tool that can help you write better resumes, job descriptions, user stories, acceptance criteria, emails, and social media content.',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, clever',
      },
    ],
  },
  {
    title: 'Facebook Ads Primary Text',
    slug: 'facebook-ads-primary-text',
    description:
      'Generate Primary Texts for your Facebook Ads that get more Leads and Sales',
    icon: FaFacebook,
    isFree: true,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    // component: GenericComponent,
    category: ['Marketing', 'Business'],
    shouldUseChatGPTAPI: true,
    maxGenerations: 5,
    fields: [
      {
        label: 'Product description',
        name: 'keywords',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Easy-Peasy.AI is a powerful AI copywriting tool that can help you write better resumes, job descriptions, user stories, acceptance criteria, emails, and social media content.',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, clever',
      },
    ],
  },

  {
    title: 'Social Media Post Generator',
    slug: 'social-media-post-generator',
    description:
      'Easily create unique, high-quality posts for your social media pages - with just one click!',
    icon: UsersIcon,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    isFree: true,
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    // component: GenericComponent,
    onlyForPaidPlans: true,
    maxGenerations: 2,
    generateImage: true,
    category: ['Social Media', 'Business'],
    supportsVideoAnalysis: true,
    fields: [
      {
        label: 'Upload an image (optional)',
        name: 'extra4',
        type: 'file',
        placeholder: 'example: image.png',
        accept: 'image/*',
        acceptDescription: 'jpg, png, webp',
      },
      {
        label: 'Upload a video (optional)',
        name: 'extra3',
        type: 'file',
        placeholder: 'Upload a video to generate a post from',
        accept: 'video/*',
        acceptDescription: 'mp4, mov, webm',
        description:
          'Upload a video and AI will analyze it to create a relevant post',
      },
      {
        label: 'Social Media Platform',
        name: 'extra2',
        type: 'select',
        placeholder: 'example: Facebook',
        options: [
          'Facebook',
          'Twitter/X.com',
          'LinkedIn',
          'Instagram',
          'Instagram Threads',
        ],
      },
      {
        label: 'What is you post about?',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'example: Easy-Peasy.AI is a powerful AI copywriting tool that can help you write better resumes, job descriptions, user stories, acceptance criteria, emails, and social media content.',
        limit: 1000,
        enableMicrophone: true,
      },
      {
        label: 'Target Audience (optional)',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: sales managers',
        limit: 1000,
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, funny',
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'example: friendly, funny, cheerful',
        radioOptions: [
          { name: 'Short', checked: false },
          { name: 'Long', checked: true },
        ],
      },
      POST_IMAGES_FIELD,
    ],
  },

  {
    title: 'SEO Content Brief',
    slug: 'seo-content-brief',
    description: 'Generate SEO content briefs that get results!',
    icon: FaGoogle,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    // component: GenericComponent,
    category: ['Marketing', 'Content'],
    shouldUseChatGPTAPI: true,
    isFree: true,
    fields: [
      {
        label: 'Keyword',
        name: 'keywords',
        limit: 1000,
        type: 'text',
        placeholder: 'example: Appartments for rent in San Francisco',
      },
    ],
  },
  {
    title: 'Cover Letter Generator',
    slug: 'cover-letter-generator',
    description: 'Get the job you want with the perfect cover letter!',
    icon: FaEnvelope,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    category: 'Career',
    isFree: true,
    maxGenerations: 2,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Job Title',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Senior Frontend Developer',
      },
      {
        label: 'Your resume (optional)',
        name: 'extra1',
        type: 'textarea',
        limit: 1000,
        placeholder: `example:\n- 4+ years of experience in frontend development\n- Proficient in React, Redux, Angular, HTML, CSS, and JavaScript\n- Worked as a Software Engineer at Netflix, with React.js and AppSync\n- Favorite Frontend Tools: React Query, Material UI, Vite, Bun, Typescript, Jotai`,
      },
    ],
  },
  {
    title: 'Resignation Letter Generator',
    slug: 'resignation-letter-generator',
    description:
      'Write a resignation letter that can help you leave your job on good terms.',
    icon: FaEnvelope,
    iconForeground: 'text-red-700',
    iconBackground: 'bg-red-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    isFree: true,
    category: 'Career',
    onlyForPaidPlans: true,
    maxGenerations: 2,
    fields: [
      {
        label: 'Job Title',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Senior Frontend Developer',
      },
      {
        label: 'Company Name',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Google',
      },
      {
        label: 'Date of Resignation',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: 2022-09-01',
      },
    ],
  },
  {
    title: 'Debate Argument Generator',
    slug: 'debate-argument-generator',
    description: 'Generate compelling arguments for any debate topic',
    icon: GiArcheryTarget,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    shouldUseChatGPTAPI: true,
    category: 'Education',
    isFree: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'text',
        limit: 500,
        placeholder:
          'example: Should social media be regulated by the government?',
      },
      {
        label: 'Your Stance',
        name: 'extra1',
        placeholder: 'Against',
        type: 'radio',
        radioOptions: [
          { name: 'For', checked: true },
          { name: 'Against', checked: false },
        ],
      },
      {
        label: 'Key Points (optional)',
        name: 'extra2',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Privacy concerns, freedom of speech, impact on mental health',
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: logical, emotional, ethical',
      },
    ],
  },
  {
    title: 'Discussion Board Response Generator',
    slug: 'discussion-board-response-generator',
    description:
      'Generate high-quality responses for any discussion board topic',
    icon: ChatAlt2Icon,
    iconForeground: 'text-lime-700',
    iconBackground: 'bg-lime-50',
    shouldUseChatGPTAPI: true,
    category: 'Social Media',
    isFree: true,
    shouldUseO3Mini: true,
    onlyForPaidPlans: true,
    useTool: true,
    fields: [
      {
        label: 'Discussion Post',
        name: 'keywords',
        type: 'textarea',
        limit: 10000,
        placeholder: `example: Discuss the ethical implications of artificial intelligence in healthcare. Consider both the potential benefits and risks, and support your arguments with evidence from recent research.`,
      },
      {
        label: 'Your Initial Thoughts',
        name: 'extra1',
        type: 'textarea',
        limit: 10000,
        placeholder:
          'example: AI in healthcare can improve diagnostic accuracy and treatment personalization, but raises concerns about data privacy, algorithmic bias, and the doctor-patient relationship. Need to explore both perspectives with academic sources.',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: academic, professional, analytical',
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'example: friendly, funny, cheerful',
        radioOptions: [
          { name: 'Short', checked: false },
          { name: 'Medium', checked: true },
          { name: 'Long', checked: false },
        ],
      },
    ],
  },
  {
    title: 'Discussion Post Commenter',
    slug: 'discussion-post-commenter',
    description:
      'Generate insightful comments and suggestions for improvement for any discussion post',
    icon: ChatAlt2Icon,
    iconForeground: 'text-lime-700',
    iconBackground: 'bg-lime-50',
    shouldUseChatGPTAPI: true,
    category: 'Social Media',
    isFree: true,
    onlyForPaidPlans: true,
    shouldUseO3Mini: true,
    maxGenerations: 2,
    fields: [
      {
        label: 'Discussion Post',
        name: 'keywords',
        type: 'textarea',
        limit: 2500,
        placeholder: `example: What's the most genuine passive income that can put an extra $200 in your pocket each month? Share your real-life experiences and ideas below.`,
      },
      {
        label: 'Your Initial Thoughts',
        name: 'extra1',
        type: 'textarea',
        limit: 2500,
        placeholder:
          'example: Use AI to create faceless YouTube videos and earn money from ads.',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, academic, casual',
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'example: friendly, funny, cheerful',
        radioOptions: [
          { name: 'Short', checked: false },
          { name: 'Medium', checked: true },
          { name: 'Long', checked: false },
        ],
      },
    ],
  },
  {
    title: 'Discussion Question Generator',
    slug: 'discussion-question-generator',
    description: 'Generate engaging discussion questions for any topic',
    icon: QuestionMarkCircleIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    isFree: true,
    onlyForPaidPlans: true,
    maxGenerations: 2,
    shouldUseChatGPTAPI: true,
    category: 'Education',
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'text',
        limit: 200000,
        placeholder: 'example: Climate Change, Artificial Intelligence, etc.',
      },
      {
        label: 'Depth of Discussion',
        name: 'extra1',
        type: 'select',
        placeholder: 'example: Basic, Intermediate, Advanced',
        options: ['Basic', 'Intermediate', 'Advanced'],
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, academic, casual',
      },
    ],
  },
  {
    title: 'Twitter Post Generator',
    slug: 'twitter-post',
    description: 'The easy way to create tweets that get noticed.',
    icon: FaTwitter,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    category: 'Social Media',
    isFree: true,
    maxGenerations: 5,
    generateImage: true,
    fields: [
      {
        label: 'What is your Tweet about?',
        name: 'keywords',
        type: 'textarea',
        limit: 1000,
        placeholder: 'example: Artificial Intelligence',
      },
      {
        label: 'Include hashtags?',
        name: 'extra1',
        type: 'checkbox',
        placeholder: 'example: true',
        initialValue: false,
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: friendly, funny, cheerful',
      },
      POST_IMAGES_FIELD,
    ],
  },
  {
    title: 'Twitter Thread Generator',
    slug: 'twitter-thread',
    description: 'Never run out of things to tweet about!',
    icon: FaTwitter,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    isFree: true,
    maxGenerations: 5,
    category: 'Social Media',
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: How To Become A Software Engineer',
        limit: 10000,
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: friendly, funny, cheerful',
      },
    ],
  },
  {
    title: 'Tweet Reply Generator',
    slug: 'tweet-reply',
    description: 'Reply to any tweet with a unique, high-quality response.',
    icon: FaTwitter,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    isFree: true,
    maxGenerations: 3,
    // component: GenericComponent,
    onlyForPaidPlans: true,
    category: 'Social Media',
    fields: [
      {
        label: 'Original Tweet',
        name: 'keywords',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: A latte is really just an excuse for adults to order warm milk without sounding like a baby',
      },
      {
        label: 'Author of the original tweet (optional)',
        name: 'extra2',
        type: 'text',
        limit: 500,
        placeholder: 'example: Elon Musk',
      },
      {
        label: 'What do you want to include in your reply? (optional)',
        name: 'extra1',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: I agree with you, but I think that the real problem is that we are all using the wrong browser.',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, energetic, clever',
      },
    ],
  },
  {
    title: 'Twitter Bio Generator',
    slug: 'twitter-bio-generator',
    description:
      'Make your Twitter profile stand out with the help of our bio generator',
    icon: FaTwitter,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    isFree: true,
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    category: 'Social Media',
    maxGenerations: 5,
    fields: [
      {
        label: 'Personal Information',
        name: 'keywords',
        limit: 500,
        type: 'textarea',
        placeholder: 'example: IT guru from France. Posting about cats.',
      },
      {
        label: 'Include emojis?',
        name: 'extra1',
        type: 'checkbox',
        placeholder: 'example: true',
        initialValue: false,
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, funny, catchy',
      },
    ],
  },
  {
    title: 'Author Bio Generator',
    slug: 'author-bio-generator',
    description: 'Create a professional biography for authors and writers.',
    icon: PencilIcon,
    iconForeground: 'text-amber-700',
    iconBackground: 'bg-amber-50',
    shouldUseChatGPTAPI: true,
    isFree: true,
    category: ['Social Media', 'Creative Writing'],
    maxGenerations: 2,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Jane Austen',
      },
      {
        label: 'Genre',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Fiction, Non-fiction, Poetry',
      },
      {
        label: 'Published Works',
        name: 'extra2',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Pride and Prejudice, Emma, Sense and Sensibility',
      },
      {
        label: 'Experience',
        name: 'extra3',
        type: 'textarea',
        limit: 1000,
        placeholder: 'example: 10 years as a novelist, former journalist',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, literary, approachable',
      },
    ],
  },
  {
    title: 'Musician Bio Generator',
    slug: 'musician-bio-generator',
    description: 'Create a professional biography for musicians and bands.',
    icon: MusicNoteIcon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    shouldUseChatGPTAPI: true,
    isFree: true,
    category: ['Audio & Music', 'Social Media'],
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Musician or Band Name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Taylor Kotlin',
      },
      {
        label: 'Musical Style',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Country, Folk',
      },
      {
        label: 'Influences',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: Johnny Cash, Dolly Parton',
      },
      {
        label: 'Accomplishments',
        name: 'extra3',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Grammy Award for Album of the Year, Grammy Award for Best Country Song',
      },
      {
        label: 'Current Projects',
        name: 'extra4',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Lover Fest, Folklore: The Long Pond Studio Sessions',
      },
    ],
  },
  {
    title: 'AI Song Generator',
    slug: 'music-generator',
    description: 'Generate original songs with lyrics using AI technology.',
    icon: MusicNoteIcon,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    isAbsoluteURL: true,
    landingPageSlug: 'ai-music-generator',
    category: 'Audio & Music',
    badge: 'New',
    isFree: false,
    onlyForPaidPlans: true,
  },
  {
    title: 'Song Idea Generator',
    slug: 'song-idea-generator',
    description: "If you can't think of a song, we can help!",
    icon: MusicNoteIcon,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    // component: GenericComponent,
    category: ['Audio & Music', 'Creative Writing'],
    isFree: true,
    shouldUseChatGPTAPI: true,
    maxGenerations: 5,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Personality',
      },
      {
        label: 'Mood',
        name: 'tone',
        type: 'text',
        placeholder: 'example: happy, sad, angry',
      },
    ],
  },
  {
    title: 'Song Lyrics Generator',
    slug: 'song-lyrics-generator',
    description: 'Generate custom songs from an AI',
    icon: MusicNoteIcon,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    category: ['Audio & Music', 'Creative Writing'],
    isFree: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Personality',
      },
      {
        label: 'Genre (optional)',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Rock',
      },
      {
        label: 'Artist (optional)',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: Taylor Swift',
      },
      {
        label: 'Mood',
        name: 'tone',
        type: 'text',
        placeholder: 'example: happy, sad, angry',
      },
    ],
  },
  {
    title: 'Rap Lyrics Generator',
    slug: 'rap-lyrics-generator',
    description: 'Generate unique rhyming rap lyrics from an AI',
    icon: MusicNoteIcon,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    isFree: true,
    onlyForPaidPlans: true,
    category: ['Audio & Music', 'Creative Writing'],
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Life',
      },
      {
        label: 'Artist (optional)',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: Dr. Dre, Eminem',
      },
      {
        label: 'Mood',
        name: 'tone',
        type: 'text',
        placeholder: 'example: happy, sad, angry',
      },
    ],
  },
  {
    title: 'Diss Track Generator',
    slug: 'diss-track-generator',
    description: 'Create witty and hard-hitting diss track lyrics with AI',
    icon: MicrophoneIcon,
    iconForeground: 'text-red-700',
    iconBackground: 'bg-red-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    isFree: true,
    // Free with limits: lyrics are free; the real track is capped by the
    // music words-in-plan quota (~1 track on Free) and the video clip is free.
    onlyForPaidPlans: false,
    category: ['Audio & Music', 'Creative Writing'],
    fields: [
      {
        label: 'Target',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: rival rapper, ex-friend',
      },
      {
        label: 'Your Style (optional)',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Eminem, Kendrick Lamar',
      },
      {
        label: 'Specific Beef (optional)',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: stole my beat, dissed my crew',
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'text',
        placeholder: 'example: aggressive, sarcastic, humorous',
      },
    ],
  },
  {
    title: 'Album Title Generator',
    slug: 'album-title-generator',
    description: 'Let AI Help Create the Perfect Album Title!',
    icon: MusicNoteIcon,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    // component: GenericComponent,
    category: ['Audio & Music', 'Creative Writing'],
    shouldUseChatGPTAPI: true,
    isFree: true,
    maxGenerations: 5,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Personality',
      },
      {
        label: 'Genre (optional)',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Rock',
      },
      {
        label: 'Artist (optional)',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: Taylor Swift',
      },
      {
        label: 'Mood',
        name: 'tone',
        type: 'text',
        placeholder: 'example: happy, sad, angry',
      },
    ],
  },
  {
    title: 'Book Title Generator',
    slug: 'book-title-generator',
    description: 'Generate Unique and Creative Book Titles with AI',
    icon: BookOpenIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    // component: GenericComponent,
    category: 'Creative Writing',
    shouldUseChatGPTAPI: true,
    isFree: true,
    maxGenerations: 5,
    fields: [
      {
        label: 'Genre',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Mystery, Romance, Sci-Fi',
      },
      {
        label: 'Keywords',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: love, adventure, time travel',
      },
    ],
  },
  {
    title: 'Book Outline Generator',
    slug: 'book-outline-generator',
    description: 'Create a structured outline for your book with AI assistance',
    icon: ViewListIcon,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    category: 'Creative Writing',
    shouldUseChatGPTAPI: true,
    isFree: true,
    maxGenerations: 5,
    fields: [
      {
        label: 'Book Genre',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Historical Fiction, Self-Help, Thriller',
      },
      {
        label: 'Main Theme or Concept',
        name: 'extra1',
        type: 'textarea',
        placeholder: 'example: A journey of self-discovery in post-war Europe',
      },
      {
        label: 'Key Plot Points or Topics (optional)',
        name: 'extra2',
        type: 'textarea',
        placeholder: 'example: Character backstory, Major conflict, Resolution',
      },
      {
        label: 'Desired Number of Chapters',
        name: 'extra3',
        type: 'text',
        placeholder: 'example: 12',
      },
    ],
  },
  {
    title: 'Chapter Generator',
    slug: 'chapter-generator',
    description: 'Generate unique and captivating story chapters with AI',
    icon: BookOpenIcon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    category: 'Creative Writing',
    shouldUseChatGPTAPI: true,
    isFree: true,
    onlyForPaidPlans: true,
    shouldUseO3Mini: true,
    fields: [
      {
        label: 'Genre',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Epic Fantasy, Noir Mystery, Sci-Fi Romance',
      },
      {
        label: 'Chapter Mood',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Suspenseful, Heartwarming, Action-packed',
      },
      {
        label: 'Key Elements',
        name: 'extra2',
        type: 'textarea',
        placeholder:
          'example: Main character faces a moral dilemma, Unexpected plot twist, Introduction of a new character',
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'brand_voice',
        placeholder:
          'example: Descriptive, Dialogue-heavy, Stream of consciousness',
      },
    ],
  },
  {
    title: 'Book Summarizer',
    slug: 'book-summarizer',
    description: 'Summarize any book with the help of AI',
    icon: BookOpenIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    category: ['Writing', 'Education'],
    outputs: 1,
    shouldUseChatGPTAPI: true,
    isFree: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Book Title',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: To Kill a Mockingbird',
      },
      {
        label: 'Author (optional)',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Harper Lee',
      },
    ],
  },
  {
    title: 'Topic Sentence Generator',
    slug: 'topic-sentence-generator',
    description:
      'Generate effective topic sentences for your paragraphs or essays',
    icon: PencilAltIcon,
    iconForeground: 'text-teal-700',
    iconBackground: 'bg-teal-50',
    category: ['Writing', 'Education'],
    shouldUseChatGPTAPI: true,
    outputs: 1,
    onlyForPaidPlans: true,
    maxGenerations: 2,
    fields: [
      {
        label: 'Paragraph',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'example: Climate change is having a significant impact on agriculture. Changes in temperature and precipitation patterns are affecting crop yields and livestock productivity. Farmers are having to adapt to these changes by altering their practices and investing in new technologies.',
      },

      {
        label: 'Tone',
        name: 'tone',
        placeholder: 'Tone',
        type: 'select',
        options: ['Informative', 'Persuasive', 'Analytical', 'Descriptive'],
      },
    ],
  },
  {
    title: 'Google Sheets & Excel Formula Generator',
    slug: 'google-sheets-excel-formula-generator',
    description: 'Write Google Sheets & Excel formulas within seconds with AI',
    icon: FaFileExcel,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    // component: GenericComponent,
    category: 'Code & Data',
    shouldUseChatGPTAPI: true,
    outputs: 1,
    isFree: true,
    hideLanguageSelector: true,
    maxGenerations: 5,
    fields: [
      {
        label: 'Spreadsheet Software',
        name: 'extra2',
        type: 'select',
        placeholder: 'example: Google Sheets',
        options: ['Google Sheets', 'Excel'],
      },
      {
        label: 'What is the formula you want to generate?',
        name: 'keywords',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Get the sum of all the cells in column A that are greater than 10',
      },
    ],
  },
  {
    title: 'Generate SQL with AI',
    slug: 'generate-sql-with-ai',
    description: 'Translate plain text into SQL',
    icon: DatabaseIcon,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    // component: GenericComponent,
    category: 'Code & Data',
    isFree: true,
    shouldUseChatGPTAPI: true,
    outputs: 1,
    hideLanguageSelector: true,
    maxGenerations: 5,
    fields: [
      {
        label: 'What is the SQL you want to generate?',
        name: 'keywords',
        type: 'textarea',
        limit: 1000,
        placeholder: 'example: Find the total number of users in the database',
      },
      {
        label: 'Database',
        name: 'extra1',
        type: 'select',
        placeholder: 'example: MySQL',
        options: [
          'MySQL',
          'PostgreSQL',
          'MS SQL Server',
          'MariaDB',
          'Snowflake',
          'BigQuery',
          'SQLite',
          'IBM DB2',
          'Hive',
          'FlinkSQL',
        ],
      },
      {
        label: 'Database Schema (optional)',
        name: 'extra2',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: CREATE TABLE users (id INT, name VARCHAR(255), email VARCHAR(255), PRIMARY KEY (id))',
      },
    ],
  },
  {
    title: 'Product Name Generator',
    slug: 'product-name-generator',
    description:
      'Generate product names for your business that your customers will love',
    icon: ShoppingCartIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    // component: GenericComponent,
    isFree: true,
    category: 'Business',
    shouldUseChatGPTAPI: true,
    fields: [
      {
        label: 'Tell us about your business or product',
        name: 'keywords',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: A baby soap that is gentle and effective. It’s formulated with natural oat ingredients. It hydrates baby’s skin. It’s hypoallergenic and formulated with clean ingredients. It isn’t tear-free, so it can sting the eyes.',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: candid, funny, cheerful',
      },
    ],
  },
  {
    title: 'Product Descriptions',
    slug: 'product-descriptions',
    description: 'Create product descriptions that sell!',
    icon: ShoppingCartIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    // component: GenericComponent,
    category: 'Business',
    shouldUseChatGPTAPI: true,
    isFree: true,
    onlyForPaidPlans: true,
    showExamples: true,
    fields: [
      {
        label: 'Product Name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Aveeno',
      },
      {
        label: 'What include in the description?',
        name: 'extra1',
        type: 'textarea',
        limit: 10000,
        placeholder:
          'example: A baby soap that is gentle and effective. It’s formulated with natural oat ingredients. It hydrates baby’s skin. It’s hypoallergenic and formulated with clean ingredients. It isn’t tear-free, so it can sting the eyes.',
        enableMicrophone: true,
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: candid, funny, cheerful',
      },
    ],
  },
  // {
  //   title: 'Pros & Cons Generator',
  //   slug: 'pros-cons-generator',
  //   description: 'Generate pros and cons for any product or service',
  //   icon: ShoppingCartIcon,
  //   iconForeground: 'text-blue-700',
  //   iconBackground: 'bg-blue-50',
  // component: GenericComponent,
  //   category: 'Business',
  //   shouldUseChatGPTAPI: true,
  //   fields: [
  //     {
  //       label: 'Product/Service Name',
  //       name: 'keywords',
  //       type: 'text',
  //       placeholder: 'example: Car Insurance',
  //     },
  //     {
  //       label:
  //         'List the pros or positive aspects of the product/service (optional)',
  //       name: 'extra1',
  //       type: 'textarea',
  //       limit: 1000,
  //       placeholder:
  //         'example: The company offers a wide range of coverage options, including liability, collision, comprehensive, medical payments, and uninsured motorist. It also offers a variety of discounts, including a good student discount, a multi-policy discount, and a safe driver discount.',
  //     },
  //     {
  //       label:
  //         'List the cons or negative aspects of the product/service (optional)',
  //       name: 'extra2',
  //       type: 'textarea',
  //       limit: 1000,
  //       placeholder:
  //         'example: Some customers have complained about the company’s customer service. It also has a high rate of complaints filed with the Better Business Bureau.',
  //     },
  //     {
  //       label: 'Overall assessment',
  //       name: 'extra3',
  //       type: 'textarea',
  //       limit: 1000,
  //       placeholder:
  //         'example: It offers a wide range of coverage options and discounts.',
  //     },
  //     {
  //       label: 'Tone',
  //       name: 'tone',
  //       type: 'text',
  //       placeholder: 'example: candid, funny, cheerful',
  //     },
  //   ],
  // },
  {
    title: 'Product Brochure',
    slug: 'product-brochure',
    description: 'Create product brochures that sell!',
    icon: ShoppingCartIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    // component: GenericComponent,
    category: 'Business',
    isFree: true,
    shouldUseChatGPTAPI: true,
    fields: [
      {
        label: 'Product Name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Aveeno',
        limit: 5000,
      },
      {
        label: 'Product Details',
        name: 'extra1',
        type: 'textarea',
        limit: 10000,
        placeholder:
          'example: A baby soap that is gentle and effective. It’s formulated with natural oat ingredients. It hydrates baby’s skin. It’s hypoallergenic and formulated with clean ingredients. It isn’t tear-free, so it can sting the eyes.',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: candid, funny, cheerful',
      },
    ],
  },
  {
    title: 'Customer Case Study Generator',
    slug: 'case-study-generator',
    description: 'Create case studies that sell!',
    icon: AcademicCapIcon,
    iconForeground: 'text-lime-700',
    iconBackground: 'bg-lime-50',
    // component: GenericComponent,
    isFree: true,
    category: 'Business',
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    onlyForPaidPlans: true,
    shouldUseO3Mini: true,
    fields: [
      {
        label: 'Company Name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: OptinMonster',
      },
      {
        label: 'Product Name',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Google Ads',
      },
      {
        label: 'Case Study Outcome',
        name: 'extra3',
        type: 'text',
        placeholder: 'example: 10% increase in sales',
      },
      {
        label: 'Case details',
        name: 'extra2',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: The company was struggling to get new customers. They tried Google Ads and saw a 10% increase in sales. They also saw a 20% increase in new customers.',
      },
    ],
  },
  {
    title: 'Sales Script Creator',
    slug: 'sales-script-creator',
    description: 'Generate effective sales scripts to close more deals',
    icon: BriefcaseIcon,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    // component: GenericComponent,
    category: 'Business',
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    onlyForPaidPlans: true,
    shouldUseO3Mini: true,
    isFree: true,
    fields: [
      {
        label: 'Product/Service Name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Premium Health Insurance',
        limit: 1000,
      },
      {
        label: 'Target Audience',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Small business owners',
        limit: 1000,
      },
      {
        label: 'Unique Selling Points',
        name: 'extra2',
        type: 'textarea',
        placeholder:
          'example: Comprehensive coverage, affordable pricing, 24/7 customer support',
        limit: 1000,
      },
    ],
  },
  {
    title: 'Sales Pitch Generator',
    slug: 'sales-pitch-generator',
    description:
      'Create compelling sales pitches to win over potential customers',
    icon: SpeakerphoneIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    category: ['Business', 'Marketing'],
    isFree: true,
    shouldUseO3Mini: true,
    shouldUseGPT4o: true,
    fields: [
      {
        label: 'Product or Service Name',
        name: 'keywords',
        type: 'text',
        placeholder: 'e.g., AI-powered CRM Software',
        limit: 100,
      },
      {
        label: 'Target Audience',
        name: 'extra1',
        type: 'text',
        placeholder: 'e.g., Small to medium-sized businesses',
        limit: 100,
      },
      {
        label: 'Key Benefits',
        name: 'extra2',
        type: 'textarea',
        placeholder:
          'e.g., Increased productivity, Better customer insights, Cost-effective',
        limit: 200,
      },
      {
        label: 'Unique Selling Proposition',
        name: 'extra3',
        type: 'textarea',
        placeholder:
          'e.g., The only CRM with built-in AI assistant for personalized customer interactions',
        limit: 200,
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'text',
        placeholder: 'Select the tone for your pitch',
      },
    ],
  },
  {
    title: 'Write like Shakespeare',
    slug: 'write-like-shakespeare',
    description: 'AI Shakespearean text generator',
    icon: FaFeather,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    // component: GenericComponent,
    category: 'Creative Writing',
    shouldUseChatGPTAPI: true,
    shouldUseO3Mini: true,
    isFree: true,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: How i love my wife',
        limit: 2000,
      },
    ],
  },
  {
    title: 'AI Story Generator',
    slug: 'ai-story-generator',
    description: 'Unlock the Magic of Automated Storytelling!',
    icon: FaFeather,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    category: 'Creative Writing',
    onlyForPaidPlans: true,
    isFree: true,
    fields: [
      {
        label: 'What is the story about?',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: AI',
        limit: 10000,
        enableMicrophone: true,
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: candid, funny, cheerful',
      },
    ],
  },
  {
    title: 'Romance Story Generator',
    slug: 'romance-story-generator',
    description: 'Create captivating romance stories with AI',
    icon: HeartIcon,
    iconForeground: 'text-pink-700',
    iconBackground: 'bg-pink-50',
    category: 'Creative Writing',
    shouldUseChatGPTAPI: true,
    onlyForPaidPlans: true,
    isFree: true,
    image:
      'https://media.easy-peasy.ai/82122a50-9b8e-4150-9f62-e5b76c80d17c/53ee06fc-e2a9-49c0-abe1-105fd3741fba.png',
    fields: [
      {
        label: 'Main Characters',
        name: 'keywords',
        type: 'text',
        placeholder:
          'example: A shy bookstore owner and a charismatic traveler',
        limit: 1000,
      },
      {
        label: 'Setting',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: A quaint coastal village in Italy',
        limit: 1000,
      },
      {
        label: 'Romantic Conflict',
        name: 'extra2',
        type: 'text',
        placeholder:
          'example: Long-distance relationship, cultural differences',
        limit: 1000,
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder:
          'example: Sweet, Passionate, Dramatic, Humorous, Bittersweet',
      },
      {
        label: 'Additional Details (Optional)',
        name: 'extra3',
        type: 'textarea',
        placeholder: 'Any specific elements or plot points you want to include',
        limit: 2000,
      },
    ],
  },
  {
    title: 'Story Plot Generator',
    slug: 'story-plot-generator',
    description: 'Generate intriguing story plots with AI',
    icon: FaFeather,
    isFree: true,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    // component: GenericComponent,
    category: 'Creative Writing',
    shouldUseChatGPTAPI: true,
    shouldUseO3Mini: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Genre',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Mystery, Romance, Sci-Fi',
        limit: 1000,
      },
      {
        label: 'Main Character',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: A detective with a mysterious past',
        limit: 1000,
      },
      {
        label: 'Setting',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: A small coastal town in the 1800s',
        limit: 1000,
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'text',
        placeholder: 'example: dark, humorous, dramatic',
      },
    ],
  },
  {
    title: 'Fanfiction Maker',
    slug: 'fanfiction-maker',
    description: 'Create your own fanfiction stories with AI',
    icon: FaFeather,
    iconForeground: 'text-red-700',
    iconBackground: 'bg-red-50',
    // component: GenericComponent,
    category: 'Creative Writing',
    isFree: false,
    outputs: 1,
    shouldUseChatGPTAPI: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Fandom',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Harry Potter, Star Wars, Marvel',
        limit: 1000,
      },
      {
        label: 'Genre',
        name: 'extra2',
        type: 'select',
        placeholder: 'example: Romance',
        options: [
          'Romance',
          'Adventure',
          'Drama',
          'Fluff',
          'Angst',
          'Humor',
          'Mystery',
          'Surprise me',
        ],
        initialValue: 'Romance',
      },
      {
        label: 'Main Characters (optional)',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Harry Potter and Hermione Granger',
        limit: 1000,
        description: 'Leave blank and we’ll pick beloved canon characters.',
      },
      {
        label: 'Setting (optional)',
        name: 'extra3',
        type: 'text',
        placeholder: 'example: Hogwarts School of Witchcraft and Wizardry',
        limit: 1000,
      },
    ],
  },
  {
    title: 'Manuscript Writer',
    slug: 'manuscript-writer',
    description: 'AI-powered tool to assist in writing your manuscript',
    icon: FaFeather,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    category: 'Creative Writing',
    shouldUseO3Mini: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Quantum Physics',
        limit: 1000,
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: academic, casual, professional',
      },
    ],
  },
  {
    title: 'Poem Title Generator',
    slug: 'poem-title-generator',
    description: 'Creating Poem Title with the Push of a Button!',
    icon: FaFeather,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    // component: GenericComponent,
    category: 'Creative Writing',
    isFree: true,
    shouldUseChatGPTAPI: true,
    fields: [
      {
        label: 'What is the poem about?',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: AI',
        limit: 1000,
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: candid, funny, cheerful',
      },
    ],
  },
  {
    title: 'Poem Generator',
    slug: 'poem-generator',
    description:
      'Unlock Your Creativity with Our Simple, Powerful Poem Generator!',
    icon: FaFeather,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    // component: GenericComponent,
    category: 'Creative Writing',
    isFree: true,
    shouldUseO3Mini: true,
    onlyForPaidPlans: true,
    shouldUseChatGPTAPI: true,
    fields: [
      {
        label: 'What is the poem about?',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: AI',
        limit: 1000,
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: candid, funny, cheerful',
      },
    ],
  },
  {
    title: 'Quote Generator',
    slug: 'quote-generator',
    description: 'Making AI quotes easy - one click away!',
    icon: FaFeather,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    // component: GenericComponent,
    category: ['Personal & Fun', 'Social Media'],
    shouldUseChatGPTAPI: true,
    isFree: true,
    fields: [
      {
        label: 'Topic for the quote',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: AI',
        limit: 100,
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: candid, funny, cheerful',
      },
    ],
  },
  {
    title: 'Inspirational Quote Generator',
    slug: 'inspirational-quote-generator',
    description:
      'Generate uplifting and motivational quotes to inspire others and yourself',
    icon: LightningBoltIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    category: ['Personal & Fun', 'Social Media'],
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    dateAdded: '2024-06-30',
    isFree: true,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: perseverance, success, happiness',
        limit: 1000,
      },
    ],
  },
  {
    title: 'AI Joke Generator',
    slug: 'ai-joke-generator',
    description:
      'Make your friends laugh. Now you can be the life of the party.',
    icon: ImSmile,
    iconForeground: 'text-orange-700',
    iconBackground: 'bg-orange-50',
    // component: GenericComponent,
    category: 'Personal & Fun',
    shouldUseChatGPTAPI: true,
    isFree: true,
    fields: [
      {
        label: 'What is the joke about?',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: AI',
        limit: 1000,
      },
    ],
  },
  {
    title: 'Pun Generator',
    slug: 'pun-generator',
    description: 'Generate puns that will make everyone laugh.',
    icon: ImSmile,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    // component: GenericComponent,
    category: 'Personal & Fun',
    shouldUseChatGPTAPI: true,
    isFree: true,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: cats',
        limit: 100,
      },
    ],
  },
  {
    title: 'Greetings & Wishes Generator',
    slug: 'greetings-generator',
    description:
      'Create personalized greetings and wishes in a few simple clicks!',
    icon: CakeIcon,
    iconForeground: 'text-lime-700',
    iconBackground: 'bg-lime-50',
    // component: GenericComponent,
    category: 'Personal & Fun',
    isFree: true,
    onlyForPaidPlans: true,
    shouldUseChatGPTAPI: true,
    generateImage: true,
    generateImageDefaultOn: true,
    generateImageAspectRatio: '2:3',
    fields: [
      {
        label: 'Recipient’s name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: John, Grandma, The team',
        limit: 100,
      },
      {
        label: 'Occasion',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Christmas, Birthday, New Year, Graduation',
        limit: 100,
      },
      {
        label: 'Your relationship to them (optional)',
        name: 'extra4',
        type: 'text',
        placeholder: 'example: Friend, Coworker, Mum',
        limit: 100,
      },
      {
        label: 'Anything specific to mention? (optional)',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: their new job, our trip last summer',
        limit: 500,
      },
      {
        label: 'From (your name, optional)',
        name: 'extra5',
        type: 'text',
        placeholder: 'example: Sarah',
        limit: 100,
      },
      {
        label: 'Card style',
        name: 'extra6',
        type: 'select',
        placeholder: 'example: Elegant, Festive, Cute & playful',
        options: [
          'Cute & playful',
          'Elegant',
          'Festive',
          'Floral',
          'Funny',
          'Modern minimalist',
        ],
        initialValue: 'Elegant',
        description: 'Choose the visual look of your card.',
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'example: short, medium, long',
        radioOptions: [
          { name: 'Short', checked: true },
          { name: 'Medium', checked: false },
          { name: 'Long', checked: false },
        ],
      },
    ],
  },
  {
    title: 'Birthday Card Creator',
    slug: 'birthday-card-creator',
    description: 'Generate personalized birthday cards with ease!',
    icon: CakeIcon,
    iconForeground: 'text-lime-700',
    iconBackground: 'bg-lime-50',
    // component: GenericComponent,
    category: 'Personal & Fun',
    // Public landing form (watermarked card + signup CTA for the HD version).
    // The signup-gated variant converted only 6.5% of visitors — "free ai
    // birthday card generator" searchers won't sign up sight unseen.
    isFree: true,
    imageModel: 'OpenAI GPT Image 2.5 Sunburst Medium',
    shouldUseChatGPTAPI: true,
    useGemini: true,
    generateImage: true,
    generateImageDefaultOn: true,
    generateImageAspectRatio: '2:3',
    fields: [
      {
        label: 'Recipient’s name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: John, Sarah, Mum',
        limit: 100,
      },
      {
        label: 'Your relationship to them',
        name: 'extra4',
        type: 'text',
        placeholder: 'example: Best friend, Sister, Coworker, Mum',
        limit: 100,
        description:
          'This sets the tone — a card for your best friend reads differently than one for your boss.',
      },
      {
        label: 'Their age (optional)',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: 25, 30, 40',
        limit: 100,
      },
      {
        label: 'Special message or inside joke (optional)',
        name: 'extra2',
        type: 'textarea',
        placeholder:
          'example: Thanks for always being there for me — here’s to our next road trip!',
        limit: 500,
        minRows: 2,
      },
      {
        label: 'From (your name, optional)',
        name: 'extra5',
        type: 'text',
        placeholder: 'example: Sarah',
        limit: 100,
      },
      {
        label: 'Card style',
        name: 'extra6',
        type: 'select',
        placeholder: 'example: Cute & playful, Elegant, Funny',
        options: [
          'Cute & playful',
          'Elegant',
          'Funny',
          'Floral',
          'Kids',
          'Modern minimalist',
        ],
        initialValue: 'Cute & playful',
        description: 'Choose the visual look of your card.',
      },
      {
        label: 'Upload a photo of the recipient (optional)',
        name: 'extra3',
        type: 'file',
        placeholder: 'example: photo.png',
        accept: 'image/*',
        acceptDescription: 'jpg, png, webp',
        description: 'Add a photo and the card will feature them as the star.',
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'example: short, medium, long',
        radioOptions: [
          { name: 'Short', checked: true },
          { name: 'Medium', checked: false },
          { name: 'Long', checked: false },
        ],
      },
    ],
  },
  {
    title: 'Love Letter Generator',
    slug: 'love-letter-generator',
    description:
      'Create personalized love letters for Valentine’s Day, anniversaries, or any other special occasion.',
    icon: HeartIcon,
    iconForeground: 'text-red-700',
    iconBackground: 'bg-red-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    image:
      'https://media.easy-peasy.ai/0553d870-86a2-42bc-a56f-fd88aa50e0bb/6d69bbf8-e075-4979-b4fe-4f4943ad927f.png',
    shouldUseGPT4o: true,
    onlyForPaidPlans: true,
    category: 'Personal & Fun',
    isFree: true,
    fields: [
      {
        label: 'Name of the person you are writing to',
        name: 'extra4',
        type: 'text',
        placeholder: 'example: Marianna, John, Sarah',
        limit: 1000,
      },
      {
        label: 'Relationship',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Wife, Husband, Boyfriend, Girlfriend',
        limit: 1000,
      },
      {
        label: 'What feeling should the letter convey?',
        name: 'extra1',
        type: 'select',
        placeholder: 'example: Deep love & devotion',
        options: [
          'Deep love & devotion',
          'Passion & desire',
          'Warm affection',
          'Playful & flirty',
          'Longing — I miss them',
          'Gratitude & appreciation',
        ],
        initialValue: 'Deep love & devotion',
      },
      {
        label: 'Special memories or moments together (optional)',
        name: 'extra2',
        type: 'textarea',
        placeholder: 'example: First date, first kiss, first time you met',
        limit: 1000,
        description: 'The more specific, the more personal the letter feels.',
      },
      {
        label: 'Hopes for your future together (optional)',
        name: 'extra3',
        type: 'textarea',
        placeholder: 'example: Marriage, kids, travel, etc.',
        limit: 1000,
      },
    ],
  },
  {
    title: 'Ship Name Generator',
    slug: 'ship-name-generator',
    description:
      'Create unique and catchy ship names by combining names of couples or fictional characters.',
    icon: HeartIcon,
    iconForeground: 'text-red-700',
    iconBackground: 'bg-red-50',
    shouldUseChatGPTAPI: true,
    isFree: true,
    category: 'Personal & Fun',
    fields: [
      {
        label: 'First Name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Brad, Elizabeth, Harry',
        limit: 100,
      },
      {
        label: 'Second Name',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: Angelina, Darcy, Hermione',
        limit: 100,
      },
      {
        label: 'Are these real people or fictional characters?',
        name: 'extra3',
        type: 'text',
        placeholder:
          'example: Real couple, Fictional characters, Celebrity couple',
        limit: 100,
      },
      {
        label: 'Any specific theme or style preferences?',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Cute, Funny, Romantic, Fantasy-inspired',
        limit: 100,
      },
    ],
  },
  {
    title: 'Valentine Creator',
    slug: 'valentine-creator',
    description:
      "Craft personalized Valentine's Day messages for your loved ones.",
    icon: HeartIcon,
    iconForeground: 'text-red-700',
    iconBackground: 'bg-red-50',
    // component: GenericComponent,
    category: 'Personal & Fun',
    shouldUseChatGPTAPI: true,
    generateImage: true,
    generateImageDefaultOn: true,
    generateImageAspectRatio: '2:3',
    isFree: true,
    fields: [
      {
        label: 'Recipient’s name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Jessica, Mike, Sarah',
        limit: 100,
      },
      {
        label: 'Your relationship to them',
        name: 'extra4',
        type: 'text',
        placeholder: 'example: Partner, Spouse, Crush, Friend',
        limit: 100,
      },
      {
        label: 'Special message (optional)',
        name: 'extra2',
        type: 'textarea',
        placeholder:
          'example: You make every day brighter — happy Valentine’s Day!',
        limit: 1000,
        minRows: 2,
      },
      {
        label: 'From (your name, optional)',
        name: 'extra5',
        type: 'text',
        placeholder: 'example: Alex',
        limit: 100,
      },
      {
        label: 'Card style',
        name: 'extra6',
        type: 'select',
        placeholder: 'example: Romantic, Cute & playful, Elegant',
        options: ['Romantic', 'Cute & playful', 'Elegant', 'Funny', 'Floral'],
        initialValue: 'Romantic',
        description: 'Choose the visual look of your card.',
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'example: short, medium, long',
        radioOptions: [
          { name: 'Short', checked: true },
          { name: 'Medium', checked: false },
          { name: 'Long', checked: false },
        ],
      },
    ],
  },
  {
    title: 'Tinder Bio Generator',
    slug: 'tinder-bio',
    description:
      'Make your Tinder profile stand out with the help of our bio generator',
    icon: HeartIcon,
    iconForeground: 'text-red-700',
    iconBackground: 'bg-red-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    category: 'Personal & Fun',
    shouldUseGPT4o: true,
    onlyForPaidPlans: true,
    isFree: true,
    fields: [
      {
        label: 'Personal Information',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: 25 years old, tall, great sense of humor',
        limit: 1000,
      },
      {
        label: 'Upload your photo (optional)',
        name: 'extra1',
        type: 'file',
        placeholder: 'Upload a photo to help AI write a better bio',
        accept: 'image/*',
        acceptDescription: 'jpg, png, webp',
      },
    ],
  },
  {
    title: 'Tinder Response Generator',
    slug: 'tinder-response',
    description: 'Generate engaging responses for your Tinder matches',
    icon: HeartIcon,
    iconForeground: 'text-red-700',
    iconBackground: 'bg-red-50',
    shouldUseChatGPTAPI: true,
    category: 'Personal & Fun',
    isFree: true,
    shouldUseGPT4o: true,
    shouldUseO3Mini: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Message',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: Hi, how are you?',
        limit: 1000,
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: Romantic, Friendly, Funny',
      },
    ],
  },
  {
    title: 'Pick Up Line Generator',
    slug: 'pick-up-line-generator',
    description: 'Never run out of lines - with our Pick Up Line Generator!',
    icon: HeartIcon,
    iconForeground: 'text-red-700',
    iconBackground: 'bg-red-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    category: 'Personal & Fun',
    onlyForPaidPlans: true,
    isFree: false,
    fields: [
      {
        label: 'Keywords',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: gym',
        limit: 100,
      },
      {
        label: 'For whom?',
        name: 'extra2',
        type: 'select',
        placeholder: 'example: Positive',
        options: ['Woman', 'Man'],
      },
    ],
  },
  {
    title: 'Instagram Bio',
    slug: 'instagram-bio',
    description:
      'Make your Instagram profile stand out with the help of our bio generator',
    icon: RiInstagramLine,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    // component: GenericComponent,
    shouldUseChatGPTAPI: true,
    category: 'Social Media',
    isFree: true,
    fields: [
      {
        label: 'Personal Information',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: IT guru from France. Posting about cats.',
        limit: 1000,
      },
    ],
  },
  {
    title: 'Reel Script Generator',
    slug: 'reel-script-generator',
    description:
      'Create engaging scripts for your Instagram Reels or TikTok videos effortlessly!',
    icon: VideoCameraIcon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    shouldUseAnthropic: true,
    category: 'Social Media',
    shouldUseChatGPTAPI: true,
    isFree: true,
    supportsVideoAnalysis: true,
    fields: [
      {
        label: 'Upload a video (optional)',
        name: 'extra1',
        type: 'file',
        placeholder: 'Upload a reference video for script generation',
        accept: 'video/*',
        acceptDescription: 'mp4, mov, webm',
        description:
          'Upload a video and AI will analyze it to create a relevant script',
      },
      {
        label: 'Background Information',
        name: 'keywords',
        type: 'textarea',
        placeholder: '',
        limit: 100000,
      },
    ],
  },
  {
    title: 'Instagram Reel Captions',
    slug: 'instagram-reel-captions-generator',
    description: 'Make your Instagram Reels stand out and get more views!',
    icon: RiInstagramLine,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    // component: GenericComponent,
    category: 'Social Media',
    isFree: true,
    shouldUseChatGPTAPI: true,
    supportsVideoAnalysis: true,
    fields: [
      {
        label: 'Upload a video (optional)',
        name: 'extra1',
        type: 'file',
        placeholder: 'Upload a video to generate caption from',
        accept: 'video/*',
        acceptDescription: 'mp4, mov, webm',
        description:
          'Upload a video and AI will analyze it to create a relevant caption',
      },
      {
        label: 'Post Topic',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: weekend with family in the park',
        limit: 1000,
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: friendly, funny, cheerful',
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'example: friendly, funny, cheerful',
        radioOptions: [
          { name: 'Short', checked: false },
          { name: 'Medium', checked: false },
          { name: 'Long', checked: true },
        ],
      },
    ],
  },
  {
    title: 'Instagram Reel Ideas',
    slug: 'instagram-reel-ideas-generator',
    description: 'Get ideas for your next Instagram Reel to get more views!',
    icon: RiInstagramLine,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    // component: GenericComponent,
    category: 'Social Media',
    isFree: true,
    shouldUseChatGPTAPI: true,
    supportsVideoAnalysis: true,
    fields: [
      {
        label: 'Upload a video (optional)',
        name: 'extra1',
        type: 'file',
        placeholder: 'Upload a video for inspiration',
        accept: 'video/*',
        acceptDescription: 'mp4, mov, webm',
        description:
          'Upload a video and AI will analyze it to suggest relevant ideas',
      },
      {
        label: 'What topic should the reel be about?',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: I want to promote my coffee shop',
        limit: 1000,
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: friendly, funny, cheerful',
      },
    ],
  },
  {
    title: 'Instagram Reel Hashtags',
    slug: 'instagram-reel-hashtags-generator',
    description:
      'Get hashtags for your next Instagram Reel to reach more people!',
    icon: RiInstagramLine,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    isFree: true,
    // component: GenericComponent,
    category: 'Social Media',
    shouldUseChatGPTAPI: true,
    supportsVideoAnalysis: true,
    fields: [
      {
        label: 'Upload a video (optional)',
        name: 'extra1',
        type: 'file',
        placeholder: 'Upload a video to generate hashtags from',
        accept: 'video/*',
        acceptDescription: 'mp4, mov, webm',
        description:
          'Upload a video and AI will analyze it to suggest relevant hashtags',
      },
      {
        label: 'What is your Instagram Reel about?',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: weekend with family on the beach',
        limit: 1000,
      },
    ],
  },
  {
    title: 'Instagram Threads Post Generator',
    slug: 'instagram-threads-post-generator',
    description: 'Generate a content for an Instagram Threads',
    icon: ThreadsSvg,
    iconForeground: 'text-black dark:fill-white dark:text-slate-200',
    iconBackground: 'bg-gray-50 dark:bg-gray-50',
    shouldUseChatGPTAPI: true,
    isFree: true,
    // component: GenericComponent,
    category: 'Social Media',
    generateImage: true,
    fields: [
      {
        label: 'What is your post about?',
        name: 'keywords',
        type: 'textarea',
        limit: 1000,
        placeholder: 'example: Artificial Intelligence',
      },
      {
        label: 'Include hashtags?',
        name: 'extra1',
        type: 'checkbox',
        placeholder: 'example: true',
        initialValue: true,
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: friendly, funny, cheerful',
      },
      POST_IMAGES_FIELD,
    ],
  },
  {
    title: 'Content Calendar Generator',
    slug: 'content-calendar-generator',
    description:
      'Plan your content for social media to reach more people and grow your audience',
    icon: UsersIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    // component: GenericComponent,
    category: 'Social Media',
    shouldUseChatGPTAPI: true,
    isFree: true,
    onlyForPaidPlans: true,
    shouldUseO3Mini: true,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: AI Conference',
        limit: 1000,
      },
    ],
  },
  {
    title: 'Podcast Name Generator',
    slug: 'podcast-name-generator',
    description: 'Generate podcast names that will help you grow your audience',
    icon: FaPodcast,
    iconForeground: 'text-lime-700',
    iconBackground: 'bg-lime-50',
    isFree: true,
    // component: GenericComponent,
    category: 'Audio & Music',
    shouldUseChatGPTAPI: true,
    fields: [
      {
        label: 'What is your podcast about?',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: AI in healthcare',
        limit: 1000,
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, funny, witty',
      },
    ],
  },
  {
    title: 'Podcast Episode Title Generator',
    slug: 'podcast-episode-title-generator',
    description:
      'Generate podcast episode titles that will help you grow your audience',
    icon: FaPodcast,
    iconForeground: 'text-lime-700',
    iconBackground: 'bg-lime-50',
    // component: GenericComponent,
    category: 'Audio & Music',
    shouldUseChatGPTAPI: true,
    showExamples: true,
    fields: [
      {
        label: 'Episode Description or Summary',
        name: 'keywords',
        type: 'textarea',
        placeholder: '',
        limit: 10000,
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, funny, witty',
      },
    ],
  },
  {
    title: 'Podcast Episode Description Generator',
    slug: 'podcast-episode-description-generator',
    description:
      'Generate podcast episode description that will help you grow your audience',
    icon: FaPodcast,
    shouldUseChatGPTAPI: true,
    iconForeground: 'text-lime-700',
    iconBackground: 'bg-lime-50',
    // component: GenericComponent,
    category: 'Audio & Music',
    shouldUseAnthropic: true,
    showExamples: true,
    fields: [
      {
        label: 'Episode Summary',
        name: 'keywords',
        type: 'textarea',
        placeholder: '',
        limit: 150000,
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, funny, witty',
      },
    ],
  },
  {
    title: 'Podcast Show Notes',
    slug: 'podcast-show-notes-generator',
    description:
      'Create show notes that will help you grow your podcast audience',
    icon: FaPodcast,
    iconForeground: 'text-lime-700',
    iconBackground: 'bg-lime-50',
    // component: GenericComponent,
    category: 'Audio & Music',
    shouldUseChatGPTAPI: true,
    showExamples: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Podcast Transcript or Summary',
        name: 'keywords',
        type: 'textarea',
        placeholder: '',
        limit: 10000,
      },
      {
        label: 'Show Notes Example (optional)',
        name: 'extra1',
        type: 'textarea',
        placeholder: '',
        limit: 1500,
      },
    ],
  },
  {
    title: 'Podcast Introduction Generator',
    slug: 'podcast-introduction-generator',
    description:
      'Generate podcast introductions that will help you grow your audience',
    icon: FaPodcast,
    iconForeground: 'text-lime-700',
    iconBackground: 'bg-lime-50',
    // component: GenericComponent,
    category: 'Audio & Music',
    loginPreset: 'chat',
    shouldUseChatGPTAPI: true,
    shouldUseAnthropic: true,
    showExamples: true,
    fields: [
      {
        label: 'Podcast Transcript or Summary',
        name: 'keywords',
        type: 'textarea',
        placeholder: '',
        limit: 200000,
      },
    ],
  },
  {
    title: 'Timestamped Overview Generator',
    slug: 'timestamped-overview-generator',
    description: 'Generate a timestamped overview of a recording transcript',
    icon: MicrophoneIcon,
    iconForeground: 'text-cyan-700',
    iconBackground: 'bg-cyan-50',
    // component: GenericComponent,
    shouldUseAnthropic: true,
    shouldUseChatGPTAPI: true,
    category: 'Audio & Music',
    fields: [
      {
        label: 'Transcript',
        name: 'keywords',
        type: 'textarea',
        minRows: 8,
        limit: 200000,
        description:
          'To get the best result, be as specific as possible with your instructions to the AI',
        placeholder: '',
      },
    ],
  },
  {
    title: 'Podcast Script Generator',
    slug: 'podcast-script-generator',
    description: 'Generate scripts for your podcast episodes',
    icon: FaPodcast,
    iconForeground: 'text-lime-700',
    iconBackground: 'bg-lime-50',
    outputs: 1,
    shouldUseChatGPTAPI: true,
    category: 'Audio & Music',
    fields: [
      {
        label: 'Podcast Topic',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: AI in healthcare',
        limit: 1000,
      },
      {
        label: 'Background Information (optional)',
        name: 'background',
        type: 'textarea',
        placeholder:
          'In this podcast episode, I will discuss the role, impact, and future of AI in healthcare. This includes its applications, benefits, challenges, and ethical considerations.',
        limit: 2000,
      },
      {
        label: 'Length',
        name: 'length',
        type: 'radio',
        radioOptions: [
          { name: 'Short', checked: false },
          { name: 'Medium', checked: true },
          { name: 'Long', checked: false },
        ],
        placeholder: 'Select length',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, funny, witty',
      },
    ],
  },
  {
    title: 'Real Estate Listing Generator',
    slug: 'real-estate-listing-generator',
    description:
      'Craft compelling real estate listings that promote fast home sales',
    icon: HomeIcon,
    iconForeground: 'text-zinc-700',
    iconBackground: 'bg-zinc-50',
    isFree: true,
    // component: GenericComponent,
    shouldUseGPT4o: true,
    onlyForPaidPlans: true,
    shouldUseChatGPTAPI: true,
    category: ['Business', 'Marketing'],
    showExamples: true,
    fields: [
      {
        label: 'Property Type',
        name: 'extra1',
        type: 'select',
        options: [
          'House',
          'Condominium',
          'Landed',
          'Commercial',
          'Townhouse',
          'Vacant Land',
          'Multi-Family',
          'Apartment',
          'HDB',
          'Other',
        ],
        placeholder: 'Select property type',
      },
      // images optional using files
      {
        label: 'Images (optional)',
        name: 'files',
        type: 'files',
        maxFiles: 6,
        maxFileSize: 10,
        placeholder: 'example: images_1.jpg, images_2.jpg, images_3.jpg',
        accept: 'image/*',
        acceptDescription: 'jpg, png, webp',
      },
      {
        label: 'Information about the home to include in the listing',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'example: bungalow, private pool, lift, walk to beach, future mrt. 5 beds. 6 baths. 2 carparks. 4510 sqft. 99 years leasehold. $2.5m. Punggol.',
        limit: 10000,
      },
      {
        label: 'Listing Type',
        name: 'extra2',
        type: 'radio',
        placeholder: 'example: For Sale',
        radioOptions: [
          { name: 'For Sale', checked: true },
          { name: 'For Rent', checked: false },
        ],
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'Select length',
        radioOptions: [
          { name: 'Short', checked: false },
          { name: 'Medium', checked: true },
          { name: 'Long', checked: false },
        ],
      },
      {
        label: 'Additional Instructions (optional)',
        name: 'extra3',
        type: 'textarea',
        limit: 5000,
        placeholder: 'example: Use only paragraphs, no headings',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, creative, neutral',
      },
    ],
  },
  {
    title: 'FAQ Generator',
    slug: 'faq-generator',
    description:
      'Generate a list of frequently asked questions for your website or app',
    icon: QuestionMarkCircleIcon,
    iconForeground: 'text-pink-700',
    iconBackground: 'bg-pink-50',
    shouldUseChatGPTAPI: true,
    isFree: true,
    // component: GenericComponent,
    category: ['Business', 'Marketing'],
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'textarea',
        limit: 8000,
        placeholder: 'example: Easy-Peasy.AI',
      },
      {
        label: 'Background Information (optional)',
        placeholder:
          'example: Easy-Peasy.AI is your One-Stop Solution for Content Creation, Image Crafting, Audio Generation, and AI Transcription.',
        name: 'extra2',
        type: 'textarea',
        limit: 10000,
      },
      {
        label: 'Number of questions',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: 10',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, funny, witty',
      },
    ],
  },
  {
    title: 'Active to Passive Voice Converter',
    slug: 'active-to-passive-voice-converter',
    hideWebSearch: true,
    description:
      'Convert Your Text Instantly From Active Voice to Passive or Vice Versa',
    icon: FaTextWidth,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    shouldUseChatGPTAPI: true,
    // component: GenericComponent,
    category: 'Writing',
    onlyForPaidPlans: true,
    outputs: 1,
    fields: [
      {
        label: 'Content to convert',
        name: 'keywords',
        type: 'textarea',
        limit: 8000,
        placeholder: 'example: The teacher taught the class a new topic',
      },
      {
        label: 'Action',
        name: 'extra1',
        type: 'select',
        placeholder: 'example: Positive',
        options: ['Active to Passive', 'Passive to Active'],
      },
    ],
  },
  {
    title: 'ChatGPT Prompt Generator',
    slug: 'chatgpt-prompt-generator',
    description:
      'Generate a prompt for ChatGPT to generate a perfect response for your needs',
    icon: BeakerIcon,
    iconForeground: 'text-lime-700',
    iconBackground: 'bg-lime-50',
    shouldUseChatGPTAPI: true,
    isFree: true,
    // component: GenericComponent,
    category: ['Code & Data', 'Writing'],
    onlyForPaidPlans: true,
    outputs: 3,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: Book Review',
      },
    ],
  },
  {
    title: 'ChatGPT Persona Instructions Generator',
    slug: 'chatgpt-persona-instructions-generator',
    description:
      'Generate ChatGPT instructions to create a persona that will generate responses in a certain style',
    icon: BeakerIcon,
    iconForeground: 'text-lime-700',
    iconBackground: 'bg-lime-50',
    shouldUseChatGPTAPI: true,
    // component: GenericComponent,
    category: ['Code & Data', 'Writing'],
    outputs: 3,
    fields: [
      {
        label: 'Persona Title',
        name: 'keywords',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: Social Media Influencer',
      },
    ],
  },
  {
    title: 'Art Prompt Generator',
    slug: 'art-prompt-generator',
    description:
      'Generate prompts for a stable diffusion-based image generators, including Easy-Peasy.AI Image Generator',
    icon: BeakerIcon,
    iconForeground: 'text-lime-700',
    iconBackground: 'bg-lime-50',
    shouldUseChatGPTAPI: true,
    hideLanguageSelector: true,
    // component: GenericComponent,
    onlyForPaidPlans: true,
    category: 'Images & Video',
    // isFree: true,
    fields: [
      // image file upload
      {
        label: 'Upload an image (optional)',
        name: 'extra1',
        type: 'file',
        placeholder: 'example: image.png',
        accept: 'image/*',
        acceptDescription: 'jpg, png, webp',
        description: 'Generate prompts based on an image',
      },
      {
        label: 'Topic',
        name: 'keywords',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: a photo of a pretty woman',
      },
    ],
  },
  {
    title: 'Video Prompt Generator',
    slug: 'video-prompt-generator',
    description:
      'Generate video prompts using camera direction, style, pacing, special effects and more',
    icon: VideoCameraIcon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    shouldUseChatGPTAPI: true,
    onlyForPaidPlans: true,
    isFree: true,
    category: 'Images & Video',
    fields: [
      {
        label: 'Upload an image (optional)',
        name: 'extra1',
        type: 'file',
        placeholder: 'example: image.png',
        accept: 'image/*',
        acceptDescription: 'jpg, png, webp',
        description: 'Analyze and incorporate an image into the video prompt',
      },
      {
        label: 'Core Concept',
        name: 'keywords',
        type: 'textarea',
        limit: 1000,
        placeholder: 'example: A futuristic city at sunset with flying cars',
        description: 'Core concept or thematic input for the video prompt',
      },
      {
        label: 'Style',
        name: 'extra6',
        type: 'select',
        placeholder: 'Select style',
        initialValue: 'Simple',
        options: [
          'Minimalist',
          'Simple',
          'Detailed',
          'Descriptive',
          'Dynamic',
          'Cinematic',
          'Documentary',
          'Animation',
          'Action',
          'Experimental',
        ],
      },
      {
        label: 'Camera Style',
        name: 'extra2',
        type: 'select',
        placeholder: 'Select camera style',
        initialValue: 'None',
        options: [
          'None',
          'Steadicam flow',
          'Drone aerials',
          'Handheld urgency',
          'Crane elegance',
          'Dolly precision',
          'VR 360',
          'Multi-angle rig',
          'Static tripod',
          'Gimbal smoothness',
          'Slider motion',
          'Jib sweep',
          'POV immersion',
          'Time-slice array',
          'Macro extreme',
          'Tilt-shift miniature',
          'Snorricam character',
          'Whip pan dynamics',
          'Dutch angle tension',
          'Underwater housing',
          'Periscope lens',
        ],
      },
      {
        label: 'Camera Direction',
        name: 'extra3',
        type: 'select',
        placeholder: 'Select camera direction',
        initialValue: 'None',
        options: [
          'None',
          'Zoom in',
          'Zoom out',
          'Pan left',
          'Pan right',
          'Tilt up',
          'Tilt down',
          'Orbital rotation',
          'Push in',
          'Pull out',
          'Track forward',
          'Track backward',
          'Spiral in',
          'Spiral out',
          'Arc movement',
          'Diagonal traverse',
          'Vertical rise',
          'Vertical descent',
        ],
      },
      {
        label: 'Pacing',
        name: 'extra4',
        type: 'select',
        placeholder: 'Select pacing',
        initialValue: 'None',
        options: [
          'None',
          'Slow burn',
          'Rhythmic pulse',
          'Frantic energy',
          'Ebb and flow',
          'Hypnotic drift',
          'Time-lapse rush',
          'Stop-motion staccato',
          'Gradual build',
          'Quick cut rhythm',
          'Long take meditation',
          'Jump cut energy',
          'Match cut flow',
          'Cross-dissolve dreamscape',
          'Parallel action',
          'Slow motion impact',
          'Ramping dynamics',
          'Montage tempo',
          'Continuous flow',
          'Episodic breaks',
        ],
      },
      {
        label: 'Special Effects',
        name: 'extra5',
        type: 'select',
        placeholder: 'Select special effects',
        initialValue: 'None',
        options: [
          'None',
          'Practical effects',
          'CGI enhancement',
          'Analog glitches',
          'Light painting',
          'Projection mapping',
          'Nanosecond exposures',
          'Double exposure',
          'Smoke diffusion',
          'Lens flare artistry',
          'Particle systems',
          'Holographic overlay',
          'Chromatic aberration',
          'Digital distortion',
          'Wire removal',
          'Motion capture',
          'Miniature integration',
          'Weather simulation',
          'Color grading',
          'Mixed media composite',
          'Neural style transfer',
        ],
      },
      {
        label: 'Custom Technical Elements',
        name: 'extra7',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: 4K resolution, HDR, anamorphic lens',
        description: 'Optional custom technical elements to include',
      },
      // length
      {
        label: 'Output Length',
        name: 'length',
        type: 'radio',
        placeholder: 'example: short, medium, long',
        initialValue: 'Medium',
        radioOptions: [
          { name: 'Short', checked: false },
          { name: 'Medium', checked: true },
          { name: 'Long', checked: false },
        ],
      },
    ],
  },
  {
    title: 'Image Description Generator',
    slug: 'image-description-generator',
    description: 'Generate descriptions for images using AI',
    icon: PhotographIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    category: ['Images & Video', 'Writing'],
    onlyForPaidPlans: true,
    outputs: 1,
    isFree: false,
    fields: [
      {
        label: 'Upload an image',
        name: 'keywords',
        type: 'file',
        placeholder: 'example: image.png',
        accept: 'image/*',
        acceptDescription: 'jpg, png, webp',
        description: 'Generate a description based on an image',
      },
      {
        label: 'Additional Instructions',
        name: 'extra1',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: Make a post for Instagram',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'text',
        placeholder: 'example: professional, funny, witty',
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'example: friendly, funny, cheerful',
        initialValue: 'Medium',
        radioOptions: [
          { name: 'Short', checked: false },
          { name: 'Medium', checked: true },
          { name: 'Long', checked: false },
        ],
      },
    ],
  },
  {
    title: 'PDF Summarizer',
    slug: 'pdf-summarizer',
    description: 'Summarize PDF documents with ease',
    icon: DocumentTextIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseAnthropic: true,
    category: ['Writing', 'Education', 'Business'],
    outputs: 1,
    fields: [
      {
        label: 'Upload a PDF',
        name: 'keywords',
        type: 'file',
        placeholder: 'example: document.pdf',
        accept: 'application/pdf',
        description: '',
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'example: friendly, funny, cheerful',
        radioOptions: [
          { name: 'Short', checked: true },
          { name: 'Long', checked: false },
        ],
      },
    ],
  },
  {
    title: 'Wedding Speech Generator',
    slug: 'wedding-speech-generator',
    description:
      'Generate a speech for your wedding that will impress your guests',
    icon: MicrophoneIcon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    shouldUseChatGPTAPI: true,
    isFree: true,
    // component: GenericComponent,
    category: 'Personal & Fun',
    outputs: 3,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Names of the couple',
        name: 'keywords',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: John and Jane',
      },
      {
        label: 'Key memories or experiences with the couple',
        name: 'extra1',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: Funny anecdotes, shared adventures',
      },
      {
        label: 'What qualities or characteristics do you admire in the couple?',
        name: 'extra2',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: Kindness, generosity, patience',
      },
      {
        label: "Any special messages or wishes for the couple's future?",
        name: 'extra3',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: I wish you a lifetime of happiness and joy',
      },
    ],
  },
  {
    title: 'Maid of Honor Speech Generator',
    slug: 'maid-of-honor-speech-generator',
    description:
      'Generate a heartfelt and memorable maid of honor speech for the wedding',
    icon: MicrophoneIcon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    shouldUseChatGPTAPI: true,
    isFree: true,
    category: 'Personal & Fun',
    onlyForPaidPlans: true,
    outputs: 3,
    fields: [
      {
        label: 'Names of the couple',
        name: 'keywords',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: Jane and John',
      },
      {
        label: 'Key memories or experiences with the bride',
        name: 'extra1',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: Shared adventures, funny anecdotes',
      },
      {
        label: 'What qualities or characteristics do you admire in the bride?',
        name: 'extra2',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: Kindness, generosity, patience',
      },
      {
        label: "Any special messages or wishes for the couple's future?",
        name: 'extra3',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: I wish you a lifetime of happiness and joy',
      },
    ],
  },
  {
    title: 'Best Man Speech Generator',
    slug: 'best-man-speech-generator',
    description:
      'Generate a heartfelt and memorable best man speech for the wedding',
    icon: MicrophoneIcon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    shouldUseChatGPTAPI: true,
    isFree: true,
    onlyForPaidPlans: true,
    dateAdded: '2024-05-01',
    category: 'Personal & Fun',
    outputs: 3,
    fields: [
      {
        label: 'Names of the couple',
        name: 'keywords',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: Jane and John',
      },
      {
        label: 'Key memories or experiences with the groom',
        name: 'extra1',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: Shared adventures, funny anecdotes',
      },
      {
        label: 'What qualities or characteristics do you admire in the groom?',
        name: 'extra2',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: Kindness, generosity, patience',
      },
      {
        label: "Any special messages or wishes for the couple's future?",
        name: 'extra3',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: I wish you a lifetime of happiness and joy',
      },
    ],
  },
  {
    title: 'Wedding Vow Generator',
    slug: 'wedding-vow-generator',
    description: 'Create heartfelt and personalized wedding vows',
    icon: HeartIcon,
    iconForeground: 'text-red-700',
    iconBackground: 'bg-red-50',
    shouldUseChatGPTAPI: true,
    onlyForPaidPlans: true,
    isFree: true,
    dateAdded: '2024-05-02',
    category: 'Personal & Fun',
    outputs: 3,
    fields: [
      {
        label: 'Your name',
        name: 'keywords',
        type: 'text',
        limit: 100,
        placeholder: 'example: Sarah',
      },
      {
        label: "Your partner's name",
        name: 'extra1',
        type: 'text',
        limit: 100,
        placeholder: 'example: Michael',
      },
      {
        label: 'How long have you been together?',
        name: 'extra2',
        type: 'text',
        limit: 100,
        placeholder: 'example: 5 years',
      },
      {
        label: 'What do you love most about your partner?',
        name: 'extra3',
        type: 'textarea',
        limit: 1500,
        placeholder:
          'example: Their kindness, sense of humor, and unwavering support',
      },
      {
        label: 'Any special memories or moments you want to include?',
        name: 'extra4',
        type: 'textarea',
        limit: 1500,
        placeholder:
          'example: Our first date, the day we moved in together, our travels',
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: romantic, heartfelt, humorous',
        limit: 250,
      },
    ],
  },
  {
    title: 'Wedding Invite Message Generator',
    slug: 'wedding-invite-message-generator',
    description:
      'Generate a message for your wedding invite that will impress your guests',
    icon: PaperAirplaneIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    // component: GenericComponent,
    category: 'Personal & Fun',
    isFree: true,
    onlyForPaidPlans: true,
    outputs: 3,
    fields: [
      {
        label: 'Names of the couple',
        name: 'keywords',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: John and Jane',
      },
      {
        label: 'Date of the wedding',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: 1st January 2022',
      },
      {
        label: 'Location of the wedding (optional)',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: Singapore',
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: friendly, funny, cheerful',
        limit: 250,
      },
    ],
  },
  {
    title: 'Birthday Invitation Generator',
    slug: 'birthday-invitation-generator',
    description: 'Generate a unique and personalized birthday invitation',
    icon: CakeIcon,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    shouldUseChatGPTAPI: true,
    generateImage: true,
    generateImageDefaultOn: true,
    generateImageAspectRatio: '2:3',
    category: 'Personal & Fun',
    isFree: true,
    outputs: 1,
    fields: [
      {
        label: 'Birthday person’s name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: John',
      },
      {
        label: 'Party date & time',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Saturday, June 14th at 2 PM',
      },
      {
        label: 'Location (optional)',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: 12 Oak Street / My house',
      },
      {
        label: 'Hosted by (optional)',
        name: 'extra5',
        type: 'text',
        placeholder: 'example: Sarah',
      },
      {
        label: 'Card style',
        name: 'extra6',
        type: 'select',
        placeholder: 'example: Cute & playful, Festive, Elegant',
        options: [
          'Cute & playful',
          'Festive',
          'Elegant',
          'Kids',
          'Modern minimalist',
        ],
        initialValue: 'Cute & playful',
        description: 'Choose the visual look of your invitation.',
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'example: short, medium, long',
        radioOptions: [
          { name: 'Short', checked: true },
          { name: 'Medium', checked: false },
          { name: 'Long', checked: false },
        ],
      },
    ],
  },
  {
    title: 'Baby Shower Speech Generator',
    slug: 'baby-shower-speech-generator',
    description: 'Generate a heartfelt and memorable speech for a baby shower',
    icon: HeartIcon,
    iconForeground: 'text-pink-700',
    iconBackground: 'bg-pink-50',
    shouldUseChatGPTAPI: true,
    category: 'Personal & Fun',
    isFree: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Name of Parents-to-be',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Sarah and John',
      },
      {
        label: 'Your Relationship to Parents',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Best friend of the mother',
      },
      {
        label: 'Special Memories or Wishes (optional)',
        name: 'extra2',
        type: 'textarea',
        limit: 500,
        placeholder:
          'example: Known Sarah since college, excited to be godmother',
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: warm, emotional, heartfelt',
        limit: 250,
      },
    ],
  },
  {
    title: 'Vision Statement Generator',
    slug: 'vision-statement-generator',
    description:
      'Generate a vision statement for your company or project that will inspire your team',
    icon: ChartBarIcon,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    shouldUseChatGPTAPI: true,
    // component: GenericComponent,
    shouldUseGPT4o: true,
    onlyForPaidPlans: true,
    category: ['Business', 'HR'],
    outputs: 3,
    isFree: true,
    fields: [
      {
        label: 'Company Name',
        name: 'keywords',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: Google',
      },
      {
        label: 'What does your company do?',
        name: 'extra1',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: We make the best search engine in the world',
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: friendly, funny, cheerful',
        limit: 250,
      },
    ],
  },
  {
    title: 'Mission Statement Generator',
    slug: 'mission-statement-generator',
    description:
      'Generate a mission statement for your company that will inspire your team',
    icon: ChartBarIcon,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    shouldUseChatGPTAPI: true,
    onlyForPaidPlans: true,
    category: ['Business', 'HR'],
    outputs: 3,
    isFree: true,
    fields: [
      {
        label: 'Company Name',
        name: 'keywords',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: Google',
      },
      {
        label: 'What does your company do?',
        name: 'extra1',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: We make the best search engine in the world',
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: friendly, funny, cheerful',
        limit: 250,
      },
    ],
  },
  {
    title: 'Business Memo Generator',
    slug: 'business-memo-generator',
    description: 'Generate a business memo based on the provided details',
    icon: DocumentIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseGPT4o: true,
    shouldUseChatGPTAPI: true,
    isFree: true,
    onlyForPaidPlans: true,
    shouldUseO3Mini: true,
    category: 'Business',
    fields: [
      {
        label: "Recipient's name",
        name: 'keywords',
        type: 'text',
        placeholder: 'example: John Doe',
      },
      {
        label: 'Purpose of memo',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Update on project status',
      },
      {
        label: 'Background information on topic at hand',
        name: 'extra2',
        type: 'textarea',
        placeholder:
          'example: We have been working on the project for 3 months and are 50% done.',
      },
      {
        label: "What you want from the reader and why it's important",
        name: 'extra3',
        type: 'textarea',
        placeholder:
          'example: I need your feedback on the project plan by Friday',
      },
    ],
  },
  {
    title: 'OKR Generator',
    slug: 'okr-generator',
    description:
      'OKR Generator helps you create Key Results for your objectives',
    icon: ChartBarIcon,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    category: ['Business', 'HR'],
    outputs: 3,
    onlyForPaidPlans: true,
    isFree: true,
    fields: [
      {
        label: 'Objective',
        name: 'keywords',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: Increase revenue by 10%',
      },
    ],
  },
  {
    title: 'KPI Generator',
    slug: 'kpi-generator',
    description:
      'Generate key performance indicators (KPIs) for your business or project',
    icon: ChartBarIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    category: ['Business', 'HR'],
    isFree: true,
    dateAdded: '2024-06-30',
    shouldUseGPT4o: true,
    onlyForPaidPlans: true,
    shouldUseO3Mini: true,
    outputs: 3,
    fields: [
      {
        label: 'Business or project name',
        name: 'keywords',
        type: 'text',
        placeholder: 'e.g., E-commerce Store, Marketing Campaign',
      },
      {
        label: 'Primary goals',
        name: 'extra1',
        type: 'textarea',
        limit: 200,
        placeholder: 'e.g., Increase sales, Improve customer satisfaction',
      },
      {
        label: 'Key metrics to track (optional)',
        name: 'extra2',
        type: 'textarea',
        limit: 200,
        placeholder: 'e.g., Conversion rate, Customer retention rate',
      },
      {
        label: 'Time frame (optional)',
        name: 'extra3',
        placeholder: 'e.g., Monthly, Quarterly',
        type: 'select',
        options: ['Weekly', 'Monthly', 'Quarterly', 'Annually'],
      },
    ],
  },
  {
    title: 'Meeting Invite Generator',
    slug: 'meeting-invite-generator',
    description:
      'Generate a meeting invite that you can use in Google Meet, Zoom, or any other video conferencing tool',
    icon: CalendarIcon,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    shouldUseChatGPTAPI: true,
    category: 'Business',
    isFree: true,
    outputs: 3,
    fields: [
      {
        label: 'What is the meeting about?',
        name: 'keywords',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: Discuss the new marketing campaign',
      },
    ],
  },
  {
    title: 'Metaphor Creator',
    slug: 'metaphor-creator',
    description: 'Generate creative metaphors for your writing',
    icon: LightBulbIcon,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    shouldUseChatGPTAPI: true,
    category: ['Writing', 'Creative Writing'],
    isFree: true,
    outputs: 3,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: Exlaining the concept of Generative AI',
      },
    ],
  },
  {
    title: 'Acronym Generator',
    slug: 'acronym-generator',
    description:
      'Generate an acronym for your company, project, event, or anything else',
    icon: SparklesIcon,
    iconForeground: 'text-lime-700',
    iconBackground: 'bg-lime-50',
    shouldUseChatGPTAPI: true,
    category: 'Business',
    outputs: 3,
    isFree: true,
    fields: [
      {
        label: 'What is the topic of the acronym?',
        name: 'keywords',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: a company producing electric cars',
      },
      {
        label: 'Goal of the acronym',
        name: 'extra1',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: to create a catchy name for the company',
      },
    ],
  },
  {
    title: 'Newsletter Name Generator',
    slug: 'newsletter-name-generator',
    description:
      'Generate a name for your newsletter that will attract subscribers',
    isFree: true,
    icon: SparklesIcon,
    iconForeground: 'text-sky-700',
    iconBackground: 'bg-sky-50',
    shouldUseChatGPTAPI: true,
    category: ['Marketing', 'Email & Messages'],
    outputs: 3,
    fields: [
      {
        label: 'What is the topic of your newsletter?',
        name: 'keywords',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: a newsletter about the latest tech news',
      },
      {
        label: 'What are main points you want to cover?',
        name: 'extra1',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: latest tech news, new gadgets, etc.',
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: friendly, funny, cheerful',
        limit: 250,
      },
    ],
  },
  {
    title: 'Newsletter Generator',
    slug: 'newsletter-generator',
    description: 'Generate a newsletter that will attract subscribers',
    icon: BookOpenIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    isFree: true,
    onlyForPaidPlans: true,
    shouldUseO3Mini: true,
    category: ['Marketing', 'Email & Messages'],
    fields: [
      {
        label: 'What do you want to write about?',
        name: 'keywords',
        type: 'textarea',
        limit: 10000,
        placeholder:
          'example: A newsletter about the announcement of a new AI tool. It will help you generate content for your social media posts. Just enter a few keywords and it will generate a post for you. It is very easy to use and will save you a lot of time.',
      },
      {
        label: 'Target Audience (optional)',
        name: 'extra1',
        type: 'text',
        limit: 500,
        placeholder:
          'example: Social media managers, content creators, marketers, etc.',
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: friendly, funny, cheerful',
        limit: 250,
      },
    ],
  },
  {
    title: 'Business Plan Generator',
    slug: 'business-plan-generator',
    description:
      'Generate a business plan for your company that will help you get funding',
    icon: BriefcaseIcon,
    iconForeground: 'text-amber-700',
    iconBackground: 'bg-amber-50',
    shouldUseChatGPTAPI: true,
    category: 'Business',
    outputs: 2,
    onlyForPaidPlans: true,
    isFree: true,
    shouldUseO3Mini: true,
    fields: [
      {
        label: 'Company Name',
        name: 'keywords',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: Easy-Peasy.AI',
      },
      {
        label: 'What is the problem you are solving?',
        name: 'extra1',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: It is hard to find a good name for your company',
      },
      {
        label: 'What is your solution?',
        name: 'extra2',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: We help you generate a name for your company',
      },
      {
        label: 'What is your target market?',
        name: 'extra3',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: Startups, small businesses, etc.',
      },
      {
        label: 'How you plan to reach your target market and make money?',
        name: 'extra4',
        type: 'textarea',
        limit: 500,
        placeholder:
          'example: We will use Google Ads to reach our target market',
      },
    ],
  },
  {
    title: 'Business Proposal Generator',
    slug: 'business-proposal-generator',
    description: 'Generate a business proposal that will impress your clients',
    icon: BriefcaseIcon,
    isFree: true,
    iconForeground: 'text-amber-700',
    iconBackground: 'bg-amber-50',
    shouldUseChatGPTAPI: true,
    category: 'Business',
    outputs: 3,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Client/Company Name',
        name: 'keywords',
        type: 'text',
        limit: 500,
        placeholder: 'e.g., Acme Corporation',
      },
      {
        label: 'Project Description',
        name: 'extra1',
        type: 'textarea',
        limit: 10000,
        placeholder:
          'e.g., Complete website redesign with modern UI/UX, mobile responsiveness, and improved performance...',
      },
      {
        label: 'Budget Range (Optional)',
        name: 'extra2',
        type: 'text',
        placeholder: 'e.g., $10,000 - $15,000',
      },
      {
        label: 'Timeline (Optional)',
        name: 'extra3',
        type: 'text',
        placeholder: 'e.g., 3 months, Q2 2024, 6-8 weeks',
      },
      {
        label: 'Target Audience (Optional)',
        name: 'extra4',
        type: 'text',
        placeholder:
          'e.g., Small businesses, E-commerce retailers, Tech startups',
      },
    ],
  },
  {
    title: 'Grant Proposal Generator',
    slug: 'grant-proposal-generator',
    description: 'Generate a compelling grant proposal for your project',
    icon: BriefcaseIcon,
    iconForeground: 'text-amber-700',
    iconBackground: 'bg-amber-50',
    isFree: true,
    shouldUseChatGPTAPI: true,
    category: 'Business',
    outputs: 3,
    onlyForPaidPlans: true,
    shouldUseO3Mini: true,
    fields: [
      {
        label: 'Project Description',
        name: 'keywords',
        type: 'textarea',
        limit: 2000,
        placeholder:
          'example: A project aimed at improving community facilities',
      },
      {
        label: 'Project Goals',
        name: 'extra1',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: To improve the quality of life in the community',
      },
    ],
  },
  {
    title: 'Letter of Request Generator',
    slug: 'letter-of-request-generator',
    description: 'Generate a formal letter of request for any situation',
    icon: MailIcon,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    shouldUseChatGPTAPI: true,
    category: ['Business', 'Legal'],
    isFree: true,
    outputs: 3,
    onlyForPaidPlans: true,
    shouldUseO3Mini: true,
    fields: [
      {
        label: 'Recipient Name',
        name: 'keywords',
        type: 'text',
        limit: 500,
        placeholder: 'example: John Doe',
      },
      {
        label: 'Request Details',
        name: 'extra1',
        type: 'textarea',
        limit: 500,
        placeholder:
          'example: Request for additional information about the project',
      },
    ],
  },
  {
    title: 'Quiz Generator',
    slug: 'quiz-generator',
    description:
      'Create customized quizzes for educational or entertainment purposes',
    icon: QuestionMarkCircleIcon,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    shouldUseChatGPTAPI: true,
    category: 'Education',
    shouldUseGPT4o: true,
    isFree: true,
    onlyForPaidPlans: true,
    dateAdded: '2024-06-30',
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'text',
        limit: 500,
        placeholder: 'e.g., World History, Pop Culture',
      },
      {
        label: 'Number of questions',
        name: 'extra1',
        type: 'text',
        limit: 100,
        placeholder: 'e.g., 10',
      },
      {
        label: 'Difficulty level',
        name: 'extra2',
        type: 'text',
        limit: 100,
        placeholder: 'e.g., Easy, Medium, Hard',
      },
      {
        label: 'Background Information (optional)',
        name: 'extra3',
        type: 'textarea',
        limit: 10000,
        placeholder:
          'Any additional context or specific requirements for the quiz. You can also provide the context for the questions here.',
      },
    ],
  },
  {
    title: 'Quiz Answer Assistant',
    slug: 'quiz-answer-assistant',
    description: 'Get help analyzing and answering quiz questions accurately',
    icon: QuestionMarkCircleIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    category: 'Education',
    isFree: true,
    fields: [
      {
        label: 'Question',
        name: 'keywords',
        type: 'textarea',
        limit: 10000,
        placeholder:
          'example: Who is the author of the book "To Kill a Mockingbird"?',
      },
    ],
  },
  {
    title: 'Lesson Plan Generator',
    slug: 'lesson-plan-generator',
    description:
      'Create a custom lesson plan for your class that will help you teach your students',
    icon: BookOpenIcon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    shouldUseChatGPTAPI: true,
    shouldUseO3Mini: true,
    category: 'Education',
    outputs: 3,
    isFree: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Subject',
        name: 'keywords',
        type: 'text',
        limit: 500,
        placeholder: 'example: Math',
      },
      {
        label: 'Grade Level',
        name: 'extra1',
        type: 'text',
        limit: 500,
        placeholder: 'example: 5th grade',
      },
      {
        label: 'I want my students to learn:',
        name: 'extra2',
        type: 'textarea',
        limit: 5000,
        placeholder: 'example: how to solve quadratic equations',
        enableMicrophone: true,
      },
      {
        label: 'Standards',
        name: 'extra3',
        type: 'select',
        options: [
          'State Standards',
          'Common Core State Standards (CCSS)',
          'Next Generation Science Standards (NGSS)',
          'International Society for Technology in Education (ISTE) Standards',
          'National Council of Teachers of Mathematics (NCTM) Standards',
          'National Standards for Arts Education',
          'College and Career Readiness Standards (CCRS)',
          'English Language Proficiency (ELP) Standards',
          'Social-Emotional Learning (SEL) Standards',
          'International Baccalaureate (IB) Standards',
          'Advanced Placement (AP) Standards',
          'None',
        ],
        placeholder: 'Select education standards',
      },
      {
        label: 'Additional Instructions (optional)',
        name: 'extra4',
        type: 'textarea',
        placeholder:
          'example: Include a call to action, Include a verse from the Bible, etc.',
        limit: 1000,
      },
    ],
  },
  {
    title: 'Reading Passage Generator',
    slug: 'reading-passage-generator',
    description:
      'Generate custom reading passages with adjustable complexity and topics',
    icon: BookOpenIcon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    category: 'Education',
    outputs: 3,
    onlyForPaidPlans: true,
    isFree: true,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'text',
        limit: 5000,
        placeholder: 'example: Ancient Egypt, Space Exploration, Ocean Life',
      },
      {
        label: 'Reading Level',
        name: 'extra1',
        type: 'text',
        limit: 500,
        placeholder: 'example: 5th grade, Middle School, High School',
      },
      {
        label: 'Length (in words)',
        name: 'extra2',
        type: 'text',
        limit: 500,
        placeholder: 'example: 300 words',
      },
      {
        label: 'Special Requirements (optional)',
        name: 'extra3',
        type: 'text',
        limit: 500,
        placeholder:
          'example: Include vocabulary words, Add comprehension questions',
      },
    ],
  },
  {
    title: 'Social Stories Generator',
    slug: 'social-stories-generator',
    description:
      'Create personalized social stories with illustrations and narrated videos to help explain social situations and behaviors',
    icon: UserGroupIcon,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    category: 'Education',
    outputs: 1,
    isFree: true,
    onlyForPaidPlans: true,
    generateImage: true,
    generateImageDefaultOn: true,
    imageModel: 'Nano Banana 2 Lite',
    fields: [
      {
        label: "Child's Name",
        name: 'extra1',
        type: 'text',
        limit: 100,
        placeholder: 'example: Alex',
      },
      {
        label: 'Grade Level or Age',
        name: 'keywords',
        type: 'text',
        limit: 500,
        placeholder: 'example: 2nd grade, or age 7',
      },
      {
        label: 'Social Situation, event or activity',
        name: 'extra2',
        type: 'textarea',
        limit: 5000,
        placeholder:
          'example: Going to the school cafeteria for lunch for the first time',
      },
      {
        label: 'Special Considerations (optional)',
        name: 'extra3',
        type: 'text',
        limit: 1000,
        placeholder:
          'example: Autism, ADHD, Anxiety, Specific triggers or interests',
      },
    ],
  },
  {
    title: 'Homework Assignment Generator',
    slug: 'homework-assignment-generator',
    description: 'Generate a custom homework assignment for your students',
    icon: BookOpenIcon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    shouldUseChatGPTAPI: true,
    category: 'Education',
    outputs: 3,
    isFree: true,
    fields: [
      {
        label: 'Subject',
        name: 'keywords',
        type: 'text',
        limit: 500,
        placeholder: 'example: Math',
      },
      {
        label: 'Grade Level',
        name: 'extra1',
        type: 'text',
        limit: 500,
        placeholder: 'example: 5th grade',
      },
      {
        label: 'Topic',
        name: 'extra2',
        type: 'text',
        limit: 500,
        placeholder: 'example: Multiplication',
      },
      {
        label: 'Bloom Verb Selection (optional)',
        name: 'extra3',
        type: 'text',
        limit: 500,
        placeholder: 'example: Create, Evaluate, Analyze, Apply, Understand',
      },
    ],
  },
  {
    title: 'Report Card Comment Generator',
    slug: 'report-card-comment-generator',
    description: 'Generate constructive and professional report card comments',
    icon: DocumentTextIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    category: 'Education',
    isFree: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Grade Level',
        name: 'keywords',
        type: 'text',
        limit: 100,
        placeholder: 'example: 8th grade',
      },
      {
        label: 'Subject',
        name: 'extra1',
        type: 'text',
        limit: 100,
        placeholder: 'example: Mathematics',
      },
      {
        label: 'Academic Performance',
        name: 'extra2',
        type: 'text',
        limit: 1500,
        placeholder:
          'example: Excels in problem-solving, struggles with geometry',
      },
      {
        label: 'Behavior and Participation',
        name: 'extra3',
        type: 'textarea',
        limit: 1500,
        placeholder:
          'example: Active participant in class discussions, occasionally disruptive',
      },
      {
        label: 'Areas for Improvement',
        name: 'extra4',
        type: 'textarea',
        limit: 1500,
        placeholder:
          'example: Needs to focus on time management and homework completion',
      },
    ],
  },
  {
    title: 'Rubric Generator',
    slug: 'rubric-generator',
    description: 'Generate a custom rubric for your assignments',
    icon: BookOpenIcon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    shouldUseChatGPTAPI: true,
    onlyForPaidPlans: true,
    shouldUseGPT4o: true,
    shouldUseO3Mini: true,
    category: 'Education',
    outputs: 1,
    isFree: true,
    fields: [
      {
        label: 'Subject',
        name: 'keywords',
        type: 'text',
        limit: 500,
        placeholder: 'example: Science',
      },
      {
        label: 'Grade Level',
        name: 'extra1',
        type: 'text',
        limit: 500,
        placeholder: 'example: 5th grade',
      },
      {
        label: 'Topic',
        name: 'extra2',
        type: 'text',
        limit: 500,
        placeholder: 'example: Engineering',
      },
      {
        label: 'Assignment Title',
        name: 'extra3',
        type: 'text',
        limit: 500,
        placeholder: 'example: a STEM experiment, the "egg drop challenge"',
      },
      {
        label: 'Assignment Description',
        name: 'extra4',
        type: 'textarea',
        limit: 500,
        placeholder:
          'example: Use specific materials to design and build a container to protect the egg from cracking as it is dropped from increasingly higher heights',
      },
    ],
  },
  {
    title: 'Classroom Jeopardy Generator',
    slug: 'classroom-jeopardy-generator',
    description: 'Generate a custom Classroom Jeopardy game for your students',
    icon: SparklesIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    loginPreset: 'chat',
    category: 'Education',
    outputs: 1,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Subject',
        name: 'keywords',
        type: 'text',
        limit: 500,
        placeholder: 'example: History',
      },
      {
        label: 'Grade Level',
        name: 'extra1',
        type: 'text',
        limit: 500,
        placeholder: 'example: 7th grade',
      },
      {
        label: 'Number of Categories',
        name: 'extra2',
        type: 'text',
        limit: 500,
        placeholder: 'example: 5',
      },
      {
        label: 'Specific Categories',
        name: 'extra3',
        type: 'text',
        limit: 500,
        placeholder:
          'example: World History, Geography, Civics, Economics, Culture',
      },
      {
        label: 'Number of Questions per Category',
        name: 'extra4',
        type: 'text',
        limit: 500,
        placeholder: 'example: 5',
      },
      {
        label: 'Additional Instructions (optional)',
        name: 'extra5',
        type: 'textarea',
        limit: 10000,
        placeholder:
          'example: Use Hawaii Department of Education Rules and Regulations for Students',
      },
    ],
  },
  {
    title: 'Worksheet Generator',
    slug: 'worksheet-generator',
    description: 'Create a custom worksheet for your students',
    icon: DocumentTextIcon,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    shouldUseChatGPTAPI: true,
    category: 'Education',
    outputs: 1,
    isFree: false,
    shouldUseO3Mini: true,
    onlyForPaidPlans: true,
    isWorksheetVisual: true,
    fields: [
      {
        label: 'Grade Level and Subject',
        name: 'keywords',
        type: 'text',
        limit: 500,
        placeholder: 'example: 7th grade Math',
      },
      {
        label: 'Topic',
        name: 'extra1',
        type: 'text',
        limit: 500,
        placeholder: 'example: Algebraic Expressions',
      },
      {
        label: 'Specific Content',
        name: 'extra2',
        type: 'text',
        limit: 500,
        placeholder: 'example: Simplifying Expressions, Evaluating Expressions',
      },
      {
        label: 'Additional Instructions (optional)',
        name: 'extra3',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Halloween themed, include 10 problems, add a bonus challenge question',
      },
    ],
  },
  {
    title: 'Icebreaker Question Generator',
    slug: 'icebreaker-question-generator',
    description:
      'Generate engaging icebreaker questions for meetings, events, or social gatherings.',
    icon: ChatAlt2Icon,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    shouldUseChatGPTAPI: true,
    category: 'Business',
    isFree: true,
    fields: [
      {
        label: 'Context',
        name: 'keywords',
        type: 'text',
        placeholder:
          'example: Team meeting, Networking event, First day of class',
        limit: 1000,
      },
      {
        label: 'Additional Context (optional)',
        name: 'extra1',
        type: 'textarea',
        placeholder:
          'example: Professional setting, Mixed age group, Remote team, etc.',
        limit: 1000,
      },
    ],
  },

  {
    title: 'Exit Ticket Generator',
    slug: 'exit-ticket-generator',
    description:
      'Create quick end-of-lesson exit ticket questions aligned to your objective to check understanding.',
    icon: ClipboardCheckIcon,
    iconForeground: 'text-teal-700',
    iconBackground: 'bg-teal-50',
    shouldUseChatGPTAPI: true,
    category: 'Education',
    isFree: true,
    fields: [
      {
        label: 'Lesson Topic or Objective',
        name: 'keywords',
        type: 'text',
        placeholder:
          'example: Solving linear equations; Identify main idea in informational texts',
        limit: 500,
      },
      {
        label: 'Grade Level and Subject',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: 7th Grade Math; 5th Grade ELA',
        limit: 200,
      },
      {
        label: 'Key Concepts Covered (optional)',
        name: 'extra2',
        type: 'textarea',
        placeholder:
          'example: One-step vs two-step equations; balance method; checking solutions',
        limit: 1000,
      },
      {
        label: 'Question Format',
        name: 'extra3',
        type: 'select',
        options: [
          'Mix',
          'Short Answer',
          'Multiple Choice',
          'Open-Ended Reflection',
        ],
        placeholder: 'Select preferred format for exit tickets',
      },
      {
        label: 'Number of Questions',
        name: 'length',
        type: 'radio',
        initialValue: 'Medium',
        radioOptions: [
          { name: 'Short', checked: false },
          { name: 'Medium', checked: true },
          { name: 'Long', checked: false },
        ],
        placeholder: 'Short (3-4), Medium (5-6), Long (7-10)',
      },
    ],
  },
  {
    title: 'IEP Goal Generator',
    slug: 'iep-goal-generator',
    description:
      'Generate a specific, measurable, attainable, reasonable, and time-bound IEP goal for your student',
    icon: AcademicCapIcon,
    iconForeground: 'text-teal-700',
    iconBackground: 'bg-teal-50',
    shouldUseChatGPTAPI: true,
    category: 'Education',
    outputs: 1,
    onlyForPaidPlans: true,
    isFree: true,
    fields: [
      {
        label: 'Goal Area',
        name: 'keywords',
        type: 'text',
        limit: 500,
        placeholder: 'example: Reading Comprehension',
      },
      {
        label: 'Grade Level',
        name: 'extra1',
        type: 'text',
        limit: 500,
        placeholder: 'example: 3rd grade',
      },
      {
        label: 'Topic',
        name: 'extra2',
        type: 'text',
        limit: 500,
        placeholder: 'example: Understanding Main Idea',
      },
      {
        label: 'Specific Description',
        name: 'extra3',
        type: 'textarea',
        limit: 1000,
        placeholder: 'example: Identify the main idea in a paragraph',
      },
      {
        label: 'Measurable and Time-bound Description',
        name: 'extra4',
        type: 'text',
        limit: 500,
        placeholder: 'example: With 80% accuracy by the end of the semester',
      },
    ],
  },
  {
    title: 'Syllabus Generator',
    slug: 'syllabus-generator',
    description: 'Generate a comprehensive syllabus for your course in minutes',
    icon: BookOpenIcon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    shouldUseChatGPTAPI: true,
    category: 'Education',
    isFree: true,
    onlyForPaidPlans: true,
    shouldUseO3Mini: true,
    outputs: 3,
    fields: [
      {
        label: 'Course Name',
        name: 'keywords',
        type: 'text',
        limit: 500,
        placeholder: 'example: Introduction to Programming',
      },
    ],
  },
  {
    title: 'Learning Objective Generator',
    slug: 'learning-objective-generator',
    description:
      'Create clear and concise learning objectives for your educational content.',
    icon: AcademicCapIcon,
    iconForeground: 'text-teal-700',
    iconBackground: 'bg-teal-50',
    shouldUseChatGPTAPI: true,
    category: 'Education',
    dateAdded: '2024-06-30',
    isFree: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Subject or topic',
        name: 'keywords',
        type: 'text',
        placeholder: 'e.g., Algebra, World War II',
        limit: 100,
      },
      {
        label: 'Target audience',
        name: 'extra1',
        type: 'text',
        placeholder: 'e.g., High school students, Adult learners',
        limit: 100,
      },
      {
        label: 'Key skills or knowledge to be acquired',
        name: 'extra2',
        type: 'textarea',
        placeholder:
          'e.g., Solving quadratic equations, Understanding causes of the war',
        limit: 1000,
      },
      {
        label: 'Assessment method (optional)',
        name: 'extra3',
        type: 'textarea',
        placeholder: 'e.g., Multiple-choice quiz, Essay',
        limit: 200,
      },
    ],
  },
  {
    title: 'Hook Generator',
    slug: 'hook-generator',
    //irresistible hook
    description: 'Create an irresistible hook to grab your reader’s attention',
    icon: FaFish,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    // component: GenericComponent,
    isFree: true,
    shouldUseChatGPTAPI: true,
    category: ['Marketing', 'Content'],
    fields: [
      {
        label: 'What is your topic?',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: how to optimize your website for search engines',
        limit: 1000,
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: friendly, funny, cheerful',
        limit: 250,
      },
    ],
  },
  {
    title: 'DJ Name Generator',
    slug: 'dj-name-generator',
    description: 'Generate unique and catchy DJ names for your music career.',
    icon: MusicNoteIcon,
    iconForeground: 'text-rose-700',
    iconBackground: 'bg-rose-50',
    shouldUseChatGPTAPI: true,
    isFree: true,
    image:
      'https://media.easy-peasy.ai/5923c502-6528-4332-9e6c-07eff5545919/0ac53b5b-dc12-4c78-a1d1-74ad1270cd51.png',
    category: ['Audio & Music', 'Personal & Fun'],
    onlyForPaidPlans: true,
    outputs: 3,
    fields: [
      {
        label: 'Genre',
        name: 'keywords',
        type: 'text',
        limit: 500,
        placeholder: 'example: House, EDM, Techno, Trance',
      },
      {
        label: 'Desired Vibe',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: Energetic, Chill, Funky',
        limit: 250,
      },
      {
        label: 'Your name (optional)',
        name: 'extra1',
        type: 'text',
        limit: 500,
        placeholder: 'example: John',
      },
    ],
  },
  {
    title: 'Band Name Generator',
    slug: 'band-name-generator',
    description: 'Generate unique and catchy band names for your music career.',
    icon: MusicNoteIcon,
    iconForeground: 'text-rose-700',
    iconBackground: 'bg-rose-50',
    shouldUseChatGPTAPI: true,
    isFree: true,
    category: ['Audio & Music', 'Personal & Fun'],
    outputs: 3,
    fields: [
      {
        label: 'Genre',
        name: 'keywords',
        type: 'text',
        limit: 500,
        placeholder:
          'example: Rock, Metal, Punk, Indie, Emo, Country, K-Pop, Jazz, Blues, etc.',
      },
      {
        label: 'Background Information (optional)',
        name: 'extra1',
        type: 'textarea',
        limit: 500,
        placeholder:
          'example: We are a band from Sweden. We play rock music. We are four members. We have been playing together for 5 years.',
      },
    ],
  },
  {
    title: 'Movie Recommender',
    slug: 'movie-recommender',
    description:
      'Get AI-powered movie recommendations based on your favorite movies and TV shows',
    icon: VideoCameraIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    category: 'Personal & Fun',
    isFree: true,
    outputs: 3,
    fields: [
      {
        label: 'Movies or TV shows you like',
        name: 'keywords',
        type: 'text',
        limit: 500,
        placeholder: 'example: The Matrix, The Lord of the Rings, etc.',
      },
      {
        label: 'Preferred genre',
        name: 'extra1',
        type: 'text',
        limit: 500,
        placeholder: 'example: Action, Comedy, Drama, etc.',
      },
    ],
  },
  {
    title: 'Grocery List Writer',
    slug: 'grocery-list-writer',
    description:
      'Generate a grocery list based on your dietary preferences and needs',
    icon: ShoppingCartIcon,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    shouldUseChatGPTAPI: true,
    category: ['Personal & Fun', 'Health & Wellness'],
    isFree: true,
    outputs: 3,
    fields: [
      {
        label: 'Dietary Preferences',
        name: 'keywords',
        type: 'text',
        limit: 500,
        placeholder: 'example: Any, Vegan, Gluten-free, etc.',
      },
      {
        label: 'Number of people',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: 4',
      },
      {
        label: 'Number of days',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: 7',
      },
    ],
  },
  {
    title: 'Recipe Generator',
    slug: 'recipe-generator',
    description: 'Generate a recipe based on your preferences and ingredients',
    icon: BeakerIcon,
    iconForeground: 'text-red-700',
    iconBackground: 'bg-red-50',
    shouldUseChatGPTAPI: true,
    category: 'Personal & Fun',
    isFree: true,
    onlyForPaidPlans: true,
    outputs: 3,
    fields: [
      {
        label: 'Preferred cuisine',
        name: 'keywords',
        type: 'text',
        limit: 500,
        placeholder: 'example: Italian, Chinese, Mexican, etc.',
      },
      {
        label: 'Ingredients you have',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: chicken, tomatoes, basil, etc.',
      },
      {
        label: 'Dietary restrictions',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: vegan, gluten-free, dairy-free, etc.',
      },
    ],
  },
  {
    title: 'Lottery Number Generator',
    slug: 'lottery-number-generator',
    description: 'Generate a set of random lottery numbers',
    icon: FaDice,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    shouldUseChatGPTAPI: true,
    category: 'Personal & Fun',
    isFree: true,
    onlyForPaidPlans: true,
    outputs: 3,
    fields: [
      // One-click by design: the game select carries the rules (count, range,
      // bonus balls) so a visitor can hit Generate without typing anything —
      // the old required "Number of numbers" / "Maximum number" text fields
      // converted at 34% vs the select-first Powerball form's 57%.
      {
        // Rules are encoded in the option labels so the model knows each
        // game's format without a lookup table. Digit games (4D, Thai) get a
        // dedicated branch in the prompt: ordered digits, repeats allowed.
        label: 'Lottery game',
        name: 'keywords',
        type: 'select',
        placeholder: 'example: Powerball',
        options: [
          'Powerball (US, 5 of 69 + Powerball 1-26)',
          'Mega Millions (US, 5 of 70 + Mega Ball 1-25)',
          'Toto (Singapore, 6 of 49)',
          '4D (Singapore/Malaysia, four digits 0000-9999)',
          'EuroMillions (5 of 50 + 2 Lucky Stars 1-12)',
          'EuroJackpot (5 of 50 + 2 of 12)',
          'UK Lotto (6 of 59)',
          'La Primitiva (Spain, 6 of 49 + Reintegro 0-9)',
          'Lotto 6aus49 (Germany, + Superzahl 0-9)',
          'Loto (France, 5 of 49 + Chance 1-10)',
          'SuperEnalotto (Italy, 6 of 90)',
          'Mega-Sena (Brazil, 6 of 60)',
          'Mark Six (Hong Kong, 6 of 49)',
          'Loto 6 (Japan, 6 of 43)',
          'Lotto 6/45 (Korea)',
          'Vietlott Power 6/55 (Vietnam)',
          'Ultra Lotto 6/58 (Philippines)',
          'Oz Lotto (Australia, 7 of 47)',
          'Powerball (Australia, 7 of 35 + 1 of 20)',
          'Thai Lottery (six digits)',
          'Lotto 6/49',
          'Custom rules',
        ],
        initialValue: 'Powerball (US, 5 of 69 + Powerball 1-26)',
      },
      {
        label: 'Number of tickets',
        name: 'extra2',
        type: 'select',
        placeholder: 'example: 1',
        options: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'],
        initialValue: '3',
      },
      {
        label: 'Winning pattern analysis',
        name: 'extra4',
        type: 'checkbox',
        placeholder: '',
        initialValue: true,
        description:
          'Analyzes 10+ years of official lottery data to identify statistical patterns and trends',
      },
      {
        label: 'Custom rules (optional)',
        name: 'extra1',
        type: 'text',
        limit: 300,
        placeholder: 'example: pick 7 numbers from 1-35, plus 1 bonus 1-20',
        description:
          'Only needed if you chose "Custom rules" — or to tweak the selected game.',
      },
      {
        label: 'Lucky numbers to include (optional)',
        name: 'extra5',
        type: 'text',
        limit: 100,
        placeholder: 'example: 7,11,21',
        description:
          'Enter your lucky numbers to include if possible, separated by commas.',
      },
      {
        label: 'Excluded numbers or ranges (optional)',
        name: 'extra3',
        type: 'text',
        limit: 100,
        placeholder: 'example: 13, 10-19',
        description:
          'Numbers or ranges to leave out, separated by commas. Example: 13, 10-19',
      },
    ],
  },
  {
    title: 'Powerball Number Generator',
    slug: 'powerball-number-generator',
    description: 'Generate random Powerball lottery numbers',
    icon: FaDice,
    iconForeground: 'text-red-700',
    iconBackground: 'bg-red-50',
    shouldUseChatGPTAPI: true,
    category: 'Personal & Fun',
    isFree: true,
    onlyForPaidPlans: true,
    outputs: 3,
    fields: [
      {
        label: 'Number of tickets',
        name: 'keywords',
        type: 'select',
        placeholder: 'example: 1',
        options: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'],
      },
      {
        label: 'Winning pattern analysis',
        name: 'extra1',
        type: 'checkbox',
        placeholder: '',
        initialValue: true,
        description:
          'Analyzes 10+ years of official lottery data to identify statistical patterns and trends',
      },
      {
        label: 'Excluded numbers (optional)',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: 1,13,25',
        description: 'Enter specific numbers to exclude, separated by commas.',
      },
      {
        label: 'Lucky numbers to include (optional)',
        name: 'extra3',
        type: 'text',
        placeholder: 'example: 7,11,21',
        description:
          'Enter your lucky numbers to include if possible, separated by commas.',
      },
    ],
  },
  {
    title: 'Mega Millions Number Generator',
    slug: 'mega-millions-number-generator',
    description: 'Generate random Mega Millions lottery numbers',
    icon: FaDice,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    shouldUseChatGPTAPI: true,
    category: 'Personal & Fun',
    isFree: true,
    onlyForPaidPlans: true,
    outputs: 3,
    fields: [
      {
        label: 'Number of tickets',
        name: 'keywords',
        type: 'select',
        placeholder: 'example: 1',
        options: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'],
      },
      {
        label: 'Winning pattern analysis',
        name: 'extra1',
        type: 'checkbox',
        placeholder: '',
        initialValue: true,
        description:
          'Analyzes 10+ years of official lottery data to identify statistical patterns and trends',
      },
      {
        label: 'Excluded numbers (optional)',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: 1,13,25',
        description: 'Enter specific numbers to exclude, separated by commas.',
      },
      {
        label: 'Lucky numbers to include (optional)',
        name: 'extra3',
        type: 'text',
        placeholder: 'example: 7,11,21',
        description:
          'Enter your lucky numbers to include if possible, separated by commas.',
      },
    ],
  },
  {
    title: 'Dialogue Generator',
    slug: 'dialogue-generator',
    description: 'Generate a dialogue based on your inputs',
    icon: ChatAlt2Icon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    category: 'Creative Writing',
    isFree: true,
    outputs: 3,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Discussing a movie',
      },
      {
        label: 'Character 1 Name',
        name: 'extra1',
        type: 'text',
        limit: 500,
        placeholder: 'example: John',
      },
      {
        label: 'Character 2 Name',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: Mary',
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: friendly, funny, cheerful',
      },
    ],
  },
  {
    title: 'Monologue Generator',
    slug: 'monologue-generator',
    description:
      'Generate compelling monologues for any character or situation',
    icon: ChatAlt2Icon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    shouldUseChatGPTAPI: true,
    category: 'Creative Writing',
    isFree: true,
    outputs: 1,
    fields: [
      {
        label: 'Character Name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: John Smith',
      },
      {
        label: 'Situation/Context',
        name: 'extra1',
        type: 'textarea',
        placeholder: 'example: A heartbroken man reflecting on lost love',
      },
      {
        label: 'Character Traits',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: cynical, passionate, introspective',
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: dramatic, emotional, contemplative',
      },
    ],
  },
  {
    title: 'Speech Writer',
    slug: 'speech-writer',
    description: 'Generate a compelling speech for any occasion',
    icon: SpeakerphoneIcon,
    iconForeground: 'text-red-700',
    iconBackground: 'bg-red-50',
    category: ['Personal & Fun', 'Business'],
    isFree: true,
    onlyForPaidPlans: true,
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    fields: [
      {
        label: 'Occasion',
        name: 'extra3',
        type: 'text',
        placeholder: 'example: wedding, graduation, birthday',
        limit: 1000,
      },
      {
        label: 'Audience',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: friends, family, colleagues, general public',
        limit: 1000,
      },
      {
        label: 'Speech Length',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: 5 minutes, 10 minutes, 15 minutes',
        limit: 1000,
      },
      {
        label: 'What do you want to say?',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: thank you for coming to my wedding',
        limit: 10000,
        enableMicrophone: true,
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: inspiring, motivational, serious',
      },
    ],
  },
  {
    title: 'Thank You Note Generator',
    slug: 'thank-you-note-generator',
    description: 'Generate a personalized thank you note',
    icon: ThumbUpIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    generateImage: true,
    generateImageDefaultOn: true,
    generateImageAspectRatio: '2:3',
    category: 'Personal & Fun',
    isFree: true,
    onlyForPaidPlans: true,
    shouldUseO3Mini: true,
    outputs: 1,
    fields: [
      {
        label: 'Recipient’s name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: John',
      },
      {
        label: 'What you’re thanking them for',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: the birthday gift, helping me move, dinner',
      },
      {
        label: 'Your relationship to them (optional)',
        name: 'extra4',
        type: 'text',
        placeholder: 'example: Friend, Colleague, Aunt',
      },
      {
        label: 'Anything specific to mention? (optional)',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: it really made my week',
      },
      {
        label: 'From (your name, optional)',
        name: 'extra5',
        type: 'text',
        placeholder: 'example: Sarah',
      },
      {
        label: 'Card style',
        name: 'extra6',
        type: 'select',
        placeholder: 'example: Elegant, Floral, Cute & playful',
        options: ['Elegant', 'Floral', 'Cute & playful', 'Modern minimalist'],
        initialValue: 'Elegant',
        description: 'Choose the visual look of your card.',
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'example: short, medium, long',
        radioOptions: [
          { name: 'Short', checked: true },
          { name: 'Medium', checked: false },
          { name: 'Long', checked: false },
        ],
      },
    ],
  },
  {
    title: 'Compliment Generator',
    slug: 'compliment-generator',
    description: 'Generate unique and personalized compliments',
    icon: ThumbUpIcon,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    shouldUseChatGPTAPI: true,
    category: 'Personal & Fun',
    onlyForPaidPlans: true,
    isFree: true,
    fields: [
      {
        label: 'Recipient Name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: John',
      },
      {
        label: 'What do you want to say?',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: You are a great friend',
      },
    ],
  },
  {
    title: 'Progress Note Generator',
    searchKeywords: ['psychotherapy', 'therapy', 'mental'],
    slug: 'progress-note-generator',
    hideWebSearch: true,
    useGemini: true,
    description:
      'Generate progress notes for tracking and documenting client progress in therapy sessions.',
    icon: PencilIcon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    shouldUseChatGPTAPI: true,
    category: 'Health & Wellness',
    isFree: true,
    // shouldUseGPT4o: true,
    // shouldUseO3Mini: true,
    onlyForPaidPlans: false,
    useOpenAI: true,
    defaultOutputs: 1,
    showExamples: true,
    fields: [
      {
        label: 'Session Summary',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          "example: Discussed the client's progress in managing anxiety.\nThe session transcript can also be added here.",
        limit: 100000,
        enableMicrophone: true,
      },
      {
        label: 'Goals Achieved',
        name: 'extra2',
        type: 'textarea',
        placeholder:
          'example: Client successfully implemented coping strategies',
        limit: 10000,
        enableMicrophone: true,
      },
      {
        label: 'Next Steps',
        name: 'extra3',
        type: 'textarea',
        placeholder:
          'example: Assign homework to practice relaxation techniques',
        limit: 10000,
        enableMicrophone: true,
      },
      {
        label: 'Format',
        name: 'extra4',
        type: 'select',
        options: ['None', 'SOAP', 'DAP', 'BIRP', 'GIRP', 'PIE'],

        placeholder: 'example: SOAP',
      },
      {
        label: 'Additional Instructions',
        name: 'extra1',
        type: 'textarea',
        placeholder: 'example: Make the note concise and to the point',
        limit: 2500,
        description: 'It will help the AI to generate a more accurate note.',
        enableMicrophone: true,
      },
    ],
  },
  {
    title: 'SOAP Note Generator',
    slug: 'soap-note-generator',
    hideWebSearch: true,
    useGemini: true,
    description:
      'Generate SOAP notes for tracking and documenting client progress.',
    icon: PencilIcon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    category: 'Health & Wellness',
    isFree: true,
    useOpenAI: true,
    // shouldUseO3Mini: true,
    onlyForPaidPlans: false,
    defaultOutputs: 1,
    showExamples: true,
    fields: [
      {
        label: 'Shorthand notes or a description of the case',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'example: Client: male, Age: 25, Diagnosis: Anxiety. His anxiety has been increasing over the past few weeks.\nSocial: lives with his parents, works as a software engineer.\nMedical: no significant medical history.\nGoals: to reduce anxiety and improve sleep.',
        limit: 100000,
        enableMicrophone: true,
      },
      {
        label: 'Additional Instructions',
        name: 'extra1',
        type: 'textarea',
        placeholder: 'example: Make the note concise and to the point',
        limit: 2500,
        description: 'It will help the AI to generate a more accurate note.',
        enableMicrophone: true,
      },
    ],
  },
  {
    title: 'DAP Note Generator',
    slug: 'dap-note-generator',
    hideWebSearch: true,
    useGemini: true,
    description:
      'Turning Session Shorthand into Structured DAP Notes Seamlessly.',
    icon: PencilIcon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    category: 'Health & Wellness',
    isFree: true,
    useOpenAI: true,
    // shouldUseO3Mini: true,
    onlyForPaidPlans: false,
    defaultOutputs: 1,
    showExamples: true,
    fields: [
      {
        label: 'Shorthand notes or a description of the case',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'example: Client: male, Age: 25, Diagnosis: Anxiety. His anxiety has been increasing over the past few weeks.\nSocial: lives with his parents, works as a software engineer.\nMedical: no significant medical history.\nGoals: to reduce anxiety and improve sleep.',
        limit: 100000,
        enableMicrophone: true,
      },
      {
        label: 'Additional Instructions',
        name: 'extra1',
        type: 'textarea',
        placeholder: 'example: Make the note concise and to the point',
        limit: 2500,
        description: 'It will help the AI to generate a more accurate note.',
        enableMicrophone: true,
      },
    ],
  },
  {
    title: 'Therapy Intake Generator',
    slug: 'therapy-intake',
    hideWebSearch: true,
    useGemini: true,
    description:
      'Generate a comprehensive therapy intake note including client background, presenting concerns, history, risks, and goals.',
    icon: ClipboardCheckIcon,
    iconForeground: 'text-teal-700',
    iconBackground: 'bg-teal-50',
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    category: 'Health & Wellness',
    isFree: true,
    onlyForPaidPlans: true,
    dateAdded: '2025-08-12',
    showExamples: true,
    fields: [
      {
        label: 'Client Overview',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'Age, gender, referral source, living situation, occupation, session date',
        limit: 10000,
        enableMicrophone: true,
      },
      {
        label: 'Presenting Concerns',
        name: 'extra1',
        type: 'textarea',
        placeholder: 'Primary symptoms, duration, triggers, functional impact',
        limit: 10000,
        enableMicrophone: true,
      },
      {
        label: 'Relevant History',
        name: 'extra2',
        type: 'textarea',
        placeholder:
          'Mental health history, medical conditions/medications, substance use, prior treatment',
        limit: 10000,
        enableMicrophone: true,
      },
      {
        label: 'Risk and Safety',
        name: 'extra3',
        type: 'textarea',
        placeholder:
          'Suicidal/homicidal ideation, self-harm, access to means, protective factors, mandated reporting concerns',
        limit: 10000,
        enableMicrophone: true,
      },
      {
        label: 'Goals and Initial Plan',
        name: 'extra4',
        type: 'textarea',
        placeholder:
          'Client goals, preliminary diagnosis or clinical impressions, recommended frequency, next steps',
        limit: 10000,
        enableMicrophone: true,
      },
      {
        label: 'Documentation Style',
        name: 'tone',
        type: 'select',
        options: [
          'Comprehensive Intake',
          'Brief Intake',
          'Insurance-ready',
          'Trauma-informed',
          'CBT-oriented',
        ],
        placeholder: 'Select a style for the intake note',
      },
    ],
  },
  {
    title: 'Treatment Plan Generator',
    slug: 'treatment-plan-generator',
    hideWebSearch: true,
    useGemini: true,
    description:
      'Generate comprehensive mental health treatment plans with diagnosis, goals, interventions and more.',
    icon: ClipboardListIcon,
    iconForeground: 'text-teal-700',
    iconBackground: 'bg-teal-50',
    shouldUseChatGPTAPI: true,
    category: 'Health & Wellness',
    shouldUseGPT4o: true,
    // shouldUseO3Mini: true,
    onlyForPaidPlans: false,
    isFree: true,
    dateAdded: '2025-01-17',
    showExamples: true,
    fields: [
      {
        label: 'Diagnosis & Symptoms',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'Enter DSM-5 diagnosis and presenting symptoms, including frequency, severity and intensity\n\nExample:\nPrimary Diagnosis: Major Depressive Disorder, Recurrent, Moderate (F33.1)\nPresenting symptoms include:\n- Depressed mood daily for past 3 months\n- Significant weight loss (10 lbs in 2 months)\n- Insomnia (difficulty falling asleep 5-6 nights/week)\n- Fatigue and loss of energy nearly every day\n- Diminished ability to concentrate at work\n- Feelings of worthlessness and excessive guilt\nSymptoms cause significant distress in social and occupational functioning.',
        limit: 10000,
        enableMicrophone: true,
      },
      {
        label: 'Presenting Problems',
        name: 'extra1',
        type: 'textarea',
        placeholder:
          'Describe behavioral examples that reflect diagnostic criteria\n\nExample:\n- Sleeping only 4-5 hours per night\n- Missing 2-3 workdays per month due to low motivation\n- Social withdrawal from friends and family\n- Decreased appetite leading to irregular meals\n- Difficulty completing work tasks due to poor concentration\n- Frequent crying spells (4-5 times per week)\n- Negative self-talk and self-criticism throughout the day',
        limit: 10000,
        enableMicrophone: true,
      },
      {
        label: 'Barriers to Treatment',
        name: 'extra2',
        type: 'textarea',
        placeholder:
          'Describe bio/psycho/social stressors impacting treatment\n\nExample:\n- Limited social support system due to recent relocation\n- Financial constraints affecting ability to afford medications\n- High-stress work environment with 60+ hour weeks\n- History of childhood trauma affecting trust in therapeutic relationships\n- Irregular work schedule making consistent appointment attendance difficult\n- Limited experience with therapy and initial skepticism about effectiveness\n- Chronic pain condition (fibromyalgia) affecting energy levels',
        limit: 10000,
        enableMicrophone: true,
      },
      {
        label: 'Treatment Goals',
        name: 'extra3',
        type: 'textarea',
        placeholder:
          'Describe desired outcomes and what client wants to achieve\n\nExample:\n- Develop healthy sleep habits and establish consistent sleep schedule\n- Improve work attendance and performance\n- Rebuild social connections and support network\n- Establish regular eating patterns and self-care routine\n- Learn cognitive behavioral techniques to manage negative thoughts\n- Develop stress management and relaxation skills\n- Return to previous level of functioning at work and home',
        limit: 10000,
        enableMicrophone: true,
      },
      {
        label: 'Current Progress',
        name: 'extra4',
        type: 'textarea',
        placeholder:
          'Describe changes and progress since treatment began\n\nExample:\nAfter 6 weeks of treatment:\n- Started sleep hygiene routine, now averaging 5-6 hours sleep\n- Attended 5/6 scheduled therapy sessions\n- Began journaling daily to track mood and thoughts\n- Started taking prescribed antidepressant consistently\n- Joined workplace wellness program\n- Made initial attempts to reconnect with two close friends\n- Implemented 10-minute daily mindfulness practice',
        limit: 10000,
        enableMicrophone: true,
      },
      {
        label: 'Medical Necessity',
        name: 'tone',
        type: 'select',
        options: [
          'Address Symptoms',
          'Reduce Symptoms',
          'Stabilize Symptoms',
          'Improve Functioning',
          'Maintain Gains',
          'Prevent Decompensation',
          'Prevent Higher Level of Care',
        ],
        placeholder: 'Select medical necessity for treatment',
      },
    ],
  },
  {
    title: 'Clinical Summary Update Generator',
    slug: 'clinical-summary-update-generator',
    hideWebSearch: true,
    description:
      'Generate comprehensive clinical summary updates for ongoing treatment cases, including insurance authorization requests.',
    icon: DocumentTextIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    category: 'Health & Wellness',
    dateAdded: '2025-04-11',
    isFree: true,
    fields: [
      {
        label: 'Client Information',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'Provide basic client information and current treatment context\n\nExample:\nClient: 35-year-old female\nDiagnosis: Major Depressive Disorder, Generalized Anxiety Disorder\nTreatment: Weekly individual therapy (CBT approach), started 3 months ago\nMedication: Sertraline 50mg daily (started 2 months ago)\nPrevious treatment: Brief counseling 2 years ago following divorce\nInsurance: Blue Cross Blue Shield, authorization expires 10/15/2025',
        limit: 10000,
        enableMicrophone: true,
      },
      {
        label: 'Recent Clinical Observations',
        name: 'extra1',
        type: 'textarea',
        placeholder:
          'Describe recent observations, changes in symptoms, and clinical impressions\n\nExample:\n- Reported improved sleep (6-7 hours vs. previous 4-5 hours)\n- Reduced anxiety in social situations but still avoids large gatherings\n- More consistent with daily activities and self-care\n- Mood fluctuations less severe but still present\n- Increased engagement in therapy sessions\n- Started using learned coping strategies between sessions\n- Medication compliance has been consistent',
        limit: 10000,
        enableMicrophone: true,
      },
      {
        label: 'Treatment Progress',
        name: 'extra2',
        type: 'textarea',
        placeholder:
          'Describe progress toward treatment goals and any adjustments needed\n\nExample:\n- Goal 1 (Improve sleep): Moderate progress with sleep hygiene routine\n- Goal 2 (Reduce anxiety): Some progress with exposure exercises\n- Goal 3 (Increase social engagement): Limited progress, needs more focus\n- Added mindfulness practices which client finds helpful\n- Considering family session to address relationship dynamics\n- Medication appears to be providing partial symptom relief',
        limit: 10000,
        enableMicrophone: true,
      },
      {
        label: 'Treatment Plan Updates',
        name: 'extra3',
        type: 'textarea',
        placeholder:
          "Describe any changes to the treatment plan or approach\n\nExample:\n- Continue weekly individual therapy with increased focus on behavioral activation\n- Maintain current medication regimen and monitor side effects\n- Introduce structured problem-solving techniques for work stressors\n- Consider referral for psychiatric consultation if sleep does not improve\n- Add journaling exercise to track mood patterns\n- Schedule check-in with support system (sister) with client's permission",
        limit: 10000,
        enableMicrophone: true,
      },
      {
        label: 'Medical Necessity Justification',
        name: 'extra4',
        type: 'textarea',
        placeholder:
          'Provide justification for continued treatment and insurance authorization\n\nExample:\n- Client continues to experience significant symptoms that impair daily functioning\n- Risk of symptom regression if treatment discontinued prematurely\n- Demonstrated measurable progress but has not yet achieved treatment goals\n- Current level of care is the least restrictive appropriate intervention\n- Client is actively engaged and motivated in treatment process\n- Specific interventions are addressing identified symptoms and functional impairments\n- Requesting authorization for 12 additional sessions to continue progress',
        limit: 10000,
        enableMicrophone: true,
      },
      {
        label: 'Summary Format',
        name: 'tone',
        type: 'select',
        options: [
          'Concise Overview',
          'Detailed Analysis',
          'Progress Report',
          'Treatment Team Update',
          'Insurance Authorization Request',
          'Referral Summary',
          'Discharge Planning',
        ],
        placeholder: 'Select the format for the clinical summary',
      },
    ],
  },
  {
    title: 'ABA Notes Generator',
    slug: 'aba-notes-generator',
    hideWebSearch: true,
    useGemini: true,
    description:
      'Generate comprehensive Applied Behavior Analysis (ABA) session notes.',
    icon: DocumentTextIcon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    category: 'Health & Wellness',
    dateAdded: '2024-06-30',
    isFree: true,
    // shouldUseO3Mini: true,
    onlyForPaidPlans: false,
    showExamples: true,
    fields: [
      {
        label: 'Shorthand notes or a description of the case',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'example: Client: male, Age: 25, Diagnosis: Anxiety. His anxiety has been increasing over the past few weeks.\nSocial: lives with his parents, works as a software engineer.\nMedical: no significant medical history.\nGoals: to reduce anxiety and improve sleep.',
        limit: 50000,
        enableMicrophone: true,
      },
      {
        label: 'Additional Instructions',
        name: 'extra1',
        type: 'textarea',
        placeholder: 'example: Make the note concise and to the point',
        limit: 2500,
        description: 'It will help the AI to generate a more accurate note.',
        enableMicrophone: true,
      },
    ],
  },
  {
    title: 'BIRP Note Generator',
    slug: 'birp-note-generator',
    hideWebSearch: true,
    useGemini: true,
    description:
      'Generate comprehensive BIRP (Behavior, Intervention, Response, Plan) notes for mental health professionals.',
    icon: DocumentTextIcon,
    iconForeground: 'text-teal-700',
    iconBackground: 'bg-teal-50',
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    category: 'Health & Wellness',
    dateAdded: '2024-08-28',
    isFree: true,
    onlyForPaidPlans: false,
    // shouldUseO3Mini: true,
    showExamples: true,
    fields: [
      {
        label: 'Shorthand notes or a description of the case',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'example: Age: 32, Diagnosis: Depression. Session focused on discussing recent job loss and coping strategies.',
        limit: 50000,
        enableMicrophone: true,
      },
      {
        label: 'Additional Instructions',
        name: 'extra1',
        type: 'textarea',
        placeholder:
          "example: Include specific interventions used and client's response to them.",
        limit: 2500,
        description:
          'Provide any specific requirements or focus areas for the BIRP note.',
        enableMicrophone: true,
      },
    ],
  },
  {
    title: 'Therapy Practice Name Generator',
    slug: 'therapy-practice-name-generator',
    description:
      'Generate unique and meaningful names for your therapy practice or mental health clinic.',
    icon: HeartIcon,
    iconForeground: 'text-pink-700',
    iconBackground: 'bg-pink-50',
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    category: ['Health & Wellness', 'Business'],
    isFree: true,
    showExamples: true,
    fields: [
      {
        label: 'Type of therapy or specialization',
        name: 'keywords',
        type: 'text',
        placeholder:
          'example: Cognitive Behavioral Therapy, Family Counseling, Trauma Recovery',
      },
      {
        label: 'Target audience or demographic (optional)',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Children, Couples, Veterans, LGBTQ+',
      },
      {
        label: 'Location or region (optional)',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: Downtown Seattle, Rural Midwest, Online',
      },
      {
        label: 'Values or approach (optional)',
        name: 'extra3',
        type: 'textarea',
        limit: 500,
        placeholder:
          'example: Holistic, Evidence-based, Compassionate care, Mindfulness-focused',
      },
      {
        label: 'Tone of voice',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: professional, compassionate, uplifting',
      },
    ],
  },
  {
    title: 'Apology Generator',
    slug: 'apology-generator',
    description: 'Generate sincere and thoughtful apologies for any situation.',
    icon: PencilIcon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    shouldUseChatGPTAPI: true,
    category: 'Personal & Fun',
    shouldUseGPT4o: true,
    isFree: true,
    onlyForPaidPlans: true,
    outputs: 3,
    fields: [
      {
        label: 'Who are you apologizing to?',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: John Doe',
      },
      {
        label: 'What are you apologizing for?',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Missing a meeting',
      },
      {
        label: 'Any additional context or details?',
        name: 'extra2',
        type: 'textarea',
        placeholder: 'example: I was stuck in traffic',
      },
      {
        label: 'Format of the apology',
        name: 'extra3',
        type: 'select',
        options: ['Email', 'Message', 'Letter', 'Speech'],
        placeholder: 'example: Email',
      },
    ],
  },
  {
    title: 'Excuse Generator',
    slug: 'excuse-generator',
    description:
      'Generate believable and creative excuses for various situations.',
    icon: LightBulbIcon,
    iconForeground: 'text-amber-700',
    iconBackground: 'bg-amber-50',
    shouldUseChatGPTAPI: true,
    category: 'Personal & Fun',
    isFree: true,
    onlyForPaidPlans: true,
    outputs: 3,
    fields: [
      {
        label: 'What do you need an excuse for?',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Missing a deadline',
      },
      {
        label: 'Who is the excuse for?',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: My boss, teacher, friend',
      },
      {
        label: 'How serious should the excuse be?',
        name: 'extra2',
        type: 'select',
        options: ['Very serious', 'Somewhat serious', 'Casual', 'Humorous'],
        placeholder: 'example: Somewhat serious',
      },
      {
        label: 'Additional Context (optional)',
        name: 'extra3',
        type: 'textarea',
        placeholder:
          'example: I need to use this excuse in a professional setting',
        limit: 1000,
      },
    ],
  },
  {
    title: 'SOP Generator',
    slug: 'sop-generator',
    description:
      'Generate Standard Operating Procedures for your business or organization.',
    icon: DocumentTextIcon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    shouldUseChatGPTAPI: true,
    category: ['HR', 'Business'],
    isFree: true,
    // shouldUseO3Mini: true,
    outputs: 3,
    onlyForPaidPlans: true,
    shouldUseO3Mini: true,
    fields: [
      {
        label: 'What is the procedure for?',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Data backup',
      },
      {
        label: 'Any specific steps or details?',
        name: 'extra1',
        type: 'textarea',
        placeholder: 'example: Step 1: Identify the data to backup',
        limit: 50000,
      },
      {
        label: 'Any additional context or details?',
        name: 'extra2',
        type: 'textarea',
        placeholder: 'example: This procedure is for weekly backup',
        limit: 5000,
      },
    ],
  },
  {
    title: 'Riddle Generator',
    slug: 'riddle-generator',
    description: 'Generate fun and challenging riddles for all ages.',
    icon: PuzzleIcon,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    shouldUseChatGPTAPI: true,
    category: ['Personal & Fun', 'Education'],
    isFree: false,
    outputs: 2,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Math riddle',
      },
      {
        label: 'Any specific context or details?',
        name: 'extra1',
        type: 'textarea',
        placeholder: 'example: Suitable for kids',
      },
    ],
  },
  {
    title: 'Riddle Solver',
    slug: 'riddle-solver',
    description: 'Solve riddles and get detailed explanations for the answers.',
    icon: PuzzleIcon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    shouldUseChatGPTAPI: true,
    category: ['Personal & Fun', 'Education'],
    isFree: false,
    outputs: 1,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Riddle',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: What has keys but can’t open locks?',
        limit: 2000,
      },
      {
        label: 'Upload an image (optional)',
        name: 'extra1',
        type: 'file',
        placeholder: 'example: riddle_image.png',
        accept: 'image/*',
        acceptDescription: 'jpg, png, webp',
      },
      {
        label: 'Any hints or context? (optional)',
        name: 'extra2',
        type: 'textarea',
        placeholder: 'example: It is a musical instrument.',
        limit: 1000,
      },
    ],
  },
  {
    title: 'SMART Goal Generator',
    slug: 'smart-goal-generator',
    description:
      'Generate SMART (Specific, Measurable, Achievable, Relevant, Time-bound) goals.',
    icon: FlagIcon,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    shouldUseChatGPTAPI: true,
    category: 'Business',
    isFree: true,
    outputs: 3,
    // shouldUseO3Mini: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'What do you want to achieve?',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: Increase online sales',
        limit: 10000,
        enableMicrophone: true,
      },
    ],
  },
  {
    title: 'Workout Plan Generator',
    slug: 'workout-plan-generator',
    description:
      'Generate a personalized workout plan based on your fitness goals.',
    icon: CalendarIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    category: ['Health & Wellness', 'Personal & Fun'],
    isFree: true,
    onlyForPaidPlans: true,
    // shouldUseO3Mini: true,
    outputs: 1,
    fields: [
      {
        label: 'What are your fitness goals?',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Lose weight, Gain muscle',
      },
      {
        label: 'Gender',
        name: 'extra1',
        type: 'select',
        placeholder: 'Gender',
        options: ['Male', 'Female', 'Other'],
      },
      {
        label: 'Training method',
        name: 'extra2',
        type: 'select',
        placeholder: 'Training method',
        options: [
          'Resistance training',
          'Resistance + cardio',
          'Meal plan only / custom routine',
        ],
      },
      {
        label: 'Workout type',
        name: 'extra3',
        type: 'select',
        placeholder: 'Workout type',
        options: ['Weighted', 'Bodyweight', 'No equipment'],
      },
      {
        label: 'Strength level',
        name: 'extra4',
        placeholder: 'Strength level',
        type: 'select',
        options: ['Beginner', 'Intermediate', 'Advanced'],
      },
    ],
  },
  {
    title: 'Meal Plan Generator',
    slug: 'meal-plan-generator',
    description:
      'Create a free personalized meal plan in seconds — tailored to your diet, calorie goal and allergies, with every meal planned for you.',
    icon: CalendarIcon,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    shouldUseChatGPTAPI: true,
    category: ['Health & Wellness', 'Personal & Fun'],
    isFree: true,
    outputs: 1,
    fields: [
      {
        label: 'Dietary Preferences',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Vegan, Gluten-free, High-protein',
      },
      {
        label: 'Meal frequency',
        name: 'extra1',
        type: 'select',
        placeholder: 'Meal frequency',
        options: [
          '3 meals a day',
          '3 meals + snacks',
          '5 small meals a day',
          'Intermittent fasting',
        ],
        initialValue: '3 meals a day',
      },
      {
        label: 'Calorie goal (optional)',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: 2000 kcal',
      },
      {
        label: 'Allergies or restrictions (optional)',
        name: 'extra3',
        type: 'text',
        placeholder: 'example: No nuts, Gluten-free, Dairy-free',
      },
    ],
  },
  {
    title: 'HTML&CSS Code Generator',
    slug: 'html-css-code-generator',
    description: 'Generate HTML and CSS code snippets with ease.',
    icon: CodeIcon,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    shouldUseChatGPTAPI: true,
    loginPreset: 'chat',
    category: 'Code & Data',
    outputs: 1,
    fields: [
      {
        label: 'What kind of HTML/CSS code do you want to generate?',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: Responsive navigation bar',
        limit: 2500,
      },
      {
        label: 'CSS Framework',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Bootstrap, Tailwind CSS',
      },
    ],
  },
  {
    title: 'Python Code Generator',
    slug: 'python-code-generator',
    description: 'Generate Python code snippets with ease.',
    icon: CodeIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    loginPreset: 'chat',
    category: 'Code & Data',
    outputs: 1,
    fields: [
      {
        label: 'What kind of Python code do you want to generate?',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Data analysis, API integration',
      },
      {
        label: 'Python Framework (optional)',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Django, Flask',
      },
    ],
  },
  {
    title: 'Java Code Generator',
    slug: 'java-code-generator',
    description: 'Generate Java code snippets with ease.',
    icon: CodeIcon,
    iconForeground: 'text-orange-700',
    iconBackground: 'bg-orange-50',
    shouldUseChatGPTAPI: true,
    loginPreset: 'chat',
    category: 'Code & Data',
    outputs: 1,
    fields: [
      {
        label: 'What kind of code do you want to generate?',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: REST API, Database connection',
      },
      {
        label: 'Framework (optional)',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Spring Boot, Jakarta EE',
      },
    ],
  },
  {
    title: 'Regex Generator',
    slug: 'regex-generator',
    description: 'Generate regular expressions with ease.',
    icon: CodeIcon,
    iconForeground: 'text-red-700',
    iconBackground: 'bg-red-50',
    shouldUseChatGPTAPI: true,
    category: 'Code & Data',
    outputs: 1,
    hideLanguageSelector: true,
    fields: [
      {
        label: 'What kind of regular expression do you want to generate?',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Email validation, URL parsing',
      },
      {
        label: 'Regex Flavor (optional)',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: JavaScript, Python, Java',
      },
    ],
  },
  {
    title: 'Bedtime Story Generator',
    slug: 'bedtime-story-generator',
    description: 'Generate soothing bedtime stories for kids.',
    icon: BookOpenIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    isFree: true,
    category: ['Creative Writing', 'Personal & Fun'],
    outputs: 3,
    generateImage: true,
    imageModel: 'Seedream 4.5',
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: About a dragon',
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'example: friendly, funny, cheerful',
        initialValue: 'Short',
        radioOptions: [
          { name: 'Short', checked: true },
          { name: 'Medium', checked: false },
          { name: 'Long', checked: false },
        ],
      },
    ],
  },
  {
    title: 'Lullaby Generator',
    slug: 'lullaby-generator',
    description: 'Generate soothing lullabies for kids.',
    icon: MusicNoteIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    category: ['Creative Writing', 'Personal & Fun'],
    outputs: 1,
    isFree: true,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Moon, Stars, Night',
      },
    ],
  },
  {
    title: 'Character Name Generator',
    slug: 'character-name-generator',
    description: 'Generate unique character names with ease.',
    image:
      'https://media.easy-peasy.ai/703d7fcb-a9b0-464c-ab94-c9314a27a37a/9624cad4-d359-470e-8da1-9af309f8cf98.png',
    icon: SparklesIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    category: 'Creative Writing',
    outputs: 1,
    isFree: true,
    fields: [
      {
        label: 'What type of character name do you want to generate?',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Fantasy, Sci-fi, Modern',
      },
      {
        label: 'Gender',
        name: 'extra1',
        type: 'select',
        placeholder: 'Gender',
        options: ['Male', 'Female', 'Non-binary', 'Any'],
      },
    ],
  },
  {
    title: 'Character Description Generator',
    slug: 'character-description-generator',
    description: 'Generate detailed descriptions for your characters.',
    icon: UserIcon,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    shouldUseChatGPTAPI: true,
    category: 'Creative Writing',
    isFree: true,
    outputs: 1,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Character name or concept',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Elara — a rogue starship mechanic',
        description:
          'No name yet? Just describe the idea and we’ll flesh it out.',
      },
      {
        label: 'Traits (optional)',
        name: 'extra1',
        type: 'textarea',
        placeholder: 'example: Brave, Intelligent, Mysterious',
      },
      {
        label: 'Background (optional)',
        name: 'extra2',
        type: 'textarea',
        placeholder: 'example: Born in a small village...',
      },
    ],
  },
  {
    title: 'Character Backstory Generator',
    slug: 'character-backstory-generator',
    description: 'Create rich and compelling backstories for your characters',
    icon: UserCircleIcon,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    shouldUseChatGPTAPI: true,
    category: 'Creative Writing',
    isFree: true,
    outputs: 1,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Character Name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Sarah Chen',
      },
      {
        label: 'Setting/Time Period',
        name: 'extra1',
        type: 'text',
        placeholder:
          'example: Modern Day Seattle, Medieval Fantasy, Future Mars Colony',
      },
      {
        label: 'Key Life Events',
        name: 'extra2',
        type: 'textarea',
        placeholder:
          'example: Lost parents at young age, Graduated top of class, Survived a disaster',
      },
      {
        label: 'Personality Traits',
        name: 'extra3',
        type: 'text',
        placeholder: 'example: Ambitious, Reserved, Compassionate',
      },
      {
        label: 'Goals/Motivations',
        name: 'extra4',
        type: 'textarea',
        placeholder:
          'example: Seeking revenge, Finding lost sibling, Building a business empire',
      },
    ],
  },
  {
    title: 'Merge Texts',
    slug: 'text-merger',
    hideWebSearch: true,
    description: 'Combine two separate texts into one cohesive piece.',
    icon: PlusIcon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    shouldUseChatGPTAPI: true,
    onlyForPaidPlans: true,
    isFree: true,
    category: 'Writing',
    outputs: 1,
    fields: [
      {
        label: 'Enter the first text',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'Enter the first text here',
        limit: 15000,
      },
      {
        label: 'Enter the second text',
        name: 'extra1',
        type: 'textarea',
        placeholder: 'Enter the second text here',
        limit: 15000,
      },
    ],
  },
  {
    title: 'SWOT Analysis Generator',
    slug: 'swot-analysis-generator',
    description: 'Generate a SWOT analysis for your business or project.',
    icon: SearchIcon,
    iconForeground: 'text-red-700',
    iconBackground: 'bg-red-50',
    shouldUseChatGPTAPI: true,
    category: 'Business',
    isFree: true,
    onlyForPaidPlans: true,
    shouldUseO3Mini: true,
    shouldUseGPT4o: true,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'text',
        placeholder:
          'example: My Startup to automate grocery shopping with drones',
      },
    ],
  },
  {
    title: 'Conversation Starter Generator',
    slug: 'conversation-starter-generator',
    description: 'Generate interesting conversation starters.',
    icon: ChatAlt2Icon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    category: 'Personal & Fun',
    isFree: true,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Travel, Food, Movies',
        limit: 1000,
      },
    ],
  },

  {
    title: 'Announcement Generator',
    slug: 'announcement-generator',
    description: 'Generate an announcement for your event or news.',
    icon: FaBullhorn,
    shouldUseGPT4o: true,
    onlyForPaidPlans: true,
    shouldUseO3Mini: true,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    shouldUseChatGPTAPI: true,
    category: ['Business', 'Personal & Fun'],
    isFree: true,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'text',
        placeholder:
          'example: New product launch, Company picnic, Team building event, etc.',
        limit: 1000,
      },
      {
        label: 'Background Information (optional)',
        name: 'extra1',
        type: 'textarea',
        placeholder: 'example: Date, Time, Location, RSVP details',
        limit: 1000,
      },
    ],
  },
  {
    title: 'Text Tone Analyzer',
    slug: 'text-tone-analyzer',
    hideWebSearch: true,
    description: 'Analyze the tone of a text.',
    icon: SearchIcon,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    shouldUseChatGPTAPI: true,
    category: 'Writing',
    outputs: 1,
    fields: [
      {
        label: 'Text',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: I am feeling great today!',
        limit: 5000,
      },
    ],
  },
  {
    title: 'Headline Analyzer',
    slug: 'headline-analyzer',
    description:
      'Check headlines, blog titles, and more for quality and clickability.',
    icon: FaHeading,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    category: ['Marketing', 'Content'],
    isFree: true,
    onlyForPaidPlans: true,
    outputs: 1,
    fields: [
      {
        label: 'Headline',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: How to write a good headline',
        limit: 500,
      },
    ],
  },
  {
    title: 'Legalese Translator',
    slug: 'legalese-translator',
    description:
      'Helps simplify and explain contract language for non-lawyers.',
    icon: DocumentTextIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    category: ['Legal', 'Business'],
    outputs: 1,
    onlyForPaidPlans: true,
    shouldUseGPT4o: true,
    isFree: true,
    dateAdded: '2024-05-09',
    fields: [
      {
        label: 'Text',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: Enter the legal text you want to translate',
        limit: 20000,
      },
    ],
  },
  {
    title: 'Legal Memo Generator',
    slug: 'legal-memo-generator',
    description:
      'Generate professional legal memos and briefs with structured analysis.',
    icon: DocumentTextIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    category: ['Legal', 'Business'],
    outputs: 1,
    shouldUseGPT4o: true,
    isFree: true,
    onlyForPaidPlans: true,
    dateAdded: '2024-05-09',
    fields: [
      {
        label: 'Legal Issue',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: Intellectual Property, Contract Dispute, etc.',
        limit: 10000,
      },
      {
        label: 'Relevant Facts',
        name: 'extra1',
        type: 'textarea',
        placeholder: 'example: Patent details, Contract details, etc.',
        limit: 10000,
      },
      {
        label: 'Jurisdiction',
        name: 'extra2',
        type: 'text',
        placeholder: 'e.g., California, Federal',
        limit: 100,
      },
    ],
  },
  {
    title: 'Text Simplifier',
    slug: 'text-simplifier',
    hideWebSearch: true,
    description: 'Simplify your text for better understanding.',
    icon: DocumentTextIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseAnthropic: true,
    shouldUseChatGPTAPI: true,
    category: 'Writing',
    outputs: 1,
    isFree: true,
    fields: [
      {
        label: 'Text',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: Enter the text you want to simplify',
        limit: 20000,
      },
    ],
  },
  {
    title: 'Letter Generator',
    slug: 'letter-generator',
    description:
      'Write any letter in seconds with the free AI letter writer — formal, personal, cover letters, complaints and thank-yous, perfectly worded and ready to send.',
    icon: DocumentTextIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    category: ['Writing', 'Personal & Fun'],
    isFree: true,
    outputs: 1,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Content',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: I would like to express my gratitude for...',
        limit: 5000,
      },
    ],
  },
  {
    title: 'Synopsis Generator',
    slug: 'synopsis-generator',
    description: 'Generate a concise and appealing synopsis.',
    icon: FaParagraph,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    category: ['Creative Writing', 'Writing'],
    shouldUseAnthropic: true,
    dateAdded: '2024-05-09',
    isFree: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Content',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'example: Enter the content for which you want to generate a synopsis',
        limit: 200000,
      },
    ],
  },
  {
    title: 'Roast AI',
    slug: 'roast-bot',
    description:
      'AI-powered roasting tool. Generate humorous and witty roasts.',
    icon: FaRobot,
    iconForeground: 'text-red-700',
    iconBackground: 'bg-red-50',
    shouldUseChatGPTAPI: true,
    category: 'Personal & Fun',
    outputs: 1,
    dateAdded: '2024-05-09',
    fields: [
      {
        label: 'Upload an image (optional)',
        name: 'extra1',
        type: 'file',
        placeholder: 'example: image.png',
        accept: 'image/*',
        acceptDescription: 'jpg, png, webp',
        description: 'Upload an image to roast.',
      },
      {
        label: 'Additional Instructions',
        name: 'keywords',
        type: 'textarea',
        limit: 500,
        placeholder: 'example: Roast my friend John or roast my landing page',
      },
    ],
  },
  {
    title: 'AI Scrambler',
    slug: 'ai-scrambler',
    hideWebSearch: true,
    description:
      'Rewrites provided text to create an original and natural sounding version.',
    icon: FaRobot,
    iconForeground: 'text-red-700',
    iconBackground: 'bg-red-50',
    shouldUseChatGPTAPI: true,
    category: 'Writing',
    outputs: 1,
    isFree: true,
    onlyForPaidPlans: true,
    dateAdded: '2024-05-09',
    fields: [
      {
        label: 'Content',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: Enter the content you want to rewrite',
        limit: 50000,
      },
    ],
  },
  {
    title: 'Career Path Generator',
    slug: 'career-path-generator',
    description:
      'AI-powered tool to suggest potential career paths based on interests and skills.',
    icon: AcademicCapIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    isFree: true,
    category: ['Career', 'Education'],
    outputs: 1,
    dateAdded: '2024-05-09',
    fields: [
      {
        label: 'Interests',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: I love coding and designing',
      },
      {
        label: 'Skills',
        name: 'extra1',
        type: 'textarea',
        placeholder: 'example: JavaScript, UI/UX Design',
      },
    ],
  },
  {
    title: 'Study Guide Maker',
    slug: 'study-guide-maker',
    description: 'Generate comprehensive study guides with AI',
    icon: DocumentTextIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    category: 'Education',
    isFree: true,
    shouldUseGPT4o: true,
    outputs: 1,
    dateAdded: '2024-05-09',
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: History of the Roman Empire',
        limit: 500,
      },
    ],
  },
  {
    title: 'Sermon Generator',
    slug: 'sermon-generator',
    description:
      'Generate sermons that are inspiring, thought-provoking, and spiritually uplifting.',
    icon: DocumentTextIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    category: 'Personal & Fun',
    isFree: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: The importance of faith',
        limit: 500,
      },
      {
        label: 'Denomination or Tradition',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Baptist, Methodist, Catholic',
        limit: 500,
      },
      // additional instructions
      {
        label: 'Additional Instructions (optional)',
        name: 'extra2',
        type: 'textarea',
        placeholder:
          'example: Include a call to action, Include a verse from the Bible, etc.',
        limit: 1000,
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'example: friendly, funny, cheerful',
        initialValue: 'Short',
        radioOptions: [
          { name: 'Short', checked: true },
          { name: 'Medium', checked: false },
          { name: 'Long', checked: false },
        ],
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'text',
        placeholder: 'example: inspirational, comforting, challenging',
        limit: 500,
      },
    ],
  },
  {
    title: 'Khutbah Generator',
    slug: 'khutbah-generator',
    description:
      'Generate Islamic Friday sermons (khutbah) with Quranic verses, Hadith references, and spiritual guidance.',
    icon: DocumentTextIcon,
    iconForeground: 'text-emerald-700',
    iconBackground: 'bg-emerald-50',
    shouldUseChatGPTAPI: true,
    category: 'Personal & Fun',
    isFree: true,
    shouldUseO3Mini: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: The importance of patience (Sabr) in Islam',
        limit: 500,
      },
      {
        label: 'Islamic Tradition',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Sunni, Shia, General Islamic',
        limit: 500,
      },
      {
        label: 'Khutbah Type',
        name: 'extra3',
        type: 'radio',
        placeholder: 'Type of khutbah',
        initialValue: 'Friday (Jummah)',
        radioOptions: [
          { name: 'Friday (Jummah)', checked: true },
          { name: 'Eid al-Fitr', checked: false },
          { name: 'Eid al-Adha', checked: false },
          { name: 'Nikah (Wedding)', checked: false },
          { name: 'General', checked: false },
        ],
      },
      {
        label: 'Additional Instructions (optional)',
        name: 'extra2',
        type: 'textarea',
        placeholder:
          'example: Include specific Surah references, mention contemporary issues, etc.',
        limit: 1000,
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'Length of the khutbah',
        initialValue: 'Medium',
        radioOptions: [
          { name: 'Short', checked: false },
          { name: 'Medium', checked: true },
          { name: 'Long', checked: false },
        ],
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'text',
        placeholder: 'example: inspirational, reflective, educational',
        limit: 500,
      },
    ],
  },
  {
    title: 'Prophecy Generator',
    slug: 'prophecy-generator',
    description:
      'Generate prophetic messages or visions for spiritual inspiration and guidance',
    icon: LightBulbIcon,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    shouldUseChatGPTAPI: true,
    category: 'Personal & Fun',
    isFree: true,
    onlyForPaidPlans: true,
    shouldUseO3Mini: true,
    fields: [
      {
        label: 'Theme or Subject',
        name: 'keywords',
        type: 'text',
        placeholder:
          'example: Hope for the future, Divine guidance, Spiritual awakening',
        limit: 1000,
      },
      {
        label: 'Spiritual Tradition',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: Christian, Jewish, Islamic, New Age',
        limit: 500,
      },
      {
        label: 'Additional Context (optional)',
        name: 'extra2',
        type: 'textarea',
        placeholder:
          'example: For a community facing challenges, For personal guidance',
        limit: 1000,
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'example: friendly, funny, cheerful',
        initialValue: 'Medium',
        radioOptions: [
          { name: 'Short', checked: false },
          { name: 'Medium', checked: true },
          { name: 'Long', checked: false },
        ],
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'text',
        placeholder: 'example: Mystical, Encouraging, Symbolic, Revelatory',
        limit: 500,
      },
    ],
  },
  {
    title: 'Prayer Generator',
    slug: 'prayer-generator',
    description:
      'Create heartfelt and meaningful prayers for various occasions and intentions',
    icon: DocumentTextIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    category: 'Personal & Fun',
    isFree: true,
    outputs: 1,
    fields: [
      {
        label: 'Occasion or Intention',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Gratitude, Healing, Guidance',
        limit: 500,
      },
      {
        label: 'Specific Details (optional)',
        name: 'extra1',
        type: 'textarea',
        placeholder: 'Any specific details or context for the prayer',
        limit: 1000,
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'example: friendly, funny, cheerful',
        initialValue: 'Short',
        radioOptions: [
          { name: 'Short', checked: true },
          { name: 'Medium', checked: false },
          { name: 'Long', checked: false },
        ],
      },
    ],
  },
  {
    title: 'Rhyme Generator',
    slug: 'rhyme-generator',
    description: 'Effortlessly generate rhymes for your poetry or songwriting.',
    icon: DocumentTextIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    category: ['Creative Writing', 'Audio & Music'],
    isFree: true,
    onlyForPaidPlans: true,
    outputs: 1,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'example: love',
        limit: 5000,
      },
    ],
  },
  {
    title: 'Word Count Tool',
    slug: 'word-count',
    hideWebSearch: true,
    description:
      'Use our free word count tool to count the number of words and characters in your text.',
    icon: DocumentTextIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    category: 'Writing',
    isFree: true,
    hideDashoard: true,
    landingPageSlug: '/free-tools/word-count-tool',
    fields: [],
    isAbsoluteURL: true,
  },
  {
    title: 'Case Converter Tool',
    slug: 'case-converter',
    hideWebSearch: true,
    description:
      'Use our free case converter tool to convert the case of your text.',
    icon: DocumentTextIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    category: 'Writing',
    landingPageSlug: '/free-tools/case-converter',
    hideDashoard: true,
    isFree: true,
    fields: [],
    isAbsoluteURL: true,
  },
  {
    title: 'Investment Policy Statement Generator',
    slug: 'investment-policy-statement-generator',
    description:
      'Create a personalized investment policy statement to guide your financial decisions.',
    icon: DocumentTextIcon,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    shouldUseChatGPTAPI: true,
    category: 'Business',
    isFree: true,
    dateAdded: '2024-06-30',
    shouldUseGPT4o: true,
    fields: [
      {
        label: 'Investment goals',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'e.g., Retirement, Education fund',
        limit: 200,
      },
      {
        label: 'Risk tolerance',
        name: 'extra1',
        type: 'select',
        options: ['Low', 'Moderate', 'High'],
        placeholder: 'Select risk tolerance',
      },
      {
        label: 'Time horizon',
        name: 'extra2',
        type: 'text',
        placeholder: 'e.g., 10 years, 20 years',
        limit: 50,
      },
      {
        label: 'Asset allocation preferences (optional)',
        name: 'extra3',
        type: 'textarea',
        placeholder: 'e.g., 60% stocks, 30% bonds, 10% cash',
        limit: 200,
      },
      {
        label: 'Special considerations (optional)',
        name: 'extra4',
        type: 'textarea',
        placeholder: 'e.g., Ethical investing, Tax considerations',
        limit: 200,
      },
    ],
  },
  {
    title: 'Sales Email Generator',
    slug: 'sales-email-generator',
    description:
      'Create personalized sales emails to engage potential clients and boost your sales.',
    icon: MailIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    category: ['Email & Messages', 'Marketing'],
    dateAdded: '2024-06-30',
    isFree: true,
    shouldUseGPT4o: true,
    fields: [
      {
        label: 'Product or service name',
        name: 'keywords',
        type: 'text',
        placeholder: 'e.g., Premium Software, Consulting Services',
        limit: 100,
      },
      {
        label: 'Key benefits or features',
        name: 'extra1',
        type: 'textarea',
        placeholder: 'e.g., Increased productivity, Cost savings',
        limit: 200,
      },
      {
        label: "Recipient's name (optional)",
        name: 'extra4',
        type: 'text',
        placeholder: 'e.g., Jane Doe, Mr. Smith',
        limit: 100,
      },
      {
        label: 'Call to action',
        name: 'extra2',
        type: 'text',
        placeholder: 'e.g., Schedule a demo, Get a free trial',
        limit: 100,
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'text',
        placeholder: 'e.g., Professional, Persuasive',
        limit: 50,
      },
    ],
  },
  {
    title: 'Lead Magnet Generator',
    slug: 'lead-magnet-generator',
    description:
      'Create compelling lead magnets to attract and convert potential customers.',
    icon: FaMagnet,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    category: ['Marketing', 'Business'],
    isFree: true,
    dateAdded: '2024-06-30',
    fields: [
      {
        label: 'Target audience',
        name: 'keywords',
        type: 'text',
        placeholder: 'e.g., Small business owners, Fitness enthusiasts',
        limit: 100,
      },
      {
        label: 'Type of lead magnet',
        name: 'extra1',
        type: 'text',
        placeholder: 'e.g., Free eBook, Exclusive Video Content',
        limit: 100,
      },
      {
        label: 'Main benefit or value',
        name: 'extra2',
        type: 'textarea',
        placeholder: 'e.g., Learn how to increase sales by 20% in 30 days',
        limit: 200,
      },
      {
        label: 'Call to action',
        name: 'extra3',
        type: 'text',
        placeholder: 'e.g., Download now, Sign up today',
        limit: 50,
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'text',
        placeholder: 'e.g., Persuasive, Informative',
        limit: 50,
      },
    ],
  },
  {
    title: 'Staff Schedule Generator',
    slug: 'staff-schedule-generator',
    description:
      'Create a detailed staff schedule for your team or organization.',
    icon: CalendarIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    category: ['Business', 'HR'],
    isFree: true,
    shouldUseO3Mini: true,
    onlyForPaidPlans: true,
    dateAdded: '2024-06-30',
    fields: [
      {
        label: 'Department or Team Name',
        name: 'keywords',
        type: 'text',
        placeholder: 'e.g., Marketing, IT Support',
        limit: 500,
      },
      {
        label: 'Number of Staff Members',
        name: 'extra1',
        type: 'text',
        placeholder: 'e.g., 15',
        limit: 3,
      },
      {
        label: 'Shift Duration',
        name: 'extra2',
        type: 'select',
        placeholder: 'e.g., 8 hours',
        options: [
          '8 hours',
          '12 hours',
          '10 hours',
          '6 hours',
          '4 hours',
          'Mixed shift lengths',
        ],
        initialValue: '8 hours',
      },
      {
        label: 'Days of Operation',
        name: 'extra3',
        type: 'select',
        placeholder: 'e.g., Monday to Friday',
        options: [
          'Monday to Friday',
          'Monday to Saturday',
          '7 days a week',
          'Weekends only',
          'Custom (describe in special instructions)',
        ],
        initialValue: 'Monday to Friday',
      },
      {
        label: 'Special Instructions (optional)',
        name: 'extra4',
        type: 'textarea',
        placeholder: 'e.g., Include lunch breaks, Rotate weekend shifts',
        limit: 5000,
      },
    ],
  },
  {
    title: 'Roadmap Generator',
    slug: 'roadmap-generator',
    description:
      'Create a detailed roadmap for your project or product development.',
    icon: ClockIcon,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    category: 'Business',
    isFree: true,
    dateAdded: '2024-06-30',
    fields: [
      {
        label: 'Project or Product Name',
        name: 'keywords',
        type: 'text',
        placeholder: 'e.g., New Mobile App',
        limit: 100,
      },
      {
        label: 'Key Milestones',
        name: 'extra1',
        type: 'textarea',
        placeholder: 'e.g., MVP Launch, User Testing, Final Release',
        limit: 300,
      },
      {
        label: 'Timeline',
        name: 'extra2',
        type: 'textarea',
        placeholder: 'e.g., Q1 2023, Q2 2023, Q3 2023',
        limit: 200,
      },
      {
        label: 'Resources Needed',
        name: 'extra3',
        type: 'textarea',
        placeholder: 'e.g., Development Team, Marketing Budget',
        limit: 300,
      },
      {
        label: 'Potential Risks (optional)',
        name: 'extra4',
        type: 'textarea',
        placeholder: 'e.g., Delays in development, Budget overruns',
        limit: 300,
      },
    ],
  },
  {
    title: 'Event Planner',
    slug: 'event-planner',
    description: 'Plan and organize your events with ease using AI.',
    icon: CalendarIcon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    category: 'Business',
    dateAdded: '2024-06-30',
    isFree: true,
    fields: [
      {
        label: 'Event Name',
        name: 'keywords',
        type: 'text',
        placeholder: 'e.g., Annual Company Meeting, Wedding',
        limit: 100,
      },
      // location
      {
        label: 'Location',
        name: 'extra3',
        type: 'text',
        placeholder: 'e.g., New York City, Virtual',
        limit: 100,
      },
      {
        label: 'Number of Attendees',
        name: 'extra1',
        type: 'text',
        placeholder: 'e.g., 50, 200',
        limit: 10,
      },
      {
        label: 'Special Instructions (optional)',
        name: 'extra2',
        type: 'textarea',
        placeholder: '',
        limit: 500,
      },
    ],
  },
  {
    title: 'Affirmation Generator',
    slug: 'affirmation-generator',
    description:
      'Create personalized affirmations to boost your confidence and positivity.',
    icon: HeartIcon,
    iconForeground: 'text-primary-700',
    iconBackground: 'bg-primary-50',
    category: ['Health & Wellness', 'Personal & Fun'],
    isFree: true,
    dateAdded: '2024-06-30',
    shouldUseGPT4o: true,
    shouldUseChatGPTAPI: true,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'text',
        placeholder: 'e.g., Self-confidence, Career success, Relationships',
        limit: 100,
      },
    ],
  },
  {
    title: 'Complaint Generator',
    slug: 'complaint-generator',
    description:
      'Generate professional and effective complaint letters or emails.',
    icon: ExclamationCircleIcon,
    iconForeground: 'text-red-700',
    iconBackground: 'bg-red-50',
    category: ['Legal', 'Personal & Fun'],
    isFree: true,
    dateAdded: '2024-07-01',
    onlyForPaidPlans: true,
    shouldUseGPT4o: true,
    shouldUseChatGPTAPI: true,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'text',
        placeholder: 'e.g., Defective product, Poor customer service',
        limit: 100,
      },
      {
        label: 'Company or Person',
        name: 'extra1',
        type: 'text',
        placeholder: 'e.g., ABC Corporation, John Doe',
        limit: 100,
      },
      {
        label: 'Desired Outcome',
        name: 'extra2',
        type: 'textarea',
        placeholder: 'e.g., Refund, Replacement, Apology',
        limit: 500,
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'text',
        placeholder: 'e.g., Persuasive, Informative',
        limit: 50,
      },
    ],
  },
  {
    title: 'Answer Generator',
    slug: 'answer-generator',
    description:
      'Generate comprehensive and accurate answers to various questions.',
    icon: QuestionMarkCircleIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    category: 'Education',
    isFree: true,
    dateAdded: '2024-09-12',
    shouldUseGPT4o: true,
    shouldUseChatGPTAPI: true,
    fields: [
      {
        label: 'Question',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'e.g., What are the main causes of climate change?',
        limit: 5000,
      },
      {
        label: 'Context',
        name: 'extra1',
        type: 'textarea',
        placeholder: 'e.g., High school level, Scientific perspective',
        limit: 10000,
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'text',
        placeholder: 'e.g., Academic, Conversational, Simplified',
        limit: 500,
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'example: friendly, funny, cheerful',
        initialValue: 'Short',
        radioOptions: [
          { name: 'Short', checked: true },
          { name: 'Medium', checked: false },
          { name: 'Long', checked: false },
        ],
      },
    ],
  },
  {
    title: 'Text Message Generator',
    slug: 'text-message-generator',
    description: 'Generate personalized text messages for various occasions.',
    icon: ChatAlt2Icon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    category: ['Email & Messages', 'Personal & Fun'],
    isFree: true,
    dateAdded: '2024-09-13',
    onlyForPaidPlans: true,
    shouldUseChatGPTAPI: true,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'textarea',
        placeholder: 'e.g., Birthday wish, Apology, Invitation',
        limit: 5000,
        enableMicrophone: true,
      },
      {
        label: 'Recipient',
        name: 'extra1',
        type: 'text',
        placeholder: 'e.g., Friend, Family, Colleague',
        limit: 100,
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'e.g., Casual, Formal, Humorous',
        limit: 500,
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'example: friendly, funny, cheerful',
        initialValue: 'Short',
        radioOptions: [
          { name: 'Short', checked: true },
          { name: 'Medium', checked: false },
          { name: 'Long', checked: false },
        ],
      },
    ],
  },
  {
    title: 'News Article Generator',
    slug: 'news-article-generator',
    description: 'Generate professional news articles on various topics.',
    icon: NewspaperIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    category: 'Content',
    isFree: true,
    dateAdded: '2024-09-14',
    shouldUseChatGPTAPI: true,
    useGemini: true,
    onlyForPaidPlans: true,
    generateImage: true,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'e.g., Recent technological advancements, Local community event',
        limit: 10000,
      },
      {
        label: 'Target Audience',
        name: 'extra1',
        type: 'text',
        placeholder: 'e.g., General public, Industry professionals, Students',
        limit: 100,
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'e.g., Objective, Investigative, Feature',
        limit: 500,
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'Select the desired length of the article',
        initialValue: 'Medium',
        radioOptions: [
          { name: 'Short', checked: false },
          { name: 'Medium', checked: true },
          { name: 'Long', checked: false },
        ],
      },
      {
        label: 'Enable web search',
        name: 'enableWebSearch',
        type: 'checkbox',
        placeholder: 'Use Google Search grounding for current sources',
        initialValue: false,
      },
    ],
  },
  {
    title: 'Haiku Generator',
    slug: 'haiku-generator',
    description: 'Create beautiful Japanese-style haiku poems.',
    icon: PencilIcon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    category: 'Creative Writing',
    isFree: true,
    dateAdded: '2024-09-15',
    shouldUseChatGPTAPI: true,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'text',
        placeholder: 'e.g., Nature, Seasons, Emotions',
        limit: 100,
      },

      {
        label: 'Specific Words to Include (Optional)',
        name: 'extra1',
        type: 'text',
        placeholder: 'e.g., Cherry blossom, Moon, River',
        limit: 500,
      },
      {
        label: 'Language Style',
        name: 'extra2',
        type: 'radio',
        placeholder: 'Select the language style for the haiku',
        initialValue: 'Traditional',
        radioOptions: [
          { name: 'Traditional', checked: true },
          { name: 'Modern', checked: false },
          { name: 'Experimental', checked: false },
        ],
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'text',
        placeholder: 'e.g., Serene, Melancholic, Joyful',
        limit: 150,
      },
    ],
  },
  {
    title: 'Sonnet Generator',
    slug: 'sonnet-generator',
    description:
      'Create elegant 14-line sonnets in traditional Shakespearean or Petrarchan style.',
    icon: PencilIcon,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    category: 'Creative Writing',
    isFree: true,
    dateAdded: '2024-09-15',
    shouldUseChatGPTAPI: true,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'text',
        placeholder: 'e.g., Love, Nature, Time, Beauty',
        limit: 100,
      },
      {
        label: 'Specific Words to Include (Optional)',
        name: 'extra1',
        type: 'text',
        placeholder: 'e.g., Heart, Soul, Eternal, Divine',
        limit: 500,
      },
      {
        label: 'Style',
        name: 'extra2',
        type: 'radio',
        placeholder: 'Select the sonnet style',
        initialValue: 'Shakespearean',
        radioOptions: [
          { name: 'Shakespearean', checked: true },
          { name: 'Petrarchan', checked: false },
          { name: 'Spenserian', checked: false },
        ],
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'text',
        placeholder: 'e.g., Romantic, Philosophical, Melancholic',
        limit: 150,
      },
    ],
  },
  {
    title: 'Limerick Generator',
    slug: 'limerick-generator',
    description: 'Create witty and humorous five-line limericks.',
    icon: PencilAltIcon,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    category: 'Creative Writing',
    isFree: true,
    dateAdded: '2024-09-16',
    shouldUseChatGPTAPI: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'text',
        placeholder: 'e.g., A person, place, or thing',
        limit: 1000,
      },
      {
        label: 'Specific Words to Include (Optional)',
        name: 'extra1',
        type: 'text',
        placeholder: 'e.g., Nantucket, bright, delight',
        limit: 500,
      },
      {
        label: 'Limerick Style',
        name: 'extra2',
        type: 'radio',
        placeholder: 'Select the style for the limerick',
        initialValue: 'Classic',
        radioOptions: [
          { name: 'Classic', checked: true },
          { name: 'Clean', checked: false },
          { name: 'Narrative', checked: false },
        ],
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'text',
        placeholder: 'e.g., Silly, Clever, Absurd',
        limit: 150,
      },
    ],
  },
  {
    title: 'Introduction Paragraph Generator',
    slug: 'introduction-paragraph-generator',
    description:
      'Generate compelling introductions for your essays, articles, or reports.',
    icon: PencilIcon,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    category: ['Content', 'Education'],
    isFree: true,
    dateAdded: '2024-10-05',
    shouldUseChatGPTAPI: true,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'text',
        placeholder:
          'e.g., Renewable energy, Space exploration, Digital privacy',
        limit: 1000,
      },
      {
        label: 'Key Points',
        name: 'extra1',
        type: 'textarea',
        placeholder: 'List the main points you want to introduce',
        limit: 2000,
      },
      {
        label: 'Target Audience',
        name: 'extra2',
        type: 'text',
        placeholder: 'e.g., Students, Professionals, General public',
        limit: 500,
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'text',
        placeholder: 'e.g., Academic, Engaging, Thought-provoking, Informative',
        limit: 150,
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'Select the desired length of the introduction',
        initialValue: 'Medium',
        radioOptions: [
          { name: 'Short', checked: false },
          { name: 'Medium', checked: true },
          { name: 'Long', checked: false },
        ],
      },
    ],
  },
  {
    title: 'Conclusion Generator',
    slug: 'conclusion-generator',
    description:
      'Generate powerful and insightful conclusions for your essays, articles, or reports.',
    icon: LightBulbIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    category: ['Content', 'Education'],
    isFree: true,
    dateAdded: '2024-10-05',
    shouldUseChatGPTAPI: true,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'text',
        placeholder:
          'e.g., Climate change, Artificial Intelligence, Economic inequality',
        limit: 1000,
      },
      {
        label: 'Key Points',
        name: 'extra1',
        type: 'textarea',
        placeholder: 'List the main points discussed in your content',
        limit: 2000,
      },
      {
        label: 'Call to Action (Optional)',
        name: 'extra3',
        type: 'text',
        placeholder:
          'e.g., Further research, Take action, Reflect on the topic',
        limit: 500,
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'text',
        placeholder:
          'e.g., Academic, Persuasive, Reflective, Optimistic, Cautionary',
      },
      {
        label: 'Output length',
        name: 'length',
        type: 'radio',
        placeholder: 'Select the desired length of the conclusion',
        initialValue: 'Medium',
        radioOptions: [
          { name: 'Short', checked: false },
          { name: 'Medium', checked: true },
          { name: 'Long', checked: false },
        ],
      },
    ],
  },
  {
    title: 'Team Name Generator',
    slug: 'team-name-generator',
    description: 'Generate creative and memorable team names for any purpose',
    icon: UsersIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    isFree: true,
    category: ['Personal & Fun', 'Business'],
    outputs: 3,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Type of Team',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Trivia team, Soccer team, Sales team',
        limit: 500,
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'select',
        placeholder: 'example: Fun',
        options: ['Fun', 'Fierce', 'Professional', 'Clever', 'Modern'],
        initialValue: 'Fun',
      },
      {
        label: 'Team Characteristics (optional)',
        name: 'extra1',
        type: 'textarea',
        placeholder: 'example: Competitive, innovative, collaborative',
        limit: 1000,
      },
      {
        label: 'Industry/Field (optional)',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: Technology, Basketball, Marketing',
        limit: 500,
      },
    ],
  },
  {
    title: 'Business Description Generator',
    slug: 'business-description-generator',
    description:
      'Create compelling and professional business descriptions for your company or product',
    icon: BriefcaseIcon,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    shouldUseChatGPTAPI: true,
    isFree: true,
    category: ['Business', 'Marketing'],
    outputs: 2,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Business Name',
        name: 'keywords',
        type: 'text',
        placeholder: 'Enter your business name',
      },
      {
        label: 'Industry',
        name: 'extra1',
        type: 'text',
        placeholder: 'e.g. Technology, Retail, Healthcare',
        limit: 100,
      },
      {
        label: 'Key Products/Services',
        name: 'extra2',
        type: 'textarea',
        placeholder: 'List your main products or services',
        limit: 10000,
      },
      {
        label: 'Target Audience',
        name: 'extra3',
        type: 'textarea',
        placeholder: 'Describe your ideal customers or clients',
        limit: 1000,
      },
      {
        label: 'Unique Value Proposition',
        name: 'extra4',
        type: 'textarea',
        placeholder: 'What makes your business unique?',
        limit: 10000,
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'text',
        placeholder: 'e.g. Professional, Friendly, Technical',
        limit: 100,
      },
    ],
  },
  {
    title: 'AI Documentation Generator',
    slug: 'ai-documentation-generator',
    description: 'Generate comprehensive documentation with AI assistance',
    icon: DocumentIcon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    shouldUseChatGPTAPI: true,
    isFree: true,
    onlyForPaidPlans: true,
    category: 'Business',
    fields: [
      {
        label: 'Project/Component Name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: User Authentication System',
      },
      {
        label: 'Technical Details',
        name: 'extra1',
        type: 'textarea',
        limit: 2000,
        placeholder:
          'Describe the technical implementation, architecture, dependencies, etc.',
      },
      {
        label: 'Usage Examples (optional)',
        name: 'extra2',
        type: 'textarea',
        limit: 1000,
        placeholder: 'Provide code examples or usage scenarios',
      },
      {
        label: 'Documentation Style',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: technical, beginner-friendly, detailed',
      },
    ],
  },
  {
    title: 'Checklist Generator',
    slug: 'checklist-generator',
    description: 'Generate comprehensive checklists for any task or project',
    icon: CheckCircleIcon,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    shouldUseChatGPTAPI: true,
    isFree: true,
    category: 'Business',
    fields: [
      {
        label: 'Purpose',
        name: 'keywords',
        type: 'text',
        placeholder: 'e.g. Project Launch, Travel Planning, Event Organization',
      },
      {
        label: 'Key Requirements',
        name: 'extra1',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'List the main requirements or goals you want to include in the checklist',
      },
      {
        label: 'Additional Context (optional)',
        name: 'extra2',
        type: 'textarea',
        limit: 1000,
        placeholder: 'Any additional details, constraints, or preferences',
      },
      {
        label: 'Style',
        name: 'tone',
        type: 'text',
        placeholder: 'e.g. Detailed, High-level, Timeline-based',
        limit: 100,
      },
    ],
  },
  {
    title: 'Trivia Question Generator',
    slug: 'trivia-question-generator',
    description:
      'Generate engaging trivia questions for games, quizzes, or events',
    icon: QuestionMarkCircleIcon,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    shouldUseChatGPTAPI: true,
    category: 'Education',
    isFree: true,
    onlyForPaidPlans: true,
    outputs: 5,
    fields: [
      {
        label: 'Topic/Category',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: History, Movies, Science, Sports, etc.',
      },
      {
        label: 'Difficulty Level',
        name: 'extra1',
        type: 'select',
        placeholder: 'Select difficulty level',
        options: ['Easy', 'Medium', 'Hard', 'Mixed'],
      },
      {
        label: 'Number of Questions',
        name: 'extra2',
        type: 'select',
        placeholder: 'Select number of questions',
        options: ['5', '10', '15', '20', '25'],
      },
      {
        label: 'Specific Focus (optional)',
        name: 'extra3',
        type: 'text',
        placeholder: 'example: World War II, Marvel Movies, Solar System',
      },
      // generate answers? (boolean)
      {
        label: 'Generate Answers',
        name: 'extra4',
        type: 'checkbox',
        placeholder: 'Generate answers for the questions',
      },
    ],
  },
  {
    title: 'Reflection Writer',
    slug: 'reflection-writer',
    description: 'AI-powered tool for reflective writing',
    icon: PencilIcon,
    iconForeground: 'text-emerald-700',
    iconBackground: 'bg-emerald-50',
    shouldUseChatGPTAPI: true,
    category: 'Education',
    isFree: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Event/Experience',
        name: 'keywords',
        type: 'textarea',
        limit: 10000,
        placeholder: 'Describe the event or experience you want to reflect on',
      },
      {
        label: 'Initial Reactions',
        name: 'extra1',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'What were your thoughts and feelings during/after this experience?',
      },
      {
        label: 'Lessons Learned',
        name: 'extra2',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'What did you learn from this experience? How has it changed your perspective?',
      },
      {
        label: 'Future Applications',
        name: 'extra3',
        type: 'textarea',
        limit: 1000,
        placeholder: 'How will you apply these insights in the future?',
      },
      {
        label: 'Writing Style',
        name: 'extra4',
        type: 'select',
        options: ['Academic', 'Personal', 'Professional'],
        placeholder: 'Choose your preferred writing style',
      },
    ],
  },
  {
    title: 'Presentation Slides Text Generator',
    slug: 'presentation-slides-text-generator',
    description: 'Create compelling presentation content with AI assistance',
    icon: PresentationChartBarIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    category: ['Business', 'Education'],
    isFree: true,
    fields: [
      {
        label: 'Topic',
        name: 'keywords',
        type: 'text',
        limit: 2000,
        placeholder: 'What is your presentation about?',
      },
      {
        label: 'Target Audience',
        name: 'extra1',
        type: 'text',
        limit: 100,
        placeholder: 'Who is your target audience?',
      },
      {
        label: 'Key Points',
        name: 'extra2',
        type: 'textarea',
        limit: 1000,
        placeholder: 'List the main points you want to cover',
      },
      {
        label: 'Presentation Duration',
        name: 'extra3',
        type: 'select',
        options: ['5-10 minutes', '15-20 minutes', '30+ minutes'],
        placeholder: 'Select presentation duration',
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'text',
        placeholder: 'example: Professional, Educational, Casual, Technical',
      },
    ],
  },
  {
    title: 'Demand Letter Generator',
    slug: 'demand-letter-generator',
    description:
      'Create a professional and effective demand letter to resolve disputes',
    icon: DocumentTextIcon,
    iconForeground: 'text-red-700',
    iconBackground: 'bg-red-50',
    shouldUseChatGPTAPI: true,
    category: ['Legal', 'Business'],
    isFree: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Recipient Name/Company',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: John Smith / ABC Corporation',
      },
      {
        label: 'Nature of Dispute',
        name: 'extra1',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Unpaid invoice for services rendered in March 2023',
      },
      {
        label: 'Amount Demanded (if applicable)',
        name: 'extra2',
        type: 'text',
        placeholder: 'example: $5,000',
      },
      {
        label: 'Resolution Timeline',
        name: 'extra3',
        type: 'text',
        placeholder: 'example: 14 days from receipt of this letter',
      },
      {
        label: 'Supporting Details',
        name: 'extra4',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Dates, contract terms, previous communication attempts',
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: firm, professional, assertive',
      },
    ],
  },
  {
    title: 'Cease and Desist Letter Generator',
    slug: 'cease-and-desist-letter-generator',
    description:
      'Generate a formal cease and desist letter to stop unwanted actions or behaviors',
    icon: DocumentTextIcon,
    iconForeground: 'text-red-700',
    iconBackground: 'bg-red-50',
    shouldUseChatGPTAPI: true,
    category: ['Legal', 'Business'],
    isFree: true,
    fields: [
      {
        label: 'Recipient Name/Company',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: John Smith / XYZ Corporation',
      },
      {
        label: 'Behavior/Action to Stop',
        name: 'extra1',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Unauthorized use of copyrighted material, trademark infringement, harassment',
      },
      {
        label: 'Evidence of Violation',
        name: 'extra2',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Dates, locations, specific instances of the behavior',
      },
      {
        label: 'Deadline to Comply',
        name: 'extra3',
        type: 'text',
        placeholder: 'example: 7 days from receipt of this letter',
      },
      {
        label: 'Consequences of Non-Compliance',
        name: 'extra4',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Legal action, monetary damages, injunctive relief',
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: stern, formal, authoritative',
      },
    ],
  },
  {
    title: 'Idea Generator',
    slug: 'idea-generator',
    description: 'Generate creative ideas for any topic or project',
    icon: LightBulbIcon,
    iconForeground: 'text-yellow-700',
    iconBackground: 'bg-yellow-50',
    shouldUseChatGPTAPI: true,
    category: 'Business',
    isFree: true,
    fields: [
      {
        label: 'Topic or Project',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: new business venture, mobile app, blog post',
      },
      {
        label: 'Target Audience',
        name: 'extra1',
        type: 'text',
        placeholder: 'example: young professionals, small business owners',
      },
      {
        label: 'Goals/Objectives',
        name: 'extra2',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: increase engagement, solve a specific problem, entertain',
      },
      {
        label: 'Constraints/Requirements',
        name: 'extra3',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: budget limitations, technical requirements, time constraints',
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: creative, practical, innovative',
      },
    ],
  },
  {
    title: 'Contract Generator',
    slug: 'contract-generator',
    description: 'Generate professional contracts for various business needs',
    icon: DocumentTextIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    shouldUseGPT4o: true,
    category: ['Legal', 'Business'],
    isFree: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Contract Type',
        name: 'keywords',
        type: 'select',
        options: [
          'Employment Contract',
          'Service Agreement',
          'Non-Disclosure Agreement (NDA)',
          'Consulting Agreement',
          'Sales Contract',
          'Partnership Agreement',
          'Franchise Agreement',
          'Non-Compete Agreement',
          'Non-Solicitation Agreement',
          'Construction Contract',
          'Shareholder Agreement',
          'Real Estate Purchase Agreement',
          'Rental/Lease Agreement',
          'Loan Agreement',
          'Other (please specify)',
        ],
        placeholder: 'Select contract type',
      },
      {
        label: 'Parties Involved',
        name: 'extra1',
        type: 'textarea',
        limit: 1000,
        placeholder: 'example: Company A (employer) and John Doe (employee)',
      },
      {
        label: 'Key Terms and Conditions',
        name: 'extra2',
        type: 'textarea',
        limit: 5000,
        placeholder:
          'example: payment terms, duration, responsibilities, termination conditions',
      },
      {
        label: 'Jurisdiction/Governing Law',
        name: 'extra3',
        type: 'text',
        placeholder: 'example: California, United States',
      },
      {
        label: 'Additional Requirements',
        name: 'extra4',
        type: 'textarea',
        limit: 10000,
        placeholder:
          'example: specific clauses, special considerations, or legal requirements',
      },
    ],
  },
  {
    title: 'Legal Document Generator',
    slug: 'legal-document-generator',
    description: 'Generate customized legal documents for various purposes',
    icon: DocumentTextIcon,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    shouldUseChatGPTAPI: true,
    category: ['Legal', 'Business'],
    isFree: false,
    fields: [
      {
        label: 'Document Type',
        name: 'keywords',
        type: 'select',
        options: [
          'Power of Attorney',
          'Last Will and Testament',
          'Living Will',
          'Cease and Desist Letter',
          'Demand Letter',
          'Promissory Note',
          'Bill of Sale',
          'Quitclaim Deed',
          'Affidavit',
          'Letter of Intent',
          'Termination Letter',
          'Privacy Policy',
          'Terms of Service',
          'DMCA Takedown Notice',
          'Other (please specify)',
        ],
        placeholder: 'Select document type',
      },
      {
        label: 'Parties Involved',
        name: 'extra1',
        type: 'textarea',
        limit: 1000,
        placeholder:
          'example: Names, addresses, and roles of all parties involved',
      },
      {
        label: 'Document Purpose and Key Details',
        name: 'extra2',
        type: 'textarea',
        limit: 5000,
        placeholder:
          'example: Specific purpose, key provisions, important dates, assets involved',
      },
      {
        label: 'Jurisdiction/Governing Law',
        name: 'extra3',
        type: 'text',
        placeholder: 'example: New York, United States',
      },
      {
        label: 'Additional Requirements',
        name: 'extra4',
        type: 'textarea',
        limit: 10000,
        placeholder:
          'example: Special clauses, witness requirements, notarization needs',
      },
    ],
  },
  {
    title: 'Estimate Generator',
    slug: 'estimate-generator',
    description: 'Generate professional estimates for projects or services',
    icon: CalculatorIcon,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    shouldUseChatGPTAPI: true,
    category: 'Business',
    isFree: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Project/Service Name',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: Website Redesign Project',
        limit: 500,
      },
      {
        label: 'Project/Service Description',
        name: 'extra1',
        type: 'textarea',
        placeholder:
          'example: Complete redesign of company website including responsive design, SEO optimization, and content migration',
        limit: 2000,
      },
      {
        label: 'Client Information',
        name: 'extra2',
        type: 'textarea',
        placeholder:
          'example: Company name, contact person, address, email, phone',
        limit: 1000,
      },
      {
        label: 'Scope of Work/Deliverables',
        name: 'extra3',
        type: 'textarea',
        placeholder:
          'example: Design mockups, development, testing, content creation, training',
        limit: 5000,
      },
      {
        label: 'Timeline and Budget Constraints',
        name: 'extra4',
        type: 'textarea',
        placeholder: 'example: 3-month timeline, budget range $5,000-$10,000',
        limit: 1000,
      },
    ],
  },
  {
    title: 'Screenplay Generator',
    slug: 'screenplay-generator',
    description: 'Generate professional screenplay content in proper format',
    icon: FilmIcon,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    shouldUseChatGPTAPI: true,
    category: 'Creative Writing',
    isFree: true,
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Title',
        name: 'keywords',
        type: 'text',
        placeholder: 'example: The Last Journey',
        limit: 500,
      },
      {
        label: 'Plot Summary',
        name: 'extra1',
        type: 'textarea',
        placeholder:
          'example: A retired astronaut is called back for one final mission',
        limit: 2000,
      },
      {
        label: 'Main Characters',
        name: 'extra2',
        type: 'textarea',
        placeholder:
          'example: John (45, veteran astronaut), Sarah (38, mission control)',
        limit: 1000,
      },
      {
        label: 'Scene Setting',
        name: 'extra3',
        type: 'textarea',
        placeholder: 'example: INT. MISSION CONTROL - NIGHT',
        limit: 1000,
      },
      {
        label: 'Genre',
        name: 'extra4',
        type: 'select',
        options: [
          'Drama',
          'Comedy',
          'Action',
          'Sci-Fi',
          'Horror',
          'Romance',
          'Thriller',
        ],
        placeholder: 'Select genre',
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'example: suspenseful, dramatic, emotional',
      },
    ],
  },
  {
    title: 'Artist Statement Generator',
    slug: 'artist-statement-generator',
    description:
      'Create a professional artist statement for exhibitions, portfolios, or grant applications.',
    icon: PencilAltIcon,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    shouldUseChatGPTAPI: true,
    category: ['Creative Writing', 'Career'],
    isFree: true,
    dateAdded: '2024-11-01',
    fields: [
      {
        label: 'Your Name',
        name: 'keywords',
        type: 'text',
        placeholder: 'e.g., Jane Smith',
        limit: 100,
      },
      {
        label: 'Art Medium/Style',
        name: 'extra1',
        type: 'text',
        placeholder:
          'e.g., Oil painting, Digital art, Mixed media, Photography',
        limit: 200,
      },
      {
        label: 'Themes/Concepts',
        name: 'extra2',
        type: 'textarea',
        placeholder:
          'e.g., Urban landscapes, Human emotion, Environmental issues',
        limit: 1000,
      },
      {
        label: 'Artistic Influences',
        name: 'extra3',
        type: 'textarea',
        placeholder:
          'e.g., Artists, movements, or experiences that influence your work',
        limit: 1000,
      },
      {
        label: 'Your Artistic Process',
        name: 'extra4',
        type: 'textarea',
        placeholder:
          'e.g., How you approach creating your art, techniques you use',
        limit: 1000,
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'e.g., Professional, Passionate, Thoughtful, Academic',
      },
      {
        label: 'Output length',
        placeholder: 'e.g., Short, Medium, Long',
        name: 'length',
        type: 'radio',
        initialValue: 'Medium',
        radioOptions: [
          { name: 'Short', checked: false },
          { name: 'Medium', checked: true },
          { name: 'Long', checked: false },
        ],
      },
    ],
  },
  {
    title: 'Feedback Generator',
    slug: 'feedback-generator',
    description: 'Generate constructive feedback for various situations.',
    icon: ChatAlt2Icon,
    iconForeground: 'text-green-700',
    iconBackground: 'bg-green-50',
    shouldUseChatGPTAPI: true,
    category: ['Business', 'HR'],
    isFree: true,
    dateAdded: '2024-09-15',
    onlyForPaidPlans: true,
    fields: [
      {
        label: 'Context',
        name: 'keywords',
        type: 'textarea',
        placeholder:
          'e.g., Employee performance review, Student assignment feedback',
        limit: 5000,
      },
      {
        label: 'Strengths',
        name: 'extra1',
        type: 'textarea',
        placeholder:
          'e.g., Strong communication skills, Meets deadlines consistently',
        limit: 2000,
      },
      {
        label: 'Areas for Improvement',
        name: 'extra2',
        type: 'textarea',
        placeholder: 'e.g., Time management, Attention to detail',
        limit: 2000,
      },
      {
        label: 'Tone',
        name: 'tone',
        type: 'brand_voice',
        placeholder: 'e.g., Constructive, Encouraging, Professional',
      },
      {
        label: 'Output length',
        placeholder: 'e.g., Short, Medium, Long',
        name: 'length',
        type: 'radio',
        initialValue: 'Medium',
        radioOptions: [
          { name: 'Short', checked: false },
          { name: 'Medium', checked: true },
          { name: 'Long', checked: false },
        ],
      },
    ],
  },
  {
    title: 'API',
    slug: 'chat-completions-api',
    icon: CodeIcon,
    iconForeground: 'text-indigo-700',
    iconBackground: 'bg-indigo-50',
    description: 'Get access to the API and use it in your own projects.',
    landingPageSlug: '/settings/api',
    fields: [],
    category: 'Code & Data',
    isAbsoluteURL: true,
    hideLandingPage: true,
  },
];

// Exact category match. A naive `preset.category.includes(cat)` does substring
// matching on single-string categories ('Creative Writing'.includes('Writing')).
export const presetHasCategory = (
  preset: Pick<Preset, 'category'>,
  category: PresetCategory,
) =>
  Array.isArray(preset.category)
    ? preset.category.includes(category)
    : preset.category === category;

// Categories that at least one built-in preset belongs to. Use this for
// filter chips so empty buckets (e.g. the 'Other' fallback) never render.
export const visiblePresetCategories = presetCategories.filter((category) =>
  presets.some((preset) => presetHasCategory(preset, category)),
);

export function extractTitleAndDescription(presets: Preset[]) {
  const result: { [key: string]: string } = {};

  presets.forEach((item) => {
    result[item.title] = item.title;
    result[item.description] = item.description;
  });

  return result;
}
